import React, { memo, useState, useEffect, useRef, useCallback, useReducer, useLayoutEffect } from 'react';
import { useStatusColors } from '../contexts/StatusColorsContext';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useConnection } from '../contexts/ConnectionContext';
import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../services/authService';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';

// Keep libraries array stable across renders to avoid LoadScript reload warnings
const GMAPS_LIBRARIES = ['marker'] as const;
import { socketService } from '@platform/socket';
import { Filter as FilterIcon, Download as DownloadIcon, Trash2 as TrashIcon } from '@ui/icons';
import AutocompleteInput from './AutocompleteInput';
import OrderSplitsEditor from './OrderSplitsEditor';
import { WskaznikWezla, zapiszStanWezlow, type StanIntegracji } from './WskaznikWezla';

const TOP_BAR_HEIGHT = 38;

// CSS styles to hide calendar icon in time inputs
const timeInputStyles = `
  .no-calendar-icon::-webkit-calendar-picker-indicator {
    display: none !important;
  }
  .no-calendar-icon::-webkit-inner-spin-button,
  .no-calendar-icon::-webkit-outer-spin-button {
    display: none !important;
  }
  .no-calendar-icon::-webkit-clear-button {
    display: none !important;
  }
  .no-calendar-icon::-webkit-datetime-edit-fields-wrapper {
    padding: 0 !important;
  }
  .no-calendar-icon::-webkit-datetime-edit {
    padding: 0 !important;
  }
  .no-calendar-icon::-webkit-datetime-edit-hour-field,
  .no-calendar-icon::-webkit-datetime-edit-minute-field {
    padding: 0 !important;
  }

  /* Leader marker (wezel wiodący) - using lila background */
  td.leader-cell {
    background-color: rgba(156, 39, 176, 0.3) !important; /* Lila background for leading wezel - more saturated */
  }

  /* Typography: text vs numeric cells */
  :root {
    --font-text: "Inter", "Roboto", system-ui, -apple-system, "Segoe UI", Arial, sans-serif;
    --font-mono: "Roboto Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  }
  td.text-cell {
    font-family: var(--font-text);
  }
  td.number-cell {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
  td.number-cell input,
  td.number-cell span,
  td.number-cell div {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    text-align: right;
    font-weight: 400;
    font-size: 11px;
  }
  
  /* Minimalny padding dla wszystkich komórek tabeli */
  table th,
  table td {
    padding: 1px !important;
  }

  /* Hide increment/decrement spinners for number inputs (quantities) */
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
  }

  .card-zamowienia-widget-root {
    --sticky-header-top: 0px;
    --sticky-filter-top: 0px;
  }

  .card-zamowienia-widget-root thead tr:nth-child(1) th {
    position: sticky;
    top: var(--sticky-header-top);
    z-index: 20;
    background: var(--card);
  }

  .card-zamowienia-widget-root thead tr:nth-child(2) th {
    position: sticky;
    top: var(--sticky-filter-top);
    z-index: 19;
    background: var(--card);
  }
`;

// Build revision to force rebuild and aid cache-busting during development
const __CARD_ZAMOWIENIA_WIDGET_BUILD_REV__ = 'rev-' + Date.now();
console.log('[CardZamowieniaWidget] build:', __CARD_ZAMOWIENIA_WIDGET_BUILD_REV__);

interface Zamowienie {
  id: number;
  zamowienie_gotowka: boolean;
  zamowienie_uwagi1: string;
  zamowienie_numer: number;
  zamowienie_godzinaod: string;
  zamowienie_godzinado: string;
  zamowienie_dzien?: string;
  zamowienie_tydzien?: number;
  klient_nazwa: string;
  klient_nip: string;
  klient_budowa: string;
  klient_telefon: string;
  zamowienie_okolo: string;
  zamowienie_ilosc: number;
  zamowienie_zrealizowano: number;
  beton_klasa: string;
  beton_konsystencja: string;
  pompa_nazwa: string;
  pompa_numer: string;
  pompa_kierowca: string;
  zamowienie_transport: string;
  zamowienie_uwagi2: string;
  status?: string;
  zamowienie_potwierdzone?: boolean;
  zamowienie_realizacja?: boolean;
  zamowienie_zakonczone?: boolean;
  zamowienie_anulowane?: boolean;
  zamowienie_wezel_wiodacy?: number;
  wezel_nazwa?: string;
  region_id?: number;
  budowa_lat?: number | null;
  budowa_lon?: number | null;
  klient_niezafakturowane?: number;
  klient_zgoda?: boolean;
  beton_klasa_is_confirmed?: boolean | null;
}

interface Wezel {
  id: number;
  nazwa: string;
  region_id: number;
}

interface Tab {
  id: string;
  name: string;
  wezelId?: number;
  isRegion: boolean;
}

interface CardZamowieniaWidgetProps {
  selectedWezel: number | null;
  selectedRegion: number | null;
  selectedDate: string;
  highlightZamowienieNumer?: number | null;
  onHighlightClear?: () => void;
}

const CardZamowieniaWidget: React.FC<CardZamowieniaWidgetProps> = ({ selectedWezel, selectedRegion, selectedDate, highlightZamowienieNumer, onHighlightClear }) => {
  const { hasRole } = useAuth();
  const statusColors = useStatusColors();
  const statusColorsRef = useRef<typeof statusColors | null>(statusColors);
  useEffect(() => { statusColorsRef.current = statusColors; }, [statusColors]);
  const [zamowienia, setZamowienia] = useState<Zamowienie[]>([]);
  const [wezels, setWezels] = useState<Wezel[]>([]);
  // Stan integracji produkcji per węzeł — zasila wskaźnik przy nazwie zakładki. Trzymany
  // OSOBNO od listy węzłów, bo odświeża się cyklicznie, a lista praktycznie nigdy.
  // Stan wskaźników NIE jest stanem tego komponentu — leży we wspólnym magazynie
  // w WskaznikWezla. Trzymanie go tutaj oznaczałoby, że odświeżenie co 15 s przerysowuje
  // cały widget zamówień razem z tabelą, żeby zaktualizować kropkę o średnicy 22 px.
  // Splits: zamowienie_id -> { wezel_id -> zamowione }
  const [splitsByOrder, setSplitsByOrder] = useState<Record<number, Record<number, number>>>({});
  const [splitsDirtyByOrder, setSplitsDirtyByOrder] = useState<Record<number, boolean>>({});
  const [realizacjaByOrder, setRealizacjaByOrder] = useState<Record<number, number>>({});
  const [realizedByOrderWezel, setRealizedByOrderWezel] = useState<Record<number, Record<number, number>>>({});
  // Helper: temp rows use Date.now() as id (very large). Treat those as not yet persisted
  const isTempId = useCallback((id: number) => Number(id) > 1e12, []);
  // States needed before helpers/effects below
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<Zamowienie>>({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('region');
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [highlighted, setHighlighted] = useState<Map<number, number>>(new Map());
  const [blinkMap, setBlinkMap] = useState<Record<number, Set<string>>>({});
  const selectedDateRef = useRef<string>(selectedDate);
  const selectedRegionRef = useRef<number | null>(selectedRegion);
  const activeTabRef = useRef<string>('region');
  
  // Refs for sticky positioning (only tabs for now)
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabsSentinelRef = useRef<HTMLDivElement>(null);
  const [tabsSticky, setTabsSticky] = useState(false);
  const [tabsHeight, setTabsHeight] = useState(0);
  const headerRowRef = useRef<HTMLTableRowElement | null>(null);
  const [headerRowHeight, setHeaderRowHeight] = useState(0);
  const stickyHeaderTop = TOP_BAR_HEIGHT + tabsHeight;
  const stickyFilterTop = stickyHeaderTop + headerRowHeight;
  const widgetRootStyle = React.useMemo<React.CSSProperties & { [key: string]: string | number }>(() => ({
    width: '100%',
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: '12px',
    '--sticky-header-top': `${stickyHeaderTop}px`,
    '--sticky-filter-top': `${stickyFilterTop}px`
  }), [stickyHeaderTop, stickyFilterTop]);
  const tabsRef = useRef<any[]>([]);
  const [regionAgg, setRegionAgg] = useState<Record<number, { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }>>({});
  const [wezelAgg, setWezelAgg] = useState<Record<number, { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }>>({});
  const [exportOpen, setExportOpen] = useState(false);
  // Column visibility (context menu on header)
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('ui.zamowienia.tableColumns.visible');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr);
      }
    } catch {}
    // default: show all we know (empty set => treat as "all visible")
    return new Set<string>();
  });
  const [headerMenu, setHeaderMenu] = useState<{ open: boolean; x: number; y: number }>({ open: false, x: 0, y: 0 });
  const [columnMenuSearch, setColumnMenuSearch] = useState<string>('');
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  // Client-side column filters (second header row)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});
  const filterDebounceRef = useRef<any>(null);
  const [regionName, setRegionName] = useState<string>('');
  const exportRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [wzByOrder, setWzByOrder] = useState<Record<number, any[]>>({});
  const [wzTransferModal, setWzTransferModal] = useState<{ visible: boolean; wz: any | null; zamowienieId: number | null }>({ visible: false, wz: null, zamowienieId: null });
  const [wzTransferData, setWzTransferData] = useState<{ numer: string; tydzien: string }>({ numer: '', tydzien: '' });
  const onDbChangeRef = useRef<((evt: any) => void) | null>(null);
  const lastAppliedSignatureRef = useRef<Record<number, string>>({});
  const [tooltip, setTooltip] = useState<{ visible: boolean; text: string; content?: React.ReactNode; x: number; y: number }>({ visible: false, text: '', x: 0, y: 0 });
  // Store is_confirmed status for beton_klasa (true = confirmed, false = unconfirmed, undefined = not set)
  const [betonKlasaConfirmed, setBetonKlasaConfirmed] = useState<Map<number, boolean>>(new Map());

  // Mandatory and known column map (label -> key)
  const MANDATORY_COLUMN_KEYS = useRef<Set<string>>(new Set([
    'zamowienie_numer','klient_nazwa','klient_budowa','zamowienie_ilosc','zamowienie_godzinaod','zamowienie_godzinado'
  ]));
  const LABEL_TO_KEY: Record<string, string> = {
    'uwagi 1': 'zamowienie_uwagi1',
    'num.': 'zamowienie_numer',
    'godz od': 'zamowienie_godzinaod',
    'do': 'zamowienie_godzinado',
    'klient': 'klient_nazwa',
    'nip': 'klient_nip',
    'budowa': 'klient_budowa',
    'telefon': 'klient_telefon',
    'ok': 'zamowienie_okolo',
    'ilość': 'zamowienie_ilosc',
    'wysłano': 'zamowienie_zrealizowano',
    'recepta': 'beton_klasa',
    'kons.': 'beton_konsystencja',
    'pompa': 'pompa_nazwa',
    'nr p.': 'pompa_numer',
    'kierowca': 'pompa_kierowca',
    'transport': 'zamowienie_transport',
    'uwagi 2': 'zamowienie_uwagi2'
  };

  // Column categories for better organization
  const COLUMN_CATEGORIES: Record<string, { label: string; keys: string[] }> = {
    basic: { label: 'Podstawowe', keys: ['zamowienie_numer', 'zamowienie_godzinaod', 'zamowienie_godzinado', 'zamowienie_ilosc', 'zamowienie_okolo', 'zamowienie_zrealizowano'] },
    client: { label: 'Klient', keys: ['klient_nazwa', 'klient_nip', 'klient_budowa', 'klient_telefon'] },
    beton: { label: 'Beton', keys: ['beton_klasa', 'beton_konsystencja'] },
    pompa: { label: 'Pompa', keys: ['pompa_nazwa', 'pompa_numer', 'pompa_kierowca'] },
    uwagi: { label: 'Uwagi', keys: ['zamowienie_uwagi1', 'zamowienie_uwagi2'] }
  };

  // View presets
  const VIEW_PRESETS: Record<string, { label: string; keys: string[] }> = {
    minimal: { 
      label: 'Minimalny', 
      keys: ['zamowienie_numer', 'klient_nazwa', 'klient_budowa', 'zamowienie_ilosc', 'zamowienie_godzinaod', 'zamowienie_godzinado'] 
    },
    standard: { 
      label: 'Standardowy', 
      keys: ['zamowienie_numer', 'zamowienie_godzinaod', 'zamowienie_godzinado', 'klient_nazwa', 'klient_nip', 'klient_budowa', 'zamowienie_ilosc', 'zamowienie_zrealizowano', 'beton_klasa'] 
    },
    full: { 
      label: 'Pełny', 
      keys: Object.values(LABEL_TO_KEY) 
    },
    noPompa: { 
      label: 'Bez pompy', 
      keys: Object.values(LABEL_TO_KEY).filter(k => !k.startsWith('pompa_')) 
    }
  };

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(Object.keys(COLUMN_CATEGORIES)));

  const isVisible = (key: string): boolean => {
    if (!key) return true;
    if (MANDATORY_COLUMN_KEYS.current.has(key)) return true;
    // empty set => all visible by default (initial state)
    if (!visibleColumns || visibleColumns.size === 0) return true;
    // If set has items, only those in the set are visible
    return visibleColumns.has(key);
  };

  // Get all available column keys (non-mandatory)
  const getAllColumnKeys = (): string[] => {
    return Object.values(LABEL_TO_KEY).filter(key => !MANDATORY_COLUMN_KEYS.current.has(key));
  };

  const applyColumnVisibility = useCallback(() => {
    try {
      const table = tableRef.current as HTMLTableElement | null;
      if (!table) return;
      const thead = table.tHead;
      if (!thead || !thead.rows || thead.rows.length === 0) return;
      const headerRow = thead.rows[0];
      const hideIdx = new Set<number>();
      
      // Build map of column index to field key for more reliable mapping
      const indexToKey: Record<number, string> = {};
      for (let i = 0; i < headerRow.cells.length; i++) {
        const th = headerRow.cells[i] as HTMLTableCellElement;
        const label = (th.textContent || '').trim().toLowerCase();
        const key = LABEL_TO_KEY[label];
        if (key) {
          indexToKey[i] = key;
          if (!isVisible(key)) hideIdx.add(i);
        }
      }
      
      // Also check cells by data-field attribute (more reliable for inputs)
      const allCells = table.querySelectorAll('td[data-field], th[data-field]');
      allCells.forEach(cell => {
        const fieldKey = cell.getAttribute('data-field');
        if (fieldKey && !isVisible(fieldKey)) {
          (cell as HTMLElement).style.display = 'none';
        } else if (fieldKey && isVisible(fieldKey)) {
          (cell as HTMLElement).style.display = '';
        }
      });
      
      // iterate all rows using index-based approach as fallback
      const rows = table.querySelectorAll('tr');
      rows.forEach(tr => {
        const cells = Array.from(tr.children) as HTMLElement[];
        cells.forEach((cell, idx) => {
          // Check if this cell has data-field attribute (preferred method)
          const fieldKey = cell.getAttribute('data-field');
          if (fieldKey) {
            // Use data-field attribute for reliable mapping
            if (!isVisible(fieldKey)) {
              cell.style.display = 'none';
            } else {
              cell.style.display = '';
            }
          } else if (hideIdx.has(idx)) {
            // Fallback to index-based hiding for cells without data-field
            cell.style.display = 'none';
          } else {
            cell.style.display = '';
          }
        });
      });
    } catch {}
  }, [visibleColumns, isVisible]);

  useEffect(() => { applyColumnVisibility(); }, [applyColumnVisibility, tabs, selectedRegion]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setHeaderMenu({ open: false, x: 0, y: 0 }); };
    const onClick = (e: MouseEvent) => {
      const menu = document.getElementById('header-columns-menu');
      if (menu && !menu.contains(e.target as Node)) setHeaderMenu({ open: false, x: 0, y: 0 });
    };
    document.addEventListener('keydown', onEsc);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onEsc); document.removeEventListener('mousedown', onClick); };
  }, []);

  // JavaScript-based sticky positioning for Tabs, Control Panel, and Table Header
  // Sticky tabs with IntersectionObserver (detect when sticky for shadow effect)
  useEffect(() => {
    const sentinel = tabsSentinelRef.current;
    if (!sentinel) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel goes out of view at the top, tabs are sticky
        setTabsSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: [0], rootMargin: '-38px 0px 0px 0px' } // Match TopBar height
    );
    
    observer.observe(sentinel);
    
    return () => observer.disconnect();
  }, []);

useLayoutEffect(() => {
  if (loading) {
    setTabsHeight(0);
    return;
  }

  const node = tabsContainerRef.current;
  if (!node) {
    setTabsHeight(0);
    return;
  }

  const updateHeight = () => {
    try {
      const next = node.getBoundingClientRect().height || node.offsetHeight || 0;
      setTabsHeight(Math.round(next));
    } catch {
      setTabsHeight(0);
    }
  };

  updateHeight();

  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }

  window.addEventListener('resize', updateHeight);
  return () => window.removeEventListener('resize', updateHeight);
}, [loading, tabs.length]);

useLayoutEffect(() => {
  if (loading) {
    setHeaderRowHeight(0);
    return;
  }

  const node = headerRowRef.current;
  if (!node) {
    setHeaderRowHeight(0);
    return;
  }

  const measureHeight = () => {
    try {
      const next = node.getBoundingClientRect().height || node.offsetHeight || 0;
      setHeaderRowHeight(Math.round(next));
    } catch {
      setHeaderRowHeight(0);
    }
  };

  measureHeight();

  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => measureHeight());
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }

  window.addEventListener('resize', measureHeight);
  return () => window.removeEventListener('resize', measureHeight);
}, [loading, tabs.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const report = () => {
      const tabsEl = tabsContainerRef.current;
      const headerEl = headerRowRef.current;
      const sampleHeaderCell = headerEl?.querySelector('th') as HTMLElement | null;
      const rows = tableRef.current?.querySelectorAll('thead tr') || null;
      const filterRow = rows && rows.length > 1 ? rows[1] : null;
      const sampleFilterCell = filterRow?.querySelector('th, td') as HTMLElement | null;

      const snapshot = {
        tabsHeight,
        headerRowHeight,
        stickyHeaderTop,
        stickyFilterTop,
        tabRect: tabsEl ? tabsEl.getBoundingClientRect() : null,
        headerRect: headerEl ? headerEl.getBoundingClientRect() : null,
        sampleHeaderTop: sampleHeaderCell ? window.getComputedStyle(sampleHeaderCell).top : null,
        sampleFilterTop: sampleFilterCell ? window.getComputedStyle(sampleFilterCell).top : null,
        scrollAncestor: (() => {
          let el: HTMLElement | null = tabsEl?.parentElement || null;
          while (el) {
            const style = window.getComputedStyle(el);
            if (/(auto|scroll)/.test(style.overflowY || '')) {
              return {
                node: el,
                className: el.className,
                top: el.getBoundingClientRect().top,
                overflowY: style.overflowY
              };
            }
            el = el.parentElement;
          }
          return null;
        })()
      };

      console.groupCollapsed(
        '[CardZamowieniaWidget][StickyDebug]',
        `tabs:${tabsHeight}px header:${headerRowHeight}px top:${stickyHeaderTop}px filterTop:${stickyFilterTop}px`
      );
      console.table(snapshot);
      console.groupEnd();

      return snapshot;
    };

    (window as any).__cardStickyDebug = report;
    console.debug('[CardZamowieniaWidget] sticky debug ready – run window.__cardStickyDebug() in console');
    report();

    return () => {
      if ((window as any).__cardStickyDebug === report) {
        delete (window as any).__cardStickyDebug;
      }
    };
  }, [tabsHeight, headerRowHeight, stickyHeaderTop, stickyFilterTop]);

  const toggleColumn = (key: string) => {
    if (MANDATORY_COLUMN_KEYS.current.has(key)) return;
    
    setVisibleColumns(prev => {
      // If set is empty, all columns are visible by default
      // We need to initialize it with all columns first, then remove the one we're hiding
      const allKeys = getAllColumnKeys();
      const currentVisible = prev.size === 0 ? new Set(allKeys) : new Set(prev);
      
      const next = new Set(currentVisible);
      if (currentVisible.has(key)) {
        // Column is currently visible - hide it by removing from set
        next.delete(key);
      } else {
        // Column is currently hidden - show it by adding to set
        next.add(key);
      }
      
      // If all columns are visible again, use empty set (default state)
      // This allows us to distinguish between "all visible" and "explicitly set"
      if (next.size === allKeys.length) {
        try { localStorage.removeItem('ui.zamowienia.tableColumns.visible'); } catch {}
        return new Set<string>();
      }
      
      try { localStorage.setItem('ui.zamowienia.tableColumns.visible', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  const applyPreset = (presetKey: string) => {
    const preset = VIEW_PRESETS[presetKey];
    if (!preset) return;
    const presetSet = new Set(preset.keys);
    setVisibleColumns(presetSet);
    try { 
      localStorage.setItem('ui.zamowienia.tableColumns.visible', JSON.stringify(Array.from(presetSet))); 
    } catch {}
    setHeaderMenu({ open: false, x: 0, y: 0 });
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) next.delete(categoryKey); else next.add(categoryKey);
      return next;
    });
  };

  // Load and cache current region name for tabs
  useAuthenticatedEffect(() => {
    const loadRegionName = async () => {
      if (!selectedRegion) { setRegionName(''); return; }
      try {
        const resp = await authenticatedFetch('/api/auth/user-regions', { credentials: 'include' });
        if (!resp.ok) { setRegionName(''); return; }
        const data = await resp.json();
        const list = Array.isArray(data?.regions) ? data.regions : [];
        const found = list.find((r: any) => Number(r.id) === Number(selectedRegion));
        setRegionName(found?.nazwa || '');
      } catch {
        setRegionName('');
      }
    };
    loadRegionName();
  }, [selectedRegion]);
  useEffect(() => { try { activeTabRef.current = activeTab; } catch {} }, [activeTab]);
  useEffect(() => { try { tabsRef.current = tabs; } catch {} }, [tabs]);

  // Exit edit mode when switching tabs (simulate ESC)
  useEffect(() => {
    try {
      if (editingId !== null) {
        // Cancel current edit to prevent disabled buttons and hidden row state
        setEditingId(null);
        setEditData({});
        setIsAddingNew(false);
        resetQtyUiState();
      }
    } catch {}
  }, [activeTab]);

  // Keep refs in sync to avoid stale closures in async handlers
  useEffect(() => {
    console.log('[CardZamowieniaWidget] useEffect[selectedDate] START', { selectedDate });
    const oldDate = selectedDateRef.current;
    selectedDateRef.current = selectedDate;
    if (oldDate !== selectedDate) {
      console.log('[CardZamowieniaWidget] Date changed', { oldDate, newDate: selectedDate });
    }
    console.log('[CardZamowieniaWidget] useEffect[selectedDate] END');
  }, [selectedDate]);
  useEffect(() => {
    const oldRegion = selectedRegionRef.current;
    selectedRegionRef.current = selectedRegion;
    if (oldRegion !== selectedRegion) {
      console.log('[useEffect][selectedRegionRef] updated', { oldRegion, newRegion: selectedRegion });
    }
  }, [selectedRegion]);

  // Handle notification navigation - activate tab and select row
  useEffect(() => {
    if (!highlightZamowienieNumer || zamowienia.length === 0) return;
    
    // Find zamowienie by number
    const targetZamowienie = zamowienia.find(z => z.zamowienie_numer === highlightZamowienieNumer);
    if (!targetZamowienie) {
      console.log('[CardZamowieniaWidget] Zamowienie not found for numer:', highlightZamowienieNumer);
      onHighlightClear?.();
      return;
    }
    
    console.log('[CardZamowieniaWidget] Found zamowienie from notification:', { 
      id: targetZamowienie.id, 
      numer: targetZamowienie.zamowienie_numer,
      wezel: targetZamowienie.zamowienie_wezel_wiodacy 
    });
    
    // Activate the correct wezel tab
    const wezelId = targetZamowienie.zamowienie_wezel_wiodacy;
    if (wezelId) {
      setActiveTab(`wezel-${wezelId}`);
    }
    
    // Select the row
    setSelectedRowId(targetZamowienie.id);
    
    // Scroll to row after a short delay (to allow tab switch and render)
    setTimeout(() => {
      const rowElement = document.querySelector(`tr[data-zamowienie-id="${targetZamowienie.id}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
    
    // Clear highlight after handling
    onHighlightClear?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightZamowienieNumer, zamowienia]);

  // ---- Quantities state management (useReducer) ----
  const QTY_EPS = 0.01;
  const DATA_FONT = '11px';
  const DATA_INPUT_HEIGHT = '18px';
  const DATA_ROW_HEIGHT = '19px';
  const round2 = (n: number): number => {
    const v = Number(n);
    if (!Number.isFinite(v)) return 0;
    return Math.round(v * 100) / 100;
  };
  const formatDot = (n: number, maxFractionDigits: number = 2): string => {
    if (!Number.isFinite(n)) return '0,00';
    const r = round2(n);
    // Always show 2 decimal places, use comma as decimal separator
    const s = r.toFixed(maxFractionDigits);
    // Replace dot with comma for Polish locale
    return s.replace('.', ',');
  };
  type QtyState = {
    currentRowId: number | null;
    globalQty: number;
    leadingId: number;
    nodes: Record<number, number>;
    error: string | null;
  };
  const initialQtyState: QtyState = {
    currentRowId: null,
    globalQty: 0,
    leadingId: 0,
    nodes: {},
    error: null,
  };
  type QtyAction =
    | { type: 'INIT'; payload: { rowId: number; globalQty: number; leadingId: number; nodes: Record<number, number> } }
    | { type: 'SET_GLOBAL'; value: number }
    | { type: 'SET_NODE'; id: number; value: number }
    | { type: 'SET_LEADING'; id: number }
    | { type: 'RESET' };

  const clamp = (n: number): number => {
    const v = Number(n);
    if (!Number.isFinite(v) || v < 0) return 0;
    return round2(v);
  };

  const sumExcept = (nodes: Record<number, number>, exceptId: number): number => {
    let s = 0;
    for (const [k, v] of Object.entries(nodes)) {
      const id = Number(k);
      if (id === Number(exceptId)) continue;
      s += Number(v) || 0;
    }
    return s;
  };

  const nearlyEqual = (a: number, b: number, eps: number = QTY_EPS): boolean => Math.abs(Number(a) - Number(b)) <= eps;
  const shallowEqualNodes = (a: Record<number, number> | undefined, b: Record<number, number> | undefined): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (!(k in b)) return false;
      if (!nearlyEqual(Number(a[Number(k)] || 0), Number(b[Number(k)] || 0))) return false;
    }
    return true;
  };

  const computeError = (globalQty: number, nodes: Record<number, number>, leadingId: number): string | null => {
    // Leading node must exist
    if (typeof nodes[Number(leadingId)] === 'undefined') {
      return 'Brak węzła wiodącego w podziale ilości.';
    }
    // Negative quantities not allowed
    for (const v of Object.values(nodes)) {
      if (Number(v) < -QTY_EPS) return 'Ilość w węzłach nie może być ujemna.';
    }
    // Sum nodes vs global check (with tolerance)
    const sumAll = Object.values(nodes).reduce((s, v) => s + Number(v || 0), 0);
    if (sumAll > Number(globalQty) + QTY_EPS) return 'Suma węzłów przekracza ilość globalną.';
    // Others vs global guard remains for clarity
    const others = Object.entries(nodes).reduce((sum, [id, val]) => (Number(id) === Number(leadingId) ? sum : sum + Number(val || 0)), 0);
    if (others > Number(globalQty) + QTY_EPS) return 'Suma pozostałych węzłów przekracza ilość globalną.';
    return null;
  };

  const qtyReducer = (state: QtyState, action: QtyAction): QtyState => {
    switch (action.type) {
      case 'RESET': {
        return initialQtyState;
      }
      case 'INIT': {
        // Idempotent init for the same row: do not override existing reducer-managed state
        if (state.currentRowId !== null && state.currentRowId === Number(action.payload.rowId)) {
          return state;
        }
        // Reset for a different row
        const nextNodes = { ...action.payload.nodes };
        const others = sumExcept(nextNodes, action.payload.leadingId);
        const leadingQty = clamp(action.payload.globalQty - others);
        nextNodes[action.payload.leadingId] = leadingQty;
        const nextGlobal = clamp(action.payload.globalQty);
        const nextLeading = Number(action.payload.leadingId);
        const error = computeError(nextGlobal, nextNodes, nextLeading);
        return {
          currentRowId: Number(action.payload.rowId),
          globalQty: nextGlobal,
          leadingId: nextLeading,
          nodes: nextNodes,
          error
        };
      }
      case 'SET_GLOBAL': {
        const newGlobal = clamp(action.value);
        const others = sumExcept(state.nodes, state.leadingId);
        const newLeading = clamp(newGlobal - others);
        const nodes = { ...state.nodes, [state.leadingId]: newLeading };
        const error = computeError(newGlobal, nodes, state.leadingId);
        return {
          ...state,
          globalQty: newGlobal,
          nodes,
          error
        };
      }
      case 'SET_NODE': {
        const id = Number(action.id);
        const val = clamp(action.value);
        if (id === state.leadingId) {
          const others = sumExcept(state.nodes, id);
          const newGlobal = clamp(val + others);
          const nodes = { ...state.nodes, [id]: val };
          const error = computeError(newGlobal, nodes, state.leadingId);
          return {
            ...state,
            globalQty: newGlobal,
            nodes,
            error
          };
        }
        // non-leading update
        const updated = { ...state.nodes, [id]: val };
        const othersNew = sumExcept(updated, state.leadingId);
        const newLeading = clamp(state.globalQty - othersNew);
        const nodes = { ...updated, [state.leadingId]: newLeading };
        const error = computeError(state.globalQty, nodes, state.leadingId);
        return {
          ...state,
          nodes,
          error
        };
      }
      case 'SET_LEADING': {
        const newLeadingId = Number(action.id);
        const others = sumExcept(state.nodes, newLeadingId);
        const newLeading = clamp(state.globalQty - others);
        const nodes = { ...state.nodes, [newLeadingId]: newLeading };
        const error = computeError(state.globalQty, nodes, newLeadingId);
        return {
          ...state,
          leadingId: newLeadingId,
          nodes,
          error
        };
      }
      default:
        return state;
    }
  };

  const [qtyState, qtyDispatch] = useReducer(qtyReducer, {
    currentRowId: null,
    globalQty: 0,
    leadingId: 0,
    nodes: {},
    error: null
  });
  const [qtyRowId, setQtyRowId] = useState<number | null>(null);
  const [globalQtyInput, setGlobalQtyInput] = useState<string>('');
  const [nodeQtyInputs, setNodeQtyInputs] = useState<Record<number, string>>({});
  // Draft inputs for live typing while reducer remains single source of truth
  const [nodeDraft, setNodeDraft] = useState<Record<number, string>>({});
  const [globalDraft, setGlobalDraft] = useState<string | null>(null);
  const lastValidationFocusRef = useRef<{ field?: string; rowId?: number } | null>(null);
  const lastIntentFocusRef = useRef<{ field?: string; rowId?: number } | null>(null);
  const editBaselineRef = useRef<{ rowId: number | null; fields: Partial<Zamowienie>; splits: Record<number, number> } | null>(null);
  const nodeDisplayValue = (id: number) => nodeDraft[id] ?? formatDot(Number(qtyState.nodes[id] ?? 0));
  const globalDisplayValue = () => globalDraft ?? formatDot(Number(qtyState.globalQty));

  const resetQtyUiState = () => {
    try {
      qtyDispatch({ type: 'RESET' });
      setQtyRowId(null);
      setNodeDraft({});
      setGlobalDraft(null);
      setNodeQtyInputs({});
      setGlobalQtyInput('');
      setValidationError(null);
    } catch {}
  };

  const isUsingQtyReducerForRow = (rowId: number) => qtyRowId != null && Number(qtyRowId) === Number(rowId);

  const initQtyFromRow = useCallback((row: Zamowienie) => {
    try {
      const rowId = Number(row.id);
      const leading = Number((editData.zamowienie_wezel_wiodacy ?? row.zamowienie_wezel_wiodacy) || 0);
      const global = clamp(Number(editData.zamowienie_ilosc ?? row.zamowienie_ilosc ?? 0));
      const nodes: Record<number, number> = {};
      for (const w of wezels) nodes[Number(w.id)] = 0;
      const existing = splitsByOrder[rowId] || {};
      for (const [wid, qty] of Object.entries(existing)) nodes[Number(wid)] = Number(qty) || 0;
      // Ensure leading computed from global and others
      const others = sumExcept(nodes, leading);
      nodes[leading] = clamp(global - others);
      qtyDispatch({ type: 'INIT', payload: { rowId, globalQty: global, leadingId: leading, nodes } });
      setQtyRowId(rowId);
      setGlobalQtyInput(String(global));
      const buf: Record<number, string> = {};
      for (const [wid, qty] of Object.entries(nodes)) buf[Number(wid)] = String(qty as number);
      setNodeQtyInputs(buf);
      setSplitsByOrder(prev => ({ ...prev, [rowId]: nodes }));
    } catch {}
  }, [editData.zamowienie_ilosc, editData.zamowienie_wezel_wiodacy, wezels, splitsByOrder]);

  const applyQtyAction = useCallback((action: QtyAction) => {
    qtyDispatch(action);
  }, []);

  // Reactive synchronization of splits and editData when qtyState changes for the managed row
  useEffect(() => {
    if (qtyRowId == null) return;
    // Sync splits map for current row (shallow compare to avoid unnecessary renders)
    setSplitsByOrder(prev => {
      const prevRow = prev[qtyRowId];
      if (shallowEqualNodes(prevRow, qtyState.nodes)) return prev;
      return { ...prev, [qtyRowId]: { ...qtyState.nodes } };
    });
    // Sync editData quantity to globalQty only when changed
    setEditData(prev => {
      const curr = Number(prev.zamowienie_ilosc || 0);
      if (nearlyEqual(curr, qtyState.globalQty)) return prev;
      return { ...prev, zamowienie_ilosc: qtyState.globalQty } as any;
    });
    // Do not drive inputs from this effect anymore; drafts + qtyState drive UI
    // Mark splits as dirty for this row (only if not already true)
    setSplitsDirtyByOrder(prevDirty => {
      if (prevDirty[qtyRowId]) return prevDirty;
      return { ...prevDirty, [qtyRowId]: true };
    });
    // Clear old validation error if any exists
    if (validationError) setValidationError(null);
  }, [qtyState.nodes, qtyState.globalQty, qtyRowId]);
  
  // Context menu states
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    zamowienieId: number | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    zamowienieId: null
  });
  
  // Clipboard state for copy/paste functionality
  const [clipboardData, setClipboardData] = useState<Partial<Zamowienie> | null>(null);
  
  // Paste confirmation modal state
  const [pasteModal, setPasteModal] = useState<{
    visible: boolean;
    data: Partial<Zamowienie> | null;
  }>({
    visible: false,
    data: null
  });
  const refreshVisibleSplits = useCallback(async () => {
    try {
      const ids = zamowienia.map(z => z.id);
      const realIds = ids.filter(id => !isTempId(id));
      if (realIds.length === 0) {
        setSplitsByOrder({});
        setRealizedByOrderWezel({});
        return;
      }
      const next: Record<number, Record<number, number>> = {};
      const realizedNext: Record<number, Record<number, number>> = {};
      for (const id of realIds) {
        try {
          const res = await authenticatedFetch(`/api/zamowienia/${id}/splits`, { credentials: 'include' });
          if (!res.ok) continue;
          const j = await res.json();
          const map: Record<number, number> = {};
          const realizedMap: Record<number, number> = {};
          (j.splits || []).forEach((s: any) => {
            const wid = Number(s.wezel_id);
            map[wid] = Number(s.zamowione || 0);
            realizedMap[wid] = Number(s.zrealizowane || 0);
          });
          next[id] = map;
          realizedNext[id] = realizedMap;
        } catch {}
      }
      setSplitsByOrder(next);
      setRealizedByOrderWezel(realizedNext);
    } catch {}
  }, [zamowienia, isTempId]);

  const refreshOrderSplits = useCallback(async (orderId: number) => {
    try {
      if (isTempId(orderId)) return;
      const res = await authenticatedFetch(`/api/zamowienia/${orderId}/splits`, { credentials: 'include' });
      if (!res.ok) return;
      const j = await res.json();
      const map: Record<number, number> = {};
      const realizedMap: Record<number, number> = {};
      (j.splits || []).forEach((s: any) => {
        const wid = Number(s.wezel_id);
        map[wid] = Number(s.zamowione || 0);
        realizedMap[wid] = Number(s.zrealizowane || 0);
      });
      setSplitsByOrder(prev => ({ ...prev, [orderId]: map }));
      setRealizedByOrderWezel(prev => ({ ...prev, [orderId]: realizedMap }));
      // If we are editing this order, reflect server values in buffer for a consistent next edit
      if (editingId === orderId) {
        const buf: Record<number, string> = {};
        for (const [wid, qty] of Object.entries(map)) buf[Number(wid)] = String(qty as number);
        setNodeQtyInputs(prev => ({ ...prev, ...buf }));
      }
    } catch {}
  }, [isTempId, editingId]);

  const getRowTotalQty = (row: Zamowienie): number => {
    if (editingId === row.id) {
      return Number(editData.zamowienie_ilosc || 0);
    }
    return Number(row.zamowienie_ilosc || 0);
  };

  const safeNumber = (value: any): number => {
    const num = Number(value);
    return (Number.isFinite(num) && !Number.isNaN(num)) ? num : 0;
  };

  const getClientFinancialColor = (row: Zamowienie): string => {
    try {
      const hasNip = String(row.klient_nip || '').trim() !== '';
      if (!hasNip) return 'var(--text)';
      const r: any = row as any;
      const zad = safeNumber(r.klient_zadluzenie);
      const lim = safeNumber(r.klient_limit_kredytowy);
      const prz = safeNumber(r.klient_przeterminowane);
      const blok = Boolean(r.klient_blokada);
      const zg = Boolean(r.klient_zgoda);
      const over = (zad > lim) || (prz > 0) || blok;
      if (over) return zg ? 'var(--ok)' : 'var(--error)';
      return 'var(--text)';
    } catch {
      return 'var(--text)';
    }
  };

  const hasClientFinancialProblem = (row: Zamowienie): boolean => {
    try {
      const hasNip = String(row.klient_nip || '').trim() !== '';
      if (!hasNip) return false;
      
      // Check if we're editing this row - use editData for financial info
      const isEditingThisRow = editingId === row.id;
      const dataSource: any = isEditingThisRow ? editData : row;
      
      const zad = safeNumber(dataSource.klient_zadluzenie);
      const lim = safeNumber(dataSource.klient_limit_kredytowy);
      const prz = safeNumber(dataSource.klient_przeterminowane);
      const blok = Boolean(dataSource.klient_blokada);
      const zg = Boolean(dataSource.klient_zgoda);
      const over = (zad > lim) || (prz > 0) || blok;
      return over && !zg;
    } catch {
      return false;
    }
  };

  const getClientFinancialTooltipContent = (row: Zamowienie): React.ReactNode => {
    try {
      const hasNip = String(row.klient_nip || '').trim() !== '';
      if (!hasNip) return null;
      const r: any = row as any;
      const zad = safeNumber(r.klient_zadluzenie);
      const niezafakturowane = safeNumber(r.klient_niezafakturowane);
      const razem = zad + niezafakturowane;
      const prz = safeNumber(r.klient_przeterminowane);
      const lim = safeNumber(r.klient_limit_kredytowy);
      const blok = Boolean(r.klient_blokada);
      const zg = Boolean(r.klient_zgoda);
      const format = (n: number) => n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      
      const rowStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' };
      const labelStyle: React.CSSProperties = { textAlign: 'left', whiteSpace: 'nowrap' };
      const valueStyle: React.CSSProperties = { textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' };
      const przStyle: React.CSSProperties = { ...valueStyle, color: prz > 0 ? '#dc2626' : undefined, fontWeight: prz > 0 ? 600 : 500 };
      
      return (
        <div style={{ minWidth: 200 }}>
          <div style={rowStyle}><span style={labelStyle}>Zadłużenie:</span><span style={valueStyle}>{format(zad)} zł</span></div>
          <div style={rowStyle}><span style={labelStyle}>Niezafakturowane:</span><span style={valueStyle}>{format(niezafakturowane)} zł</span></div>
          <div style={{ ...rowStyle, borderTop: '1px solid #e5e5e5', marginTop: 2, paddingTop: 4 }}><span style={{ ...labelStyle, fontWeight: 600 }}>Razem:</span><span style={{ ...valueStyle, fontWeight: 600 }}>{format(razem)} zł</span></div>
          <div style={{ ...rowStyle, marginTop: 4 }}><span style={labelStyle}>Przeterminowane:</span><span style={przStyle}>{format(prz)} zł</span></div>
          <div style={rowStyle}><span style={labelStyle}>Limit:</span><span style={valueStyle}>{format(lim)} zł</span></div>
          {zg && <div style={{ marginTop: 6, color: '#16a34a', fontWeight: 600 }}>✅ ZGODA</div>}
          {!zg && blok && <div style={{ marginTop: 6, color: '#dc2626', fontWeight: 600 }}>⛔ BLOKADA</div>}
        </div>
      );
    } catch {
      return null;
    }
  };
  // Add CSS styles to hide calendar icon in time inputs
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = timeInputStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Initialize splits for the currently edited row when editing starts or qty/leading changes
  useEffect(() => {
    if (editingId === null) return;
    const row = zamowienia.find(z => z.id === editingId);
    if (!row) return;
    if (isAddingNew) return;
    if (!isUsingQtyReducerForRow(row.id)) {
      initQtyFromRow(row);
    }
  }, [editingId, isAddingNew, zamowienia, initQtyFromRow]);

  const updateWezelSplit = (row: Zamowienie, wezelId: number, value: number) => {
    const v = Number(value) || 0;
    if (isUsingQtyReducerForRow(row.id)) {
      applyQtyAction({ type: 'SET_NODE', id: wezelId, value: v });
    } else {
      setSplitsByOrder(prev => {
        const current: Record<number, number> = { ...(prev[row.id] || {}) };
        for (const w of wezels) if (current[w.id] === undefined) current[w.id] = 0;
        current[wezelId] = Math.max(0, v);
        setSplitsDirtyByOrder(prevDirty => ({ ...prevDirty, [row.id]: true }));
        return { ...prev, [row.id]: current };
      });
    }
  };

  const putZamowienieSplits = async (
    zamowienieId: number,
    overrideMap?: Record<number, number>,
    expectedOrderQty?: number
  ) => {
    try {
      if (isTempId(zamowienieId)) return; // skip saving for temporary rows
      
      const map = overrideMap || splitsByOrder[zamowienieId];
      if (!map) return;
      // Capture old splits BEFORE sending request (shallow copy)
      const oldSplitsForOrder: Record<number, number> = { ...(splitsByOrder[zamowienieId] || {}) };
      // Normalize new map (number, rounded)
      const newSplitsForOrder: Record<number, number> = Object.fromEntries(
        Object.entries(map).map(([k, v]) => [Number(k), round2(Number(v) || 0)])
      );
      const payload = {
        splits: Object.entries(newSplitsForOrder).map(([wezel_id, zamowione]) => ({ wezel_id: Number(wezel_id), zamowione: Number(zamowione || 0) })),
        enforceSumEq: true
      };
      
      // Debug: Check if sum matches order quantity
      const sum = payload.splits.reduce((acc, s) => acc + Number(s.zamowione || 0), 0);
      const orderQty = (typeof expectedOrderQty === 'number')
        ? Number(expectedOrderQty)
        : Number((zamowienia.find(z => z.id === zamowienieId)?.zamowienie_ilosc) || 0);
     
      if (!nearlyEqual(sum, orderQty, QTY_EPS)) {
        setValidationError('Suma ilości na węzłach musi równać się polu Ilość. Popraw wartości.');
        return;
      }
      
      const response = await authenticatedFetch(`/api/zamowienia/${zamowienieId}/splits`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Splits update failed: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      // No local aggregate fallback; rely strictly on DB NOTIFY events.


    } catch (error) {
      // Don't throw the error to prevent breaking the UI
    }
  };
  

  const getAltSocketUrl = (): string => {
    return '';
  };
  const toggleExpand = async (row: Zamowienie) => {
    const id = row.id;
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    const nowExpanded = !expanded[id];
    if (nowExpanded && !isTempId(id)) {
      try {
        
        const res = await authenticatedFetch(`/api/zamowienia/${id}/wz`, { credentials: 'include' });
        if (res.ok) {
          const j = await res.json();
          setWzByOrder(prev => ({ ...prev, [id]: j.wz || [] }));
        }
      } catch {}
    }
  };

  const handleWzTransfer = async () => {
    if (!wzTransferModal.wz || !wzTransferModal.zamowienieId) return;
    
    const numer = Number(wzTransferData.numer);
    const tydzien = Number(wzTransferData.tydzien);
    
    if (!numer || !tydzien) {
      alert('Proszę podać numer zamówienia i tydzień');
      return;
    }

    try {
      const res = await authenticatedFetch(`/api/zamowienia/wz/${wzTransferModal.wz.id}/transfer`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          newZamowienieNumer: numer,
          newZamowienieTydzien: tydzien
        })
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        // Odśwież listę WZ dla obu zamówień
        if (wzTransferModal.zamowienieId) {
          const resOld = await authenticatedFetch(`/api/zamowienia/${wzTransferModal.zamowienieId}/wz`, { credentials: 'include' });
          if (resOld.ok) {
            const jOld = await resOld.json();
            setWzByOrder(prev => ({ ...prev, [wzTransferModal.zamowienieId!]: jOld.wz || [] }));
          }
        }
        
        // Odśwież główną listę zamówień
        await loadZamowienia();
        
        setWzTransferModal({ visible: false, wz: null, zamowienieId: null });
        setWzTransferData({ numer: '', tydzien: '' });
        alert('WZ zostało przeniesione pomyślnie');
      } else {
        alert(data.message || 'Błąd podczas przenoszenia WZ');
      }
    } catch (error) {
      console.error('Error transferring WZ:', error);
      alert('Błąd podczas przenoszenia WZ');
    }
  };
  // Add refs for request cancellation and debouncing
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const splitsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const regionDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const wezelDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isRegionFetchInFlightRef = useRef<boolean>(false);
  const isWezelFetchInFlightRef = useRef<boolean>(false);
  const hasInitialPollRef = useRef(false);
  const lastKomunikatNotificationRef = useRef<string>('');
  const lastAlertRef = useRef<number>(0);
  // Debug/diagnostic: track if Socket.IO events are processed locally
  const socketEventCountRef = useRef<number>(0);
  const socketLastEventAtRef = useRef<number>(0);
  // Cache of orders for delta calculations
  const orderCacheRef = useRef<Record<number, Partial<Zamowienie>>>({});
  // Suppress local aggregates recalculation for a short time after save to avoid socket echo double updates
  const suppressAggregatesUntilRef = useRef<number>(0);
  // Track last local save timestamps per zamowienie to correlate with socket echoes
  const lastLocalSaveByOrderRef = useRef<Record<number, number>>({});
  // Track last locally-applied DB event signature per zamowienie to dedupe exact socket echo (no time windows)
  const lastLocalEventSigRef = useRef<Record<number, string>>({});
  // Track locally applied split deltas (per order, per wezel) to skip echo socket deltas
  const lastLocalSplitApplyRef = useRef<Record<number, Record<number, { qty: number; at: number }>>>({});
  // Track if zamowienie DELETE already adjusted Węzeł aggregates (to avoid double subtract on subsequent split DELETE events)
  const orderDeleteWezelAdjustedRef = useRef<Record<number, boolean>>({});
  // Track last processed notification sequence number (for gap detection)
  const lastSeqRef = useRef<number>(0);
  // Suppress aggregates while the user is typing inline (ilość / split fields)
  // Removed typing suppression - we will disable aggregates during editing entirely
  const alertOnce = useCallback((msg: string) => {
    try {
      const now = Date.now();
      if (now - lastAlertRef.current < 600) return;
      lastAlertRef.current = now;
      alert(msg);
    } catch {
      // no-op
    }
  }, []);

 
  // Helper function to get current hour rounded to nearest 30 minutes
  // Logic: 
  // - 00-14 min → round down to :00
  // - 15-44 min → round to :30  
  // - 45-59 min → round up to :00 (next hour)
  const getCurrentHourRounded = (): string => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Round to nearest 30 minutes
    // Logic: 
    // - 00-14 min → round down to :00
    // - 15-44 min → round to :30  
    // - 45-59 min → round up to :00 (next hour)
    let roundedHour = currentHour;
    let roundedMinutes = 0;
    
    if (currentMinutes < 15) {
      // Round down to previous 30-minute mark
      roundedMinutes = 0;
    } else if (currentMinutes < 45) {
      // Round to current 30-minute mark
      roundedMinutes = 30;
    } else {
      // Round up to next 30-minute mark (next hour)
      roundedHour = (currentHour + 1) % 24;
      roundedMinutes = 0;
    }
    
    // Format as HH:MM
    return `${roundedHour.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
  };

  // Helper function to calculate end time (start time + 2 hours, rounded to 30 minutes)
  const calculateEndTime = (startTime: string): string => {
    if (!startTime || startTime.length !== 5) return startTime; // Invalid format
    
    const [hours, minutes] = startTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return startTime; // Invalid numbers
    
    // Add 2 hours
    let newHours = hours + 2;
    if (newHours >= 24) {
      newHours = newHours % 24;
    }
    
    // Round minutes to nearest 30-minute mark
    // Logic: 
    // - 00-14 min → round down to :00
    // - 15-44 min → round to :30  
    // - 45-59 min → round up to :00 (next hour)
    let roundedMinutes = 0;
    if (minutes < 15) {
      roundedMinutes = 0;
    } else if (minutes < 45) {
      roundedMinutes = 30;
    } else {
      roundedMinutes = 0;
      // If rounding up to next 30-minute mark, also adjust hour
      if (minutes >= 45) {
        newHours = (newHours + 1) % 24;
      }
    }
    
    // Format as HH:MM (rounded to 30 minutes)
    return `${newHours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
  };

  // Helper function to round any time to nearest 30 minutes
  // Logic: 
  // - 00-14 min → round down to :00
  // - 15-44 min → round to :30  
  // - 45-59 min → round up to :00 (next hour)
  const roundTimeTo30Minutes = (time: string): string => {
    if (!time || time.length !== 5) return time; // Invalid format
    
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time; // Invalid numbers
    
    // Round to nearest 30 minutes
    let roundedHours = hours;
    let roundedMinutes = 0;
    
    if (minutes < 15) {
      // Round down to previous 30-minute mark
      roundedMinutes = 0;
    } else if (minutes < 45) {
      // Round to current 30-minute mark
      roundedMinutes = 30;
    } else {
      // Round up to next 30-minute mark (next hour)
      roundedHours = (hours + 1) % 24;
      roundedMinutes = 0;
    }
    
    // Format as HH:MM
    return `${roundedHours.toString().padStart(2, '0')}:${roundedMinutes.toString().padStart(2, '0')}`;
  };

  // Helper function to format time display (remove seconds if present)
  const formatTimeDisplay = (time: string): string => {
    if (!time) return '';
    
    // If time is already in HH:MM format, return as is
    if (time.length === 5 && time.includes(':')) {
      return time;
    }
    
    // If time is in HH:MM:SS format, extract only HH:MM
    if (time.length === 8 && time.includes(':')) {
      const parts = time.split(':');
      if (parts.length === 3) {
        return `${parts[0]}:${parts[1]}`;
      }
    }
    
    // Return original if format is unknown
    return time;
  };

  const generateTabs = useCallback(() => {
    if (!selectedRegion) return;


    const newTabs: Tab[] = [
      {
        id: 'region',
        name: regionName || 'Region',
        isRegion: true
      }
    ];

    if (wezels && wezels.length > 0) {
      // Ensure tabs follow wezel.id ascending
      const sortedWezels = [...wezels].sort((a, b) => Number(a.id) - Number(b.id));
      // Stan łączności NIE jest tu wyliczany: zakładki przebudowują się rzadko, a wskaźnik
      // musi odliczać na bieżąco. Czyta go sam ze wspólnego magazynu w WskaznikWezla.
      sortedWezels.forEach(wezel => {
        newTabs.push({
          id: `wezel-${wezel.id}`,
          name: wezel.nazwa,
          wezelId: wezel.id,
          isRegion: false,
        } as any);
      });
    }

    if (wezels && wezels.length > 0) {
      // Only set activeTab to 'region' if it's not already set or if it's not a valid tab
      setActiveTab(prev => {
        const validTabIds = newTabs.map(tab => tab.id);
        const newActiveTab = validTabIds.includes(prev) ? prev : 'region';
        return newActiveTab;
      });
    } else {
    }
    // Ensure tabs state is updated
    setTabs(newTabs);
  }, [selectedRegion, wezels, regionName]);
  const { status: connectionStatus, checkHealth, setStatus } = useConnection();
  const loadZamowienia = useCallback(async () => {
    if (!selectedRegion) return;
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      
      // Ensure backend connectivity before attempting data fetch
      const ok = await checkHealth();
      if (!ok) {
        setStatus('offline');
        throw new Error('Backend health check failed');
      }
      
      let url = '/api/zamowienia';
      const params = new URLSearchParams();
      
      // Always constrain by active region; fetch all orders for region/date.
      // Filtering for WEZEL tab (leading or split>0) is handled locally.
      params.append('region_id', selectedRegion.toString());
      
      params.append('zamowienie_dzien', selectedDate);
      
      url += `?${params.toString()}`;
      
      
      const response = await authenticatedFetch(url, {
        headers: {
          
        },
        signal: abortControllerRef.current?.signal
      });
      
       
      if (response.ok) {
        const data = await response.json();
 
        const uniqueById = (arr: any[]) => {
          const m = new Map<number, any>();
          (arr || []).forEach((z: any) => m.set(Number(z.id), z));
          return Array.from(m.values());
        };
        
        // Sort the data by zamowienie_godzinaod, then zamowienie_numer for consistent ordering
        const sortZamowienia = (zamowienia: any[]) => {
          const toTime = (s?: string) => {
            if (!s) return 0;
            const [hh, mm] = String(s).split(':').map(n => parseInt(n || '0', 10));
            return (hh || 0) * 60 + (mm || 0);
          };
          
          return [...zamowienia].sort((a, b) => {
            const aStart = toTime(a.zamowienie_godzinaod);
            const bStart = toTime(b.zamowienie_godzinaod);
            if (aStart !== bStart) return aStart - bStart;
            // Use zamowienie_numer as secondary sort for consistent ordering
            return (a.zamowienie_numer || 0) - (b.zamowienie_numer || 0);
          });
        };
        
        const list = sortZamowienia(uniqueById(data.zamowienia || []));
        
        // Debug: sprawdź pierwsze zamówienie
        if (list.length > 0) {
          console.log('[Load] Sample zamowienie data:', {
            id: list[0].id,
            zamowienie_transport: list[0].zamowienie_transport,
            zamowienie_uwagi2: list[0].zamowienie_uwagi2,
            allFields: Object.keys(list[0])
          });
        }
        
        setZamowienia(list);
        setStatus('online');
        
        // Update lastSeq from server (atomic with data fetch)
        const serverSeq = Number(data.current_seq || 0);
        if (serverSeq > 0) {
          socketService.setLastSeq(serverSeq);
          console.log('[Load] Updated lastSeq from server:', serverSeq);
        }
        
        // Load splits in bulk for all orders
        if (list.length > 0) {
          try {
            const zamowienie_ids = list.map((z: any) => Number(z.id));
            const bulkResp = await authenticatedFetch('/api/zamowienia/splits/bulk', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ zamowienie_ids, date: selectedDate })
            });
            
            if (bulkResp.ok) {
              const bulkData = await bulkResp.json();
              const splitsMap: Record<number, Record<number, number>> = {};
              const realizedMap: Record<number, Record<number, number>> = {};
              const realizacjaMap: Record<number, number> = {};
              
              // Group splits by zamowienie_id
              (bulkData.splits || []).forEach((s: any) => {
                const zid = Number(s.zamowienie_id);
                const wid = Number(s.wezel_id);
                
                if (!splitsMap[zid]) splitsMap[zid] = {};
                if (!realizedMap[zid]) realizedMap[zid] = {};
                if (!realizacjaMap[zid]) realizacjaMap[zid] = 0;
                
                splitsMap[zid][wid] = Number(s.zamowione || 0);
                realizedMap[zid][wid] = Number(s.zrealizowane || 0);
                realizacjaMap[zid] += Number(s.zrealizowane || 0);
              });
              
              setSplitsByOrder(splitsMap);
              setRealizedByOrderWezel(realizedMap);
              setRealizacjaByOrder(realizacjaMap);
              
              console.log('[Load] Bulk splits loaded:', {
                ordersCount: list.length,
                splitsCount: bulkData.splits?.length || 0,
                ordersWithSplits: Object.keys(splitsMap).length
              });
            }
          } catch (err) {
            console.error('[Load] Failed to fetch bulk splits:', err);
          }
        }
        
        // Load recept confirmation status from backend
        setBetonKlasaConfirmed(prev => {
          const newMap = new Map(prev);
          list.forEach((z: any) => {
            if (z.beton_klasa_is_confirmed !== null && z.beton_klasa_is_confirmed !== undefined) {
              newMap.set(Number(z.id), z.beton_klasa_is_confirmed === true);
            }
          });
          return newMap;
        });
        // Warm order cache for delta
        try {
          const oc: Record<number, Partial<Zamowienie>> = {};
          list.forEach((z: any) => {
            oc[Number(z.id)] = {
              id: Number(z.id),
              region_id: Number(z.region_id),
              zamowienie_dzien: z.zamowienie_dzien,
              zamowienie_ilosc: Number(z.zamowienie_ilosc || 0),
              zamowienie_anulowane: Boolean(z.zamowienie_anulowane)
            } as any;
          });
          orderCacheRef.current = oc;
        } catch {}
      } else if (response.status === 429) {
        console.warn('Rate limit exceeded. Please wait before making another request.');
        alert('Zbyt wiele żądań. Proszę poczekać chwilę przed ponowną próbą.');
      } else {
        console.error('🔍 API error status:', response.status);
        const errorText = await response.text();
        console.error('🔍 API error response:', errorText);
        setStatus('offline');
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      console.error('Error loading zamowienia:', error);
      setStatus('offline');
    } finally {
      setLoading(false);
    }
    // Zapytanie zalezy WYLACZNIE od regionu i dnia - takie parametry trafiaja do URL-a.
    // `activeTab` i `tabs` byly tu wczesniej, choc funkcja nigdzie ich nie czyta. Skutki byly dwa:
    // przy kazdej przebudowie zakladek powstawala nowa referencja loadZamowienia, co odpalalo
    // efekty trzymajace ja w zaleznosciach (m.in. ponowna subskrypcje socketu), a zmiana zakladki
    // wymuszala pobranie tych samych danych od nowa. Podzial na wezly robi rowsForTab lokalnie.
  }, [selectedRegion, selectedDate]);

  const debouncedLoadZamowienia = useCallback(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      const region = selectedRegionRef.current;
      if (region) {
        loadZamowienia();
      }
    }, 0);
  }, [loadZamowienia]);
  const [pendingRowChange, setPendingRowChange] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; targetRowId: number | null }>(() => ({ visible: false, targetRowId: null }));

  const hasRowChanges = (id: number): boolean => {
    // Prefer baseline captured when entering edit mode for more accurate comparison
    const baseline = editBaselineRef.current && editBaselineRef.current.rowId === id ? editBaselineRef.current : null;
    const original = baseline ? (baseline.fields as Zamowienie) : zamowienia.find(z => z.id === id);
    if (!original) return false;
    // Compare edited data to baseline/original for a subset of fields
    const fieldsToCheck: (keyof Zamowienie)[] = [
      'zamowienie_godzinaod','zamowienie_godzinado','zamowienie_dzien','klient_nazwa','klient_nip','klient_budowa','klient_telefon','zamowienie_okolo','beton_klasa','beton_konsystencja','pompa_nazwa','pompa_numer','pompa_kierowca','zamowienie_transport','zamowienie_uwagi1','zamowienie_uwagi2','zamowienie_gotowka','zamowienie_wezel_wiodacy','region_id'
    ];
    for (const f of fieldsToCheck) {
      if (String((editData as any)[f] ?? '') !== String((original as any)[f] ?? '')) return true;
    }
    // Compare global quantity with baseline/original
    const q = getCurrentQuantities(id);
    const originalGlobal = Number((original as any).zamowienie_ilosc || 0);
    if (!nearlyEqual(q.global, originalGlobal, QTY_EPS)) return true;
    // Compare splits map against baseline splits if available
    const baseSplits = baseline ? (baseline.splits || {}) : undefined;
    if (baseSplits) {
      const currentNodes = q.nodes || {};
      const keySet = new Set([...Object.keys(baseSplits), ...Object.keys(currentNodes)]);
      for (const k of Array.from(keySet)) {
        const kn = Number(k);
        const a = Number(baseSplits[kn] || 0);
        const b = Number(currentNodes[kn] || 0);
        if (!nearlyEqual(a, b, QTY_EPS)) return true;
      }
    }
    return false;
  };

  const handleEdit = (zamowienie: Zamowienie) => {
    // If another row is being edited, enforce single-row editing with confirmation
    if (editingId !== null && editingId !== zamowienie.id) {
      if (hasRowChanges(editingId)) {
        setConfirmModal({ visible: true, targetRowId: zamowienie.id });
        setPendingRowChange(zamowienie.id);
        return;
      }
      // No changes: close current edit and switch
      setEditingId(zamowienie.id);
      setEditData(zamowienie);
      // Capture baseline for the newly edited row
      try {
        editBaselineRef.current = {
          rowId: zamowienie.id,
          fields: { ...zamowienie },
          splits: { ...(splitsByOrder[zamowienie.id] || {}) }
        };
      } catch {}
      // Default intent: ilość
      lastIntentFocusRef.current = { field: 'zamowienie_ilosc', rowId: zamowienie.id };
      return;
    }
    // Same row or none edited yet
    setEditingId(zamowienie.id);
    setEditData(zamowienie);
    // Capture baseline when starting to edit this row
    try {
      editBaselineRef.current = {
        rowId: zamowienie.id,
        fields: { ...zamowienie },
        splits: { ...(splitsByOrder[zamowienie.id] || {}) }
      };
    } catch {}
    lastIntentFocusRef.current = { field: 'zamowienie_ilosc', rowId: zamowienie.id };
  };

  const beginEditAt = (row: Zamowienie, fieldKey: string) => {
    try {
      lastIntentFocusRef.current = { field: fieldKey, rowId: row.id };
      handleEdit(row);
    } catch {}
  };

  const focusField = (rowId: number, fieldKey: string) => {
    try {
      const selector = `input[data-field="${fieldKey}"][data-row-id="${rowId}"]`;
      const el = document.querySelector(selector) as HTMLInputElement | null;
      if (el) { el.focus(); el.select?.(); return true; }
    } catch {}
    return false;
  };

  // Focus input when entering edit mode (prefer last intent, else ilość)
  useEffect(() => {
    if (editingId == null) return;
    // Defer focus to end of paint to avoid losing it due to re-render
    const id = window.requestAnimationFrame(() => {
      const target = lastIntentFocusRef.current;
      if (target && target.rowId === editingId && target.field) {
        const ok = focusField(editingId, target.field);
        if (ok) { lastIntentFocusRef.current = null; return; }
      }
      focusField(editingId, 'zamowienie_ilosc');
    });
    return () => window.cancelAnimationFrame(id);
  }, [editingId]);

  // After validation modal closes, move focus to the field that failed validation
  useEffect(() => {
    if (validationError === null) {
      const target = lastValidationFocusRef.current;
      if (target && typeof target.rowId === 'number' && target.field) {
        const rowIdNum = Number(target.rowId);
        const fieldKeyStr = String(target.field);
        window.requestAnimationFrame(() => {
          focusField(rowIdNum, fieldKeyStr);
          lastValidationFocusRef.current = null;
        });
      }
    }
  }, [validationError]);

  const getCurrentQuantities = (id: number) => {
    if (isUsingQtyReducerForRow(id) && qtyState.currentRowId === id) {
      return { global: qtyState.globalQty, nodes: qtyState.nodes, leadingId: qtyState.leadingId };
    }
    const original = zamowienia.find(z => z.id === id);
    return {
      global: Number(editData.zamowienie_ilosc ?? original?.zamowienie_ilosc ?? 0),
      nodes: splitsByOrder[id] ?? {},
      leadingId: Number(editData.zamowienie_wezel_wiodacy ?? original?.zamowienie_wezel_wiodacy ?? 0),
    };
  };
  const handleSave = async (id: number) => {
    try {
      
      const original = zamowienia.find(z => z.id === id);
      
      // Filter out fields that are not part of the database schema
      const q = getCurrentQuantities(id);
      // Validate splits vs global using reducer state if available
      const sumNodes = Object.values(q.nodes).reduce((acc, v) => acc + Number(v || 0), 0);
      if (!nearlyEqual(sumNodes, q.global, QTY_EPS)) {
        setValidationError('Suma ilości na węzłach musi równać się polu Ilość. Popraw wartości.');
        // After modal close, focus on the global quantity field
        lastValidationFocusRef.current = { field: 'zamowienie_ilosc', rowId: id };
        return;
      }

      const dataToSend = {
        zamowienie_godzinaod: editData.zamowienie_godzinaod,
        zamowienie_godzinado: editData.zamowienie_godzinado,
        zamowienie_dzien: editData.zamowienie_dzien,
        klient_nazwa: editData.klient_nazwa,
        klient_nip: editData.klient_nip,
        klient_budowa: editData.klient_budowa,
        klient_telefon: editData.klient_telefon,
        zamowienie_okolo: editData.zamowienie_okolo,
        zamowienie_ilosc: q.global,

        beton_klasa: editData.beton_klasa,
        beton_konsystencja: editData.beton_konsystencja,
        pompa_nazwa: editData.pompa_nazwa,
        pompa_numer: editData.pompa_numer,
        pompa_kierowca: editData.pompa_kierowca,
        zamowienie_transport: editData.zamowienie_transport,
        zamowienie_uwagi1: editData.zamowienie_uwagi1,
        zamowienie_uwagi2: editData.zamowienie_uwagi2,
        zamowienie_gotowka: editData.zamowienie_gotowka,
        zamowienie_wezel_wiodacy: q.leadingId,
        region_id: editData.region_id
      };

      // If splits are dirty and quantity changed, persist splits first with enforce disabled to avoid race
      const splits = isUsingQtyReducerForRow(id) && qtyState.currentRowId === id ? q.nodes : splitsByOrder[id];
      if (splits) {
        // Strict sum validation before save
        const sum = Object.values(splits).reduce((acc, v) => acc + Number(v || 0), 0);
        const total = q.global;
        if (!nearlyEqual(sum, total, QTY_EPS)) {
          setValidationError('Suma ilości na węzłach musi równać się polu Ilość. Popraw wartości.');
          lastValidationFocusRef.current = { field: 'zamowienie_ilosc', rowId: id };
          return;
        }
      }

      const response = await authenticatedFetch(`/api/zamowienia/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        const responseData = await response.json();
        const updatedZamowienie = responseData.zamowienie;
        // Capture old state for delta
        const oldRow = original || null;
        const oldSplitsMap: Record<number, number> = { ...(splitsByOrder[id] || {}) };
        setZamowienia(prev => prev.map(z => z.id === id ? updatedZamowienie : z));
        // Aggregates delta update only if updated row matches current filters
        try {
          const oldSplitsForRow: Record<number, number> = oldSplitsMap;
          // IMPORTANT: Do NOT adjust Węzeł aggregates here. Splits are saved separately.
          // We only adjust Region now; Węzeł deltas will be applied by socket `zamowienie_wezel` events after DB save.
          const newSplitsForRow: Record<number, number> = oldSplitsForRow;
          const sameDate = String(updatedZamowienie.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
          const sameRegion = Number(updatedZamowienie.region_id) === Number(selectedRegionRef.current);
          if (sameDate && sameRegion) { debouncedFetchRegion(); }
        } catch {}
        // Update order cache for delta processing next time
        try {
          orderCacheRef.current[id] = {
            id: Number(updatedZamowienie.id),
            region_id: Number(updatedZamowienie.region_id),
            zamowienie_dzien: updatedZamowienie.zamowienie_dzien,
            zamowienie_ilosc: Number(updatedZamowienie.zamowienie_ilosc || 0),
            zamowienie_anulowane: Boolean(updatedZamowienie.zamowienie_anulowane)
          } as any;
        } catch {}
        setEditingId(null);
        setEditData({});
        // Record exact local event signature to dedupe socket echo by fingerprint (no time window)
        try {
          const sigPayload = {
            table: 'zamowienie',
            op: 'UPDATE',
            id: id,
            row_qty: Number(updatedZamowienie?.zamowienie_ilosc ?? NaN),
            old_row_qty: Number((original as any)?.zamowienie_ilosc ?? NaN),
            row_wezel_id: NaN,
            row_split: NaN,
            old_row_split: NaN,
            row_anul: Boolean((updatedZamowienie as any)?.zamowienie_anulowane),
            old_row_anul: Boolean((original as any)?.zamowienie_anulowane)
          };
          lastLocalEventSigRef.current[id] = JSON.stringify(sigPayload);
        } catch {}

        // Save splits after successful zamowienie update only if splits changed
        try {
          if (splitsDirtyByOrder[id]) {
            // Use the just-saved quantity from server as the comparison baseline
            const expectedQty = Number(updatedZamowienie?.zamowienie_ilosc || 0);
            await putZamowienieSplits(id, isUsingQtyReducerForRow(id) && qtyState.currentRowId === id ? q.nodes : undefined, expectedQty);
            setSplitsDirtyByOrder(prev => ({ ...prev, [id]: false }));
          }
        } catch (error) {
          console.error('Error saving splits after zamowienie update:', error);
        }

        // Refresh pump schedule for incremental update on label/time/pump changes
        try {
          const oldRow = original || {} as any;
          const pumpChanged =
            String((oldRow.pompa_nazwa || '')).trim() !== String((updatedZamowienie.pompa_nazwa || '')).trim() ||
            String((oldRow.pompa_numer || '')).trim() !== String((updatedZamowienie.pompa_numer || '')).trim() ||
            String((oldRow.pompa_kierowca || '')).trim() !== String((updatedZamowienie.pompa_kierowca || '')).trim();
          const timeChanged =
            String(oldRow.zamowienie_godzinaod || '') !== String(updatedZamowienie.zamowienie_godzinaod || '') ||
            String(oldRow.zamowienie_godzinado || '') !== String(updatedZamowienie.zamowienie_godzinado || '');
          const labelChanged =
            Number(oldRow.zamowienie_ilosc || 0) !== Number(updatedZamowienie.zamowienie_ilosc || 0) ||
            String((oldRow.klient_nazwa || '')).trim() !== String((updatedZamowienie.klient_nazwa || '')).trim() ||
            String((oldRow.klient_budowa || '')).trim() !== String((updatedZamowienie.klient_budowa || '')).trim();
          if (pumpChanged || timeChanged || labelChanged) {
            // Incremental local update without full reload
            try {
              const patch: any = { id: id, ...updatedZamowienie };
              window.dispatchEvent(new CustomEvent('pumpSchedule:orderUpdate', { detail: { row: patch } }));
            } catch {}
          }
        } catch {}

        // Refresh map ONLY if Budowa changed
        const oldBudowa = (original?.klient_budowa || '').trim().toUpperCase();
        const newBudowa = (updatedZamowienie.klient_budowa || '').trim().toUpperCase();
        const budowaChanged = oldBudowa !== newBudowa;
        if (budowaChanged) {
          try {
            window.dispatchEvent(new CustomEvent('map:refresh', {
              detail: {
                date: selectedDate, // always local YYYY-MM-DD
                region_id: updatedZamowienie.region_id
              }
            }));
          } catch {}
        }
        // Clear quantity-related UI/reducer state after successful save to avoid leaking state to next edits
        resetQtyUiState();
      } else {
        const errorData = await response.json();
        console.error('Backend validation error:', errorData);
        setValidationError(formatValidationErrors(errorData));
        focusFirstErrorField(errorData);
      }
    } catch (error) {
      console.error('Error saving zamowienie:', error);
    }
  };

  const handleCancel = () => {
    // If we're canceling a new row (isAddingNew is true), remove it from the table
    if (isAddingNew && editingId !== null) {
      setZamowienia(prev => prev.filter(z => z.id !== editingId));
    }
    setEditingId(null);
    setEditData({});
    setIsAddingNew(false);
    // Also reset quantity-related UI/reducer state
    resetQtyUiState();
  };
  // Confirm save/discard when switching edited rows
  const confirmSwitchSave = async () => {
    try {
      if (editingId == null || confirmModal.targetRowId == null) return;
      await handleSave(editingId);
      // After save, switch to target row
      const target = zamowienia.find(z => z.id === confirmModal.targetRowId);
      if (target) {
        setEditingId(target.id);
        setEditData(target);
      } else {
        setEditingId(null);
        setEditData({});
      }
    } finally {
      setConfirmModal({ visible: false, targetRowId: null });
      setPendingRowChange(null);
    }
  };
  const confirmSwitchDiscard = () => {
    try {
      // Discard changes; if new row, remove it; otherwise reset edit
      if (isAddingNew && editingId !== null) {
        setZamowienia(prev => prev.filter(z => z.id !== editingId));
      }
      const targetId = confirmModal.targetRowId;
      setEditingId(null);
      setEditData({});
      setIsAddingNew(false);
      // Ensure quantity-related UI/reducer state is fully cleared when discarding
      resetQtyUiState();
      if (targetId != null) {
        const target = zamowienia.find(z => z.id === targetId);
        if (target) {
          setEditingId(target.id);
          setEditData(target);
        }
      }
    } finally {
      setConfirmModal({ visible: false, targetRowId: null });
      setPendingRowChange(null);
    }
  };
  const formatValidationErrors = (errorData: any) => {
    if (errorData.errors && Array.isArray(errorData.errors)) {
      const fieldTranslations: { [key: string]: string } = {
        'zamowienie_godzinaod': 'Godzina Od',
        'zamowienie_godzinado': 'Godzina Do',
        'zamowienie_dzien': 'Dzień',
        'klient_nazwa': 'Nazwa Klienta',
        'klient_nip': 'NIP Klienta',
        'klient_budowa': 'Budowa',
        'klient_telefon': 'Telefon',
        'zamowienie_okolo': 'Ok',
        'zamowienie_ilosc': 'Ilość',
        'beton_klasa': 'Recepta Betonu',
        'beton_konsystencja': 'Konsystencja',
        'pompa_nazwa': 'Nazwa Pompy',
        'pompa_numer': 'Nr p.',
        'pompa_kierowca': 'Operator',
        'zamowienie_transport': 'Transport',
        'zamowienie_uwagi1': 'Uwagi 1',
        'zamowienie_uwagi2': 'Uwagi 2',
        'zamowienie_gotowka': 'Gotówka',
        'zamowienie_wezel_wiodacy': 'Węzeł Wiodący',
        'region_id': 'Region'
      };

      const errorTranslations: { [key: string]: string } = {
        'is required': 'jest wymagane',
        'is not allowed to be empty': 'nie może być puste',
        'must be a number': 'musi być liczbą',
        'must be an integer': 'musi być liczbą całkowitą',
        'must be a string': 'musi być tekstem',
        'must be a boolean': 'musi być wartością logiczną',
        'must be a valid date': 'musi być prawidłową datą',
        'must be greater than': 'musi być większe niż',
        'must be less than': 'musi być mniejsze niż',
        'must be greater than or equal to': 'musi być większe lub równe',
        'must be less than or equal to': 'musi być mniejsze lub równe',
        'length must be': 'długość musi wynosić',
        'contains an invalid value': 'zawiera nieprawidłową wartość',
        'is not allowed': 'jest niedozwolone',
        'must contain': 'musi zawierać',
        'fails to match the required pattern': 'nie pasuje do wymaganego wzorca'
      };

      const detailedErrors = errorData.errors.map((error: string) => {
        let translatedError = error;
        let fieldName = '';
        
        // First, extract field name and translate it
        for (const [field, translation] of Object.entries(fieldTranslations)) {
          if (error.includes(`"${field}"`) || error.includes(field)) {
            fieldName = translation;
            // Remove field name from error message
            translatedError = error.replace(`"${field}"`, '').replace(field, '').trim();
            break;
          }
        }
        
        // Then translate error messages
        for (const [eng, pol] of Object.entries(errorTranslations)) {
          if (translatedError.includes(eng)) {
            translatedError = translatedError.replace(eng, pol);
            break;
          }
        }
        
        // Clean up the message
        translatedError = translatedError.replace(/^[\s\.\,\-]+|[\s\.\,\-]+$/g, '').trim();
        
        if (fieldName) {
          return `• ${fieldName}: ${translatedError}`;
        } else {
          return `• ${translatedError}`;
        }
      });

      return detailedErrors.join('\n');
    }
    return errorData.message || 'Wystąpił nieznany błąd walidacji';
  };

  const focusFirstErrorField = (errorData: any) => {
    try {
      const knownFields = [
        'zamowienie_godzinaod', 'zamowienie_godzinado', 'zamowienie_dzien',
        'klient_nazwa', 'klient_nip', 'klient_budowa', 'klient_telefon',
        'zamowienie_okolo', 'zamowienie_ilosc', 'beton_klasa', 'beton_konsystencja',
        'pompa_nazwa', 'pompa_numer', 'pompa_kierowca', 'zamowienie_transport', 'zamowienie_uwagi1', 'zamowienie_uwagi2',
        'zamowienie_gotowka', 'zamowienie_wezel_wiodacy', 'region_id'
      ];
      let fieldKey: string | null = null;
      if (errorData && Array.isArray(errorData.errors)) {
        const messages: string[] = errorData.errors;
        for (const msg of messages) {
          for (const key of knownFields) {
            if (msg.includes(`"${key}"`) || msg.includes(key)) {
              fieldKey = key;
              break;
            }
          }
          if (fieldKey) break;
        }
      }
      if (!fieldKey) return;
      const rowId = editingId !== null ? editingId : (selectedRowId as number);
      if (rowId === null || rowId === undefined) return;
      // Defer focusing until the modal is dismissed; store target here
      lastValidationFocusRef.current = { field: fieldKey, rowId: Number(rowId) };
    } catch {}
  };
  const handleSaveNew = async (id: number) => {
    try {
      
      // Filter out fields that are not part of the database schema
      const dataToSend = {
        zamowienie_godzinaod: editData.zamowienie_godzinaod,
        zamowienie_godzinado: editData.zamowienie_godzinado,
        zamowienie_dzien: editData.zamowienie_dzien,
        klient_nazwa: editData.klient_nazwa,
        klient_nip: editData.klient_nip,
        klient_budowa: editData.klient_budowa,
        klient_telefon: editData.klient_telefon,
        zamowienie_okolo: editData.zamowienie_okolo,
        zamowienie_ilosc: editData.zamowienie_ilosc,

        beton_klasa: editData.beton_klasa,
        beton_konsystencja: editData.beton_konsystencja,
        pompa_nazwa: editData.pompa_nazwa,
        pompa_numer: editData.pompa_numer,
        pompa_kierowca: editData.pompa_kierowca,
        zamowienie_transport: editData.zamowienie_transport,
        zamowienie_uwagi1: editData.zamowienie_uwagi1,
        zamowienie_uwagi2: editData.zamowienie_uwagi2,
        zamowienie_gotowka: editData.zamowienie_gotowka,
        zamowienie_wezel_wiodacy: editData.zamowienie_wezel_wiodacy,
        region_id: editData.region_id
      };

      // Build splits payload from current inline state (wezel_id -> qty)
      const currentSplits = splitsByOrder[id] || {};
      const splitsPayload = Object.entries(currentSplits).map(([wezel_id, zamowione]) => ({ wezel_id: Number(wezel_id), zamowione: Number(zamowione || 0) }));

      // Strict sum validation before create
      const splitsForNew = splitsByOrder[id];
      if (splitsForNew) {
        const sum = Object.values(splitsForNew).reduce((acc, v) => acc + Number(v || 0), 0);
        const total = Number(editData.zamowienie_ilosc || 0);
        if (sum !== total) {
          alertOnce('Suma ilości na węzłach musi równać się polu Ilość. Popraw wartości.');
          return;
        }
      }

      const response = await authenticatedFetch('/api/zamowienia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
        },
        // Do not include 'splits' in POST to avoid backend validation error
        body: JSON.stringify({ ...dataToSend })
      });

      // OPTIMIZATION: Smart validation for new orders - adjust splits before saving
      const splits = splitsByOrder[id];
      if (splits) {
        const orderQty = Number(editData.zamowienie_ilosc || 0);
        const currentSplits = { ...splits };
        
        // Check if there are values in non-leading wezels
        const nonLeadingWezels = Object.entries(currentSplits).filter(([wezelId, qty]) => {
          const wezelIdNum = Number(wezelId);
          const leadingWezelId = Number(editData.zamowienie_wezel_wiodacy);
          return wezelIdNum !== leadingWezelId && Number(qty || 0) > 0;
        });
        
        if (nonLeadingWezels.length > 0) {
          // There are values in non-leading wezels - try to adjust leading wezel
          const nonLeadingSum = nonLeadingWezels.reduce((acc, [_, qty]) => acc + Number(qty || 0), 0);
          const leadingWezelId = editData.zamowienie_wezel_wiodacy;
          
          if (nonLeadingSum > orderQty) {
            // Impossible to adjust - non-leading wezels exceed total
            setValidationError(`Nieprawidłowe wartości ilości: suma węzłów nie-wiodących (${nonLeadingSum}) przekracza ilość zamówienia (${orderQty}).`);
            return;
          }
          
          // Adjust leading wezel to make sum equal
          const leadingQty = orderQty - nonLeadingSum;
          if (leadingQty < 0) {
            setValidationError(`Nieprawidłowe wartości ilości: nie można ustawić ujemnej wartości na węźle wiodącym.`);
            return;
          }
          
          // Update splits with adjusted leading wezel
          if (leadingWezelId) {
            currentSplits[leadingWezelId] = leadingQty;
            setSplitsByOrder(prev => ({
              ...prev,
              [id]: currentSplits
            }));
          }
          
        } else {
          // No values in non-leading wezels - set leading wezel to total quantity
          const leadingWezelId = editData.zamowienie_wezel_wiodacy;
          if (leadingWezelId) {
            currentSplits[leadingWezelId] = orderQty;
            setSplitsByOrder(prev => ({
              ...prev,
              [id]: currentSplits
            }));
          }
          
        }
        
        // Final validation - sum should now equal order quantity
        const finalSum = Object.values(currentSplits).reduce((acc, val) => acc + Number(val || 0), 0);
        if (Math.abs(finalSum - orderQty) > 0.01) {
          setValidationError(`Błąd walidacji: suma ilości na węzłach (${finalSum}) nie równa się ilości zamówienia (${orderQty}).`);
          return;
        }
      }

      if (response.ok) {
        const responseData = await response.json();
        const newZamowienie = responseData.zamowienie;

        // After successful create, persist splits via PUT on the new id
        try {
          if (Array.isArray(splitsPayload) && splitsPayload.length > 0) {
            await authenticatedFetch(`/api/zamowienia/${newZamowienie.id}/splits`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ splits: splitsPayload, enforceSumEq: true })
            });
            
            // OPTIMIZATION: Update local splits cache instead of API call
            // We know the splits we just saved, so update them locally
            const newSplitsFromPayload: Record<number, number> = {};
            splitsPayload.forEach(split => {
              newSplitsFromPayload[split.wezel_id] = split.zamowione;
            });
            
            setSplitsByOrder(prev => ({
              ...prev,
              [newZamowienie.id]: newSplitsFromPayload
            }));

            // No local aggregate fallback; rely strictly on DB NOTIFY events
            
          }
        } catch {}
        // Store financial data from editData before removing temporary row
        const editDataAny = editData as any;
        const financialData = {
          klient_zadluzenie: editDataAny.klient_zadluzenie,
          klient_limit_kredytowy: editDataAny.klient_limit_kredytowy,
          klient_przeterminowane: editDataAny.klient_przeterminowane,
          klient_blokada: editDataAny.klient_blokada,
          klient_zgoda: editDataAny.klient_zgoda,
          klient_niezafakturowane: editDataAny.klient_niezafakturowane
        };
        // Remove only the temporary row; rely on socket to add the final row
        // But preserve financial data in temporary state until socket arrives
        setZamowienia(prev => {
          // Store financial data for the new order ID if available
          const filtered = prev.filter(z => Number(z.id) !== Number(id));
          if (newZamowienie?.id) {
            // Add financial data to the new order if it exists in filtered list
            return filtered.map(z => {
              if (Number(z.id) === Number(newZamowienie.id)) {
                return { ...z, ...financialData } as any;
              }
              return z;
            });
          }
          return filtered;
        });
        setEditingId(null);
        setEditData({});
        setIsAddingNew(false);
        // Clear quantity-related UI/reducer state after creating a new order
        resetQtyUiState();
        // Mark local save for new order and suppress briefly
        try {
          if (newZamowienie?.id) {
            lastLocalSaveByOrderRef.current[Number(newZamowienie.id)] = Date.now();
            suppressAggregatesUntilRef.current = Date.now() + 1200;
          }
        } catch {}
        // Rely on Socket.IO INSERT to update aggregates to avoid double counting

        // Refresh pump schedule if pump fields are present
        if (
          (newZamowienie.pompa_nazwa && newZamowienie.pompa_nazwa.trim() !== '') ||
          (newZamowienie.pompa_numer && newZamowienie.pompa_numer.trim() !== '')
        ) {
          try {
            window.dispatchEvent(new CustomEvent('pumpSchedule:refresh', {
              detail: {
                date: selectedDate, // always local YYYY-MM-DD
                region_id: newZamowienie.region_id
              }
            }));
          } catch (e) {
            // no-op if CustomEvent is not supported
          }
        }

        // Refresh map on create only if Budowa is present
        if ((newZamowienie.klient_budowa || '').trim() !== '') {
          try {
            window.dispatchEvent(new CustomEvent('map:refresh', {
              detail: {
                date: selectedDate, // always local YYYY-MM-DD
                region_id: newZamowienie.region_id
              }
            }));
          } catch {}
        }
      } else {
        const errorData = await response.json();
        console.error('Backend validation error:', errorData);
        setValidationError(formatValidationErrors(errorData));
        focusFirstErrorField(errorData);
      }
    } catch (error) {
      console.error('Error saving new zamowienie:', error);
    }
  };

  const handleClientSelect = async (client: any) => {
    // Check if client is blocked and show warning
    const isBlocked = (client.zadluzenie > (client.limit_kredytowy || 0)) || 
                     (client.przeterminowane > 0) || 
                     client.blokada;
    
    if (isBlocked && !client.zgoda) {
      const confirmSelection = window.confirm(
        '⚠️ OSTRZEŻENIE!\n\n' +
        'Składanie zamówienia na klienta zabronione!\n\n' +
        `Klient: ${client.nazwa}\n` +
        `NIP: ${client.nip}\n` +
        `Zadłużenie: ${(client.zadluzenie || 0).toLocaleString('pl-PL')} zł\n` +
        `Limit kredytowy: ${(client.limit_kredytowy || 0).toLocaleString('pl-PL')} zł\n` +
        `Przeterminowane: ${(client.przeterminowane || 0).toLocaleString('pl-PL')} zł\n` +
        `Blokada: ${client.blokada ? 'TAK' : 'NIE'}\n` +
        `Zgoda: ${client.zgoda ? 'TAK' : 'NIE'}\n\n` +
        'Czy na pewno chcesz wybrać tego klienta?'
      );
      
      if (!confirmSelection) {
        return; // Don't proceed with selection
      }
    }

    setEditData(prev => ({
      ...prev,
      klient_nazwa: client.nazwa,
      klient_nip: client.nip,
      klient_telefon: client.telefon || '',
      // Store financial data for immediate color indication
      klient_zadluzenie: client.zadluzenie,
      klient_limit_kredytowy: client.limit_kredytowy,
      klient_przeterminowane: client.przeterminowane,
      klient_blokada: client.blokada,
      klient_zgoda: client.zgoda,
      klient_niezafakturowane: client.niezafakturowane
    }));
  };

  const handleClientSelectionComplete = () => {
    // Focus on budowa field after client selection is complete
    setTimeout(() => {
      const budowaInput = document.querySelector(`input[data-field="klient_budowa"][data-row-id="${editingId}"]`) as HTMLInputElement;
      if (budowaInput) {
        budowaInput.focus();
        // Trigger autocomplete by simulating typing
        budowaInput.click();
        budowaInput.dispatchEvent(new Event('focus', { bubbles: true }));
      }
    }, 100);
  };

  // Map modal state for precise Budowa localization
  const [mapModal, setMapModal] = useState<{ visible: boolean; rowId: number | null; value: string }>({
    visible: false,
    rowId: null,
    value: ''
  });

  const openMapModal = (rowId: number) => {
    try {
      const current = (rowId === editingId)
        ? String(editData.klient_budowa || '')
        : String((zamowienia.find(z => z.id === rowId)?.klient_budowa || ''));
      // Log where the map will center: budowa coords or wezel fallback
      try {
        const row = zamowienia.find(z => z.id === rowId) || (editingId === rowId ? (editData as any) : null);
        const lat = Number((row as any)?.budowa_lat);
        const lon = Number((row as any)?.budowa_lon);
        if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0) {
          console.log('[MapModal][click] rowId:', rowId, 'source: budowa', { lat, lon });
        } else {
          let centerWezelId: number | null = null;
          if (activeTab && activeTab.startsWith('wezel-')) {
            const idStr = activeTab.slice('wezel-'.length);
            const idNum = parseInt(idStr, 10);
            if (Number.isFinite(idNum)) centerWezelId = idNum;
          }
          let wezelForCenter: any = null;
          if (centerWezelId != null) {
            wezelForCenter = wezels.find(w => Number(w.id) === Number(centerWezelId));
          }
          if (!wezelForCenter && wezels.length > 0) {
            wezelForCenter = wezels[0];
          }
          const wLat = Number((wezelForCenter as any)?.latitude);
          const wLon = Number((wezelForCenter as any)?.longitude);
          if (wezelForCenter && Number.isFinite(wLat) && Number.isFinite(wLon)) {
            console.log('[MapModal][click] rowId:', rowId, 'source: wezel', { wezelId: wezelForCenter.id, lat: wLat, lon: wLon });
          } else {
            console.log('[MapModal][click] rowId:', rowId, 'source: default', { lat: 52.2297, lon: 21.0122 });
          }
        }
      } catch {}
      setMapModal({ visible: true, rowId, value: current });
    } catch {
      setMapModal({ visible: true, rowId, value: '' });
    }
  };

  const closeMapModal = () => {
    setMapModal({ visible: false, rowId: null, value: '' });
  };
  const applyMapModal = () => {
    try {
      if (mapModal.rowId == null) { closeMapModal(); return; }
      // Apply only to the currently edited row
      if (editingId !== null && mapModal.rowId === editingId) {
        setEditData(prev => ({ ...prev, klient_budowa: mapModal.value }));
        // Persist manual coords to cache immediately (as manual), backend will also upsert on save
        if (pendingGeo && (selectedRegion || (editData.region_id || (zamowienia.find(z=>z.id===editingId)?.region_id)))) {
          (async () => {
            try {
              
              const regionId = Number(selectedRegion || editData.region_id || (zamowienia.find(z=>z.id===editingId)?.region_id));
              await authenticatedFetch('/api/geocode/manual-upsert', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  region_id: regionId,
                  klient_nazwa: String(editData.klient_nazwa || ''),
                  klient_budowa: String(mapModal.value || ''),
                  lat: pendingGeo.lat,
                  lon: pendingGeo.lon
                })
              });
              // notify map to refresh pins
              window.dispatchEvent(new CustomEvent('map:refresh', { detail: { date: selectedDate, region_id: regionId } }));
            } catch {}
          })();
        }
      }
    } finally {
      closeMapModal();
    }
  };
  // Geocode current text in modal and place a pin
  const geocodeAddressInModal = async () => {
    try {
      if (!mapModal.value) {
        try { console.warn('[MapModal][Wyszukaj] brak wartości w polu'); } catch {}
        return;
      }
      
      const regionId = selectedRegion ?? (mapModal.rowId != null ? zamowienia.find(z=>z.id===mapModal.rowId)?.region_id : null) ?? editData.region_id;
      const leadingWezel = editData.zamowienie_wezel_wiodacy ?? (mapModal.rowId != null ? zamowienia.find(z=>z.id===mapModal.rowId)?.zamowienie_wezel_wiodacy : null);
      const q = new URLSearchParams({
        region_id: String(regionId || ''),
        klient_nazwa: String(editData.klient_nazwa || (mapModal.rowId != null ? (zamowienia.find(z=>z.id===mapModal.rowId)?.klient_nazwa || '') : '')),
        klient_budowa: String(mapModal.value || ''),
        wezel_id: String(leadingWezel || ''),
        radius_km: '20'
      });
      try { console.log('[MapModal][Wyszukaj] request', { regionId, wezelId: leadingWezel, value: mapModal.value, params: Object.fromEntries(q.entries()) }); } catch {}
      const resp = await authenticatedFetch(`/api/geocode/resolve?${q.toString()}`, { credentials: 'include' });
      try { console.log('[MapModal][Wyszukaj] response', { ok: resp.ok, status: resp.status }); } catch {}
      if (resp.ok) {
        const j = await resp.json();
        try { console.log('[MapModal][Wyszukaj] payload', j); } catch {}
        if (j && j.success && typeof j.lat === 'number' && typeof j.lon === 'number') {
          setPickMarker({ lat: j.lat, lng: j.lon });
          setPendingGeo({ lat: j.lat, lon: j.lon });
          try {
            if (modalMapRef.current) {
              modalMapRef.current.setCenter({ lat: j.lat, lng: j.lon });
              modalMapRef.current.setZoom(14);
              console.log('[MapModal][Wyszukaj] centered map to', { lat: j.lat, lon: j.lon });
            }
          } catch {}
        } else {
          try { console.warn('[MapModal][Wyszukaj] brak wyniku lub poza limitem 50km'); } catch {}
        }
      }
    } catch {}
  };

  // --- Map modal (Google Maps) ---
  const readMapsKey = (): string => {
    try {
      const w: any = (typeof window !== 'undefined') ? (window as any) : undefined;
      const meta = (typeof document !== 'undefined') ? (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null) : null;
      const fromWindow = w?.GMAPS_API_KEY;
      const fromMeta = meta?.content;
      const fromStorage = (typeof window !== 'undefined') ? localStorage.getItem('GMAPS_API_KEY') : null;
      return String(fromWindow || fromMeta || fromStorage || '');
    } catch { return ''; }
  };
  const [gKey, setGKey] = useState<string>(() => readMapsKey());
  useAuthenticatedEffect(() => {
    const k = readMapsKey();
    if (k) { setGKey(k); return; }
    (async () => {
      try {
        
        const res = await authenticatedFetch('/api/geocode/key', { credentials: 'include' });
        if (res.ok) {
          const j = await res.json();
          if (j && typeof j.key === 'string') setGKey(j.key || '');
        }
      } catch {}
    })();
  }, []);
  // Ensure loader is called with a stable key across the app
  const stableKey = React.useMemo(() => {
    try {
      const w: any = window as any;
      if (!w.__GMAPS_LOADER_KEY__ && gKey) w.__GMAPS_LOADER_KEY__ = gKey;
      return w.__GMAPS_LOADER_KEY__ as string;
    } catch {
      return gKey || '';
    }
  }, [gKey]);
  // Freeze loader options to avoid React Google Maps loader mismatch (must not change across renders)
  const loaderOptionsRef = useRef<any | null>(null);
  if (!loaderOptionsRef.current) {
    loaderOptionsRef.current = {
      id: 'google-map-script',
      version: 'weekly',
      libraries: GMAPS_LIBRARIES as any,
      googleMapsApiKey: stableKey || ''
    };
  }
  const { isLoaded: mapLibLoaded, loadError: mapLibError } = useJsApiLoader(loaderOptionsRef.current);
  const readMapId = (): string => {
    try {
      const meta = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null)?.content || '';
      const fromWin = (window as any)?.GMAPS_MAP_ID || '';
      return String(meta || fromWin || '');
    } catch { return ''; }
  };
  const [modalMapId, setModalMapId] = useState<string>(() => readMapId());
  useAuthenticatedEffect(() => {
    const current = readMapId();
    console.log('[MapModal] Initial mapId from meta/window:', current || '(empty)');
    if (current) { setModalMapId(current); return; }
    (async () => {
      try {
        console.log('[MapModal] Fetching mapId from /api/geocode/map-id...');
        const res = await authenticatedFetch('/api/geocode/map-id', { credentials: 'include' });
        console.log('[MapModal] /api/geocode/map-id response status:', res.status);
        if (res.ok) {
          const j = await res.json();
          console.log('[MapModal] /api/geocode/map-id response body:', j);
          const id = j && typeof j.mapId === 'string' ? j.mapId : '';
          if (!id) {
            console.warn('[MapModal] mapId is empty in response');
            return;
          }
          console.log('[MapModal] Setting mapId:', id.slice(0, 10) + '...');
          try { (window as any).GMAPS_MAP_ID = id; } catch {}
          try {
            const meta = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null);
            if (!meta && typeof document !== 'undefined') {
              const newMeta = document.createElement('meta');
              newMeta.setAttribute('name', 'gmaps-map-id');
              newMeta.setAttribute('content', id);
              document.head.appendChild(newMeta);
            } else if (meta) {
              meta.setAttribute('content', id);
            }
          } catch {}
          setModalMapId(id);
        } else {
          console.error('[MapModal] Failed to fetch mapId, status:', res.status);
        }
      } catch (e) {
        console.error('[MapModal] Error fetching mapId:', e);
      }
    })();
  }, []);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 52.2297, lng: 21.0122 });
  const [pickMarker, setPickMarker] = useState<{ lat: number; lng: number } | null>(null);
  const [pendingGeo, setPendingGeo] = useState<{ lat: number; lon: number } | null>(null);
  const modalMapRef = useRef<any | null>(null);
  const [wezelMarker, setWezelMarker] = useState<{ lat: number; lng: number } | null>(null);
  const [budowaMarker, setBudowaMarker] = useState<{ lat: number; lng: number } | null>(null);
  const wezelAdvRef = useRef<any | null>(null);
  const budowaAdvRef = useRef<any | null>(null);
  const pickAdvRef = useRef<any | null>(null);

  const mountAdvancedMarker = useCallback((opts: { position: { lat: number; lng: number }; color: string; border: string; zIndex?: number }): any | null => {
    try {
      // Ensure Google Maps Advanced Marker API is available
      const g: any = (window as any).google;
      if (!g || !g.maps || !g.maps.marker || !g.maps.marker.AdvancedMarkerElement) return null;
      const pin = new g.maps.marker.PinElement({ background: opts.color, borderColor: opts.border, glyphColor: '#ffffff' });
      const marker = new g.maps.marker.AdvancedMarkerElement({
        map: modalMapRef.current,
        position: { lat: opts.position.lat, lng: opts.position.lng },
        content: pin.element,
        zIndex: Number.isFinite(opts.zIndex) ? opts.zIndex : undefined
      });
      return marker;
    } catch {
      return null;
    }
  }, []);
  useEffect(() => {
    if (mapModal.visible) {
      try {
        const row = zamowienia.find(z => z.id === (mapModal.rowId ?? editingId));
        const lat = Number(row?.budowa_lat);
        const lon = Number(row?.budowa_lon);
        // Set budowa (blue) marker from row if available
        if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0) {
          setBudowaMarker({ lat, lng: lon });
        } else {
          setBudowaMarker(null);
        }

        // Resolve red wezel marker from zamowienie_wezel_wiodacy
        let primaryWezelId: number | null = null;
        if (row && (row as any).zamowienie_wezel_wiodacy != null) {
          primaryWezelId = Number((row as any).zamowienie_wezel_wiodacy);
        } else if (editData && (editData as any).zamowienie_wezel_wiodacy != null) {
          primaryWezelId = Number((editData as any).zamowienie_wezel_wiodacy);
        }
        if (primaryWezelId != null) {
          const wm = wezels.find(w => Number(w.id) === Number(primaryWezelId));
          const wLat = Number((wm as any)?.latitude ?? (wm as any)?.lat);
          const wLon = Number((wm as any)?.longitude ?? (wm as any)?.lon);
          if (Number.isFinite(wLat) && Number.isFinite(wLon)) {
            setWezelMarker({ lat: wLat, lng: wLon });
          } else {
            setWezelMarker(null);
          }
        } else {
          setWezelMarker(null);
        }
        if (Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0) {
          try { console.log('[MapModal][center] source: budowa', { rowId: mapModal.rowId ?? editingId, lat, lon }); } catch {}
          setMapCenter({ lat, lng: lon });
          if (modalMapRef.current) {
            try { modalMapRef.current.setCenter({ lat, lng: lon }); modalMapRef.current.setZoom(14); } catch {}
          }
          // mount/update budowa advanced marker
          try {
            if (budowaAdvRef.current) { budowaAdvRef.current.position = { lat, lng: lon }; }
            else { budowaAdvRef.current = mountAdvancedMarker({ position: { lat, lng: lon }, color: '#1976D2', border: '#0D47A1', zIndex: 10 }); }
          } catch {}
          return;
        }
        // fallback: pick wezel depending on active tab
        let centerWezelId: number | null = null;
        if (activeTab && activeTab.startsWith('wezel-')) {
          const idStr = activeTab.slice('wezel-'.length);
          const idNum = parseInt(idStr, 10);
          if (Number.isFinite(idNum)) centerWezelId = idNum;
        }
        let wezelForCenter: any = null;
        if (centerWezelId != null) {
          wezelForCenter = wezels.find(w => Number(w.id) === Number(centerWezelId));
        }
        if (!wezelForCenter && wezels.length > 0) {
          wezelForCenter = wezels[0];
        }
        if (wezelForCenter && (wezelForCenter as any).latitude && (wezelForCenter as any).longitude) {
          const wLat = Number((wezelForCenter as any).latitude);
          const wLon = Number((wezelForCenter as any).longitude);
          try { console.log('[MapModal][center] source: wezel', { wezelId: (wezelForCenter as any).id, lat: wLat, lon: wLon }); } catch {}
          setMapCenter({ lat: wLat, lng: wLon });
          if (modalMapRef.current) {
            try { modalMapRef.current.setCenter({ lat: wLat, lng: wLon }); modalMapRef.current.setZoom(14); } catch {}
          }
          // mount/update wezel advanced marker
          try {
            if (wezelAdvRef.current) { wezelAdvRef.current.position = { lat: wLat, lng: wLon }; }
            else { wezelAdvRef.current = mountAdvancedMarker({ position: { lat: wLat, lng: wLon }, color: '#d32f2f', border: '#8b1d1d', zIndex: 20 }); }
          } catch {}
        } else {
          try { console.log('[MapModal][center] source: default', { lat: 52.2297, lon: 21.0122 }); } catch {}
          if (modalMapRef.current) {
            try { modalMapRef.current.setCenter({ lat: 52.2297, lng: 21.0122 }); modalMapRef.current.setZoom(12); } catch {}
          }
        }
      } catch {}
    } else {
      setPickMarker(null);
      setPendingGeo(null);
      setWezelMarker(null);
      setBudowaMarker(null);
      try {
        if (wezelAdvRef.current) { wezelAdvRef.current.map = null; wezelAdvRef.current = null; }
        if (budowaAdvRef.current) { budowaAdvRef.current.map = null; budowaAdvRef.current = null; }
        if (pickAdvRef.current) { pickAdvRef.current.map = null; pickAdvRef.current = null; }
      } catch {}
    }
  }, [mapModal.visible, wezels, activeTab, zamowienia, editingId]);

  // Auto reverse-geocode after user picks marker
  useEffect(() => {
    if (pickMarker) reverseGeocodeAndFill(pickMarker.lat, pickMarker.lng);
  }, [pickMarker]);

  const reverseGeocodeAndFill = async (lat: number, lng: number) => {
    try {
      
      const resp = await authenticatedFetch(`/api/geocode/reverse?lat=${encodeURIComponent(String(lat))}&lon=${encodeURIComponent(String(lng))}`, {
        credentials: 'include'
      });
      if (resp.ok) {
        const j = await resp.json();
        console.log('[ReverseGeocode] result', j);
        if (j && j.success && j.formatted_address) {
          setMapModal(prev => ({ ...prev, value: j.formatted_address }));
          setPendingGeo({ lat, lon: lng });
        }
      } else {
        // fallback: still set coordinates and show plain lat,lon text
        try { setMapModal(prev => ({ ...prev, value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` })); } catch {}
        try { setPendingGeo({ lat, lon: lng }); } catch {}
      }
    } catch (e) {
      console.warn('[ReverseGeocode] error', e);
      try { setMapModal(prev => ({ ...prev, value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` })); } catch {}
      try { setPendingGeo({ lat, lon: lng }); } catch {}
    }
  };

  const handlePumpSelect = (pump: any) => {
    setEditData(prev => ({
      ...prev,
      pompa_nazwa: pump?.nazwa ?? prev.pompa_nazwa,
      pompa_numer: pump?.nrpompy ?? ''
    }));
  };
  const handlePumpSelectionComplete = () => {
    // After choosing pump name, focus next column (Nr p.)
    setTimeout(() => {
      const nextInput = document.querySelector(`input[data-field="pompa_numer"][data-row-id="${editingId}"]`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }, 50);
  };
  const handleInputChange = (field: keyof Zamowienie, value: string) => {
    // Skip updating read-only fields
    if (field === 'zamowienie_zrealizowano') {
      return;
    }
    
    setEditData(prev => {
      const newData = { ...prev, [field]: value };
      
      // If zamowienie_godzinaod is being updated, automatically update zamowienie_godzinado
      if (field === 'zamowienie_godzinaod') {
        const newEndTime = calculateEndTime(value);
        newData.zamowienie_godzinado = newEndTime;
      }
      
      return newData;
    });
    
    // Note: Quantity changes are handled in onBlur with user confirmation
    // No automatic reset here to give user control over when to reset splits
  };
  const applyRowQtyToSplits = (row: Zamowienie) => {
    try {
      const rowId = Number(row.id);
      if (!isUsingQtyReducerForRow(rowId)) initQtyFromRow(row);
      // Use reducer-driven state; recomputation will occur via dispatch when inputs commit
    } catch (error) {
      console.error('🔍 Error in applyRowQtyToSplits:', error);
    }
  };

  // Disabled reactive auto-save: validation and save occur only on explicit save action
  // const isSavingRef = useRef(false);
  // useEffect(() => { /* no-op */ }, []);

  const handleAddNewRow = (wezelId: number, wezelNazwa: string) => {
    if (isAddingNew || editingId !== null) { return; }
    // Ensure any previous quantity drafts/reducer state are cleared before starting a fresh new row
    resetQtyUiState();

    // Get current time rounded to nearest 30 minutes (00 or 30)
    // Examples: 14:23 → 14:00, 14:37 → 14:30, 14:52 → 15:00
    const startTime = getCurrentHourRounded();
    // Calculate end time (start + 2 hours, also rounded to 30 minutes)
    // Examples: 14:00 → 16:00, 14:30 → 16:30, 15:00 → 17:00
    const endTime = calculateEndTime(startTime);

              const newZamowienie: Partial<Zamowienie> = {
            zamowienie_gotowka: false,
            zamowienie_uwagi1: '',
            zamowienie_numer: 0,
            zamowienie_godzinaod: startTime,
            zamowienie_godzinado: endTime,
            zamowienie_dzien: selectedDate,
            klient_nazwa: '',
            klient_nip: '',
            klient_budowa: '',
            klient_telefon: '',
            zamowienie_okolo: '',
            zamowienie_ilosc: 0,

            beton_klasa: '',
            beton_konsystencja: '',
            pompa_nazwa: '',
            pompa_numer: '',
            pompa_kierowca: '',
            zamowienie_transport: '',
            zamowienie_uwagi2: '',
            zamowienie_wezel_wiodacy: wezelId,
            wezel_nazwa: wezelNazwa,
            region_id: selectedRegion || undefined
          };

    const tempId = Date.now();
    setZamowienia(prev => [{ ...newZamowienie, id: tempId } as Zamowienie, ...prev]);
    setEditingId(tempId);
    setEditData(newZamowienie);
    setIsAddingNew(true);
  };

  const handleKeyPress = (e: React.KeyboardEvent, id: number) => {
    // Ensure we only handle the keydown event to avoid duplicate handling from keypress
    if (e.type !== 'keydown') {
      return;
    }

    if (e.key === 'Enter') {
      // Prevent default Enter behavior to avoid unintended submits
      e.preventDefault();
      
      const row = zamowienia.find(z => z.id === id);
      if (row) {
        // For new orders, don't show confirmation dialog, just check splits and save
        if (isAddingNew) {
          // Walidacja ilości tylko przy zapisie
          applyRowQtyToSplits(row);
          handleSaveNew(id);
          return;
        }
        
        // Dla istniejących zamówień: zapisz na Enter (walidacja wykona się w handleSave)
        applyRowQtyToSplits(row);
        handleSave(id);
      } else {
        // If no row found, save immediately
        if (isAddingNew) {
          handleSaveNew(id);
        } else {
          handleSave(id);
        }
      }
    } else if (e.key === 'Escape') {
      if (validationError) {
        setValidationError(null);
      } else {
        handleCancel();
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      moveToNextField(e.currentTarget as HTMLElement);
    }
  };

  const moveToNextField = (currentElement: HTMLElement) => {
    // Get all input fields in the current row
    const row = currentElement.closest('tr');
    if (!row) return;

    const inputs = Array.from(row.querySelectorAll('input, select, textarea')) as HTMLElement[];
    const currentIndex = inputs.indexOf(currentElement);

    // find next focusable element, skipping readOnly/disabled or explicitly marked to skip
    for (let i = currentIndex + 1; i < inputs.length; i++) {
      const el = inputs[i] as any;
      const isDisabled = Boolean(el?.disabled);
      const isReadOnly = Boolean(el?.readOnly);
      const shouldSkip = el?.getAttribute?.('data-skip-focus') === 'true';
      if (!isDisabled && !shouldSkip && (!isReadOnly || el?.tagName === 'SELECT')) {
        (el as HTMLElement).focus();
        return;
      }
    }
    // If nothing suitable, keep focus where it is
    currentElement.focus();
  };

  const loadWezels = async () => {
    if (!selectedRegion) return;
    
    try {
      
      const response = await authenticatedFetch(`/api/wezels?region_id=${selectedRegion}`, {
        headers: {
          
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const sorted = (data.wezels || []).slice().sort((a: any, b: any) => Number(a.id) - Number(b.id));

        // Przesunięcie zegarów liczymy RAZ, w chwili odebrania odpowiedzi. Trzymanie
        // surowego `serwer_teraz` miało dwie wady: wartość zmieniała się przy każdym
        // pobraniu (więc porównanie stanu ZAWSZE wykrywało zmianę i wymuszało zapis),
        // a po kilku minutach w pamięci przestawała być aktualna i przesuwała odliczanie.
        // Zaokrąglenie do sekund zjada drgania opóźnienia sieci, dzięki czemu przy
        // niezmienionym stanie odcisk danych jest identyczny i nikt nie jest budzony.
        const odebrano = Date.now();
        const mapaStanu: Record<number, StanIntegracji> = {};
        for (const w of sorted as any[]) {
          const serwerMs = w.serwer_teraz ? new Date(w.serwer_teraz).getTime() : 0;
          mapaStanu[Number(w.id)] = {
            przebiegStart: w.przebieg_start ?? null,
            przebiegKoniec: w.przebieg_koniec ?? null,
            wynik: w.przebieg_wynik ?? null,
            log: w.przebieg_log ?? null,
            nastepne: w.nastepne_uruchomienie ?? null,
            interwal: Number(w.uruchamiaj_co ?? 0) || 0,
            bledy: Number(w.kolejne_bledy ?? 0) || 0,
            przesuniecieMs: serwerMs ? Math.round((serwerMs - odebrano) / 1000) * 1000 : 0,
            ostatnieUdane: w.ostatnie_udane_logowanie ?? null,
          };
        }
        // Zapis idzie do magazynu poza Reactem: budzi wyłącznie zamontowane wskaźniki,
        // i tylko wtedy, gdy dane faktycznie się zmieniły. Ten komponent się nie przerysowuje.
        zapiszStanWezlow(mapaStanu);

        // Referencję listy podmieniamy tylko przy faktycznej zmianie składu. To zapytanie
        // powtarza się co kilkanaście sekund, a nowa tablica przebudowywałaby zakładki
        // i przeliczenia podziałów na węzły w trakcie edycji zamówienia.
        setWezels((prev) => (
          prev.length === sorted.length
          && prev.every((w, i) => Number(w.id) === Number(sorted[i].id) && w.nazwa === sorted[i].nazwa)
            ? prev
            : sorted
        ));
      } else {
        console.error('Wezels response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading wezels:', error);
    }
  };

  const handleStatusChange = async (zamowienieId: number, newStatus: string, statusUpdate?: any) => {
    try {
      
      // Capture region for local refresh event
      const rowBefore = zamowienia.find(z => z.id === zamowienieId);

      const response = await authenticatedFetch(`/api/zamowienia/${zamowienieId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ 
          status: newStatus,
          ...(statusUpdate && { status_update: statusUpdate })
        })
      });

      if (response.ok) {
        // Record exact local event signature to dedupe socket echo by fingerprint (no time window)
        try {
          const before = rowBefore || {};
          const sigPayload = {
            table: 'zamowienie',
            op: 'UPDATE',
            id: zamowienieId,
            row_qty: Number((before as any)?.zamowienie_ilosc ?? NaN),
            old_row_qty: Number((before as any)?.zamowienie_ilosc ?? NaN),
            row_wezel_id: NaN,
            row_split: NaN,
            old_row_split: NaN,
            row_anul: Boolean((statusUpdate as any)?.zamowienie_anulowane),
            old_row_anul: Boolean((before as any)?.zamowienie_anulowane)
          };
          lastLocalEventSigRef.current[zamowienieId] = JSON.stringify(sigPayload);
        } catch {}
        setZamowienia(prev => prev.map(z => z.id === zamowienieId ? { 
          ...z, 
          // Nie nadpisuj lokalnie pola status; opieraj UI na polach boolean
          ...(statusUpdate && statusUpdate)
        } : z));
        // DB-driven: if anulowane toggled and matches current filters, refresh Region+Wezel
        try {
          const before = rowBefore || null;
          const after = statusUpdate ? { ...before, ...statusUpdate } : before;
          const sameDate = before && String((before as any).zamowienie_dzien).slice(0,10) === selectedDateRef.current;
          const sameRegion = before && Number((before as any).region_id) === Number(selectedRegionRef.current);
          if (sameDate && sameRegion && before) {
            const wasAnul = Boolean((before as any).zamowienie_anulowane);
            const nowAnul = Boolean((after as any).zamowienie_anulowane);
            if (wasAnul !== nowAnul) { debouncedFetchRegion(); debouncedFetchWezel(); }
          }
        } catch {}
        // Local incremental update for PompyWidget without full reload
        try {
          const patch: any = { id: zamowienieId, ...(statusUpdate || {}) };
          // Provide pump fields if known to help PompyWidget place/update the row
          if (rowBefore) {
            patch.pompa_nazwa = (rowBefore as any).pompa_nazwa;
            patch.pompa_numer = (rowBefore as any).pompa_numer;
            patch.zamowienie_godzinaod = (rowBefore as any).zamowienie_godzinaod;
            patch.zamowienie_godzinado = (rowBefore as any).zamowienie_godzinado;
            patch.klient_nazwa = (rowBefore as any).klient_nazwa;
            patch.klient_budowa = (rowBefore as any).klient_budowa;
          }
          window.dispatchEvent(new CustomEvent('pumpSchedule:orderUpdate', { detail: { row: patch } }));
        } catch {}
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };
  const handleWezelChange = async (zamowienieId: number, wezelId: number, wezelNazwa: string) => {
    try {
      
      const response = await authenticatedFetch(`/api/zamowienia/${zamowienieId}/wezel`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ wezel_id: wezelId })
      });

      if (response.ok) {
        setZamowienia(prev => prev.map(z => z.id === zamowienieId ? { ...z, wezel_id: wezelId, wezel_nazwa: wezelNazwa } : z));
      }
    } catch (error) {
      console.error('Error updating wezel:', error);
    }
  };
  const handleDeleteCurrent = async () => {
    if (editingId === null && selectedRowId === null) return;
    const idToDelete = editingId !== null ? editingId : (selectedRowId as number);

    // If deleting a new, unsaved row that is currently being added
    if (isAddingNew && editingId === idToDelete) {
      setZamowienia(prev => prev.filter(z => z.id !== idToDelete));
      setEditingId(null);
      setEditData({});
      setIsAddingNew(false);
      if (selectedRowId === idToDelete) setSelectedRowId(null);
      return;
    }

    const confirmDelete = window.confirm('Czy na pewno chcesz usunąć to zamówienie?');
    if (!confirmDelete) return;

    try {
      // Capture old row and splits for delta before deletion
      const oldRow = zamowienia.find(z => z.id === idToDelete) || null;
      const oldSplitsMap: Record<number, number> = { ...(splitsByOrder[idToDelete] || {}) };

      const response = await authenticatedFetch(`/api/zamowienia/${idToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        try {
          lastLocalSaveByOrderRef.current[idToDelete] = Date.now();
          suppressAggregatesUntilRef.current = Date.now() + 1200;
        } catch {}
        setZamowienia(prev => prev.filter(z => z.id !== idToDelete));
        if (editingId === idToDelete) {
          setEditingId(null);
          setEditData({});
          setIsAddingNew(false);
        }
        if (selectedRowId === idToDelete) {
          setSelectedRowId(null);
        }
        // DB-driven refresh for delete - only if deleted order matches current date/region
        if (oldRow) {
          const sameDate = String(oldRow.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
          const sameRegion = Number(oldRow.region_id) === Number(selectedRegionRef.current);
          if (sameDate && sameRegion) {
            debouncedFetchRegion();
            debouncedFetchWezel();
          }
        }
      } else {
        const errorText = await response.text();
        console.error('Error deleting zamowienie:', errorText);
        alert('Nie udało się usunąć zamówienia.');
      }
    } catch (error) {
      console.error('Error deleting zamowienie:', error);
      alert('Wystąpił błąd podczas usuwania.');
    }
  };

  // Context menu functions
  const handleContextMenu = (e: React.MouseEvent, zamowienieId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedRowId(zamowienieId);
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      zamowienieId
    });
  };
  // Context menu for empty table area (no specific zamowienie)
  const handleContextMenuEmpty = (e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedRowId(null);
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      zamowienieId: null
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      zamowienieId: null
    });
  };

  const closeContextMenuWithDelay = () => {
    setTimeout(() => {
      closeContextMenu();
    }, 100);
  };
  const handleContextMenuStatusChange = async (status: string) => {
    if (!contextMenu.zamowienieId) {
      return;
    }
    
    const currentZamowienie = zamowienia.find(z => z.id === contextMenu.zamowienieId);
    if (!currentZamowienie) {
      return;
    }
    

    let statusUpdate: any = {};
    
    if (status === 'Potwierdzone') {
      statusUpdate = {
        zamowienie_potwierdzone: !currentZamowienie.zamowienie_potwierdzone,
        zamowienie_realizacja: currentZamowienie.zamowienie_realizacja,
        zamowienie_zakonczone: currentZamowienie.zamowienie_zakonczone,
        zamowienie_anulowane: currentZamowienie.zamowienie_anulowane
      };
    } else if (status === 'Realizacja') {
      statusUpdate = {
        zamowienie_potwierdzone: currentZamowienie.zamowienie_potwierdzone,
        zamowienie_realizacja: !currentZamowienie.zamowienie_realizacja,
        zamowienie_zakonczone: currentZamowienie.zamowienie_zakonczone,
        zamowienie_anulowane: currentZamowienie.zamowienie_anulowane
      };
    } else if (status === 'Zakonczone') {
      statusUpdate = {
        zamowienie_potwierdzone: currentZamowienie.zamowienie_potwierdzone,
        zamowienie_realizacja: currentZamowienie.zamowienie_realizacja,
        zamowienie_zakonczone: !currentZamowienie.zamowienie_zakonczone,
        zamowienie_anulowane: currentZamowienie.zamowienie_anulowane
      };
    } else if (status === 'Anulowane') {
      statusUpdate = {
        zamowienie_potwierdzone: currentZamowienie.zamowienie_potwierdzone,
        zamowienie_realizacja: currentZamowienie.zamowienie_realizacja,
        zamowienie_zakonczone: currentZamowienie.zamowienie_zakonczone,
        zamowienie_anulowane: !currentZamowienie.zamowienie_anulowane
      };
    }

    if (Object.keys(statusUpdate).length > 0) {
      await handleStatusChange(contextMenu.zamowienieId, status, statusUpdate);
    }
    
    closeContextMenu();
  };

  const handleContextMenuDelete = async () => {
    if (!contextMenu.zamowienieId) return;
    
    const confirmDelete = window.confirm('Czy na pewno chcesz usunąć to zamówienie?');
    if (!confirmDelete) return;

    try {
      
      const response = await authenticatedFetch(`/api/zamowienia/${contextMenu.zamowienieId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        try {
          lastLocalSaveByOrderRef.current[Number(contextMenu.zamowienieId)] = Date.now();
          suppressAggregatesUntilRef.current = Date.now() + 1200;
        } catch {}
        setZamowienia(prev => prev.filter(z => z.id !== contextMenu.zamowienieId));
        if (selectedRowId === contextMenu.zamowienieId) {
          setSelectedRowId(null);
        }
      } else {
        const errorText = await response.text();
        console.error('Error deleting zamowienie:', errorText);
        alert('Nie udało się usunąć zamówienia.');
      }
    } catch (error) {
      console.error('Error deleting zamowienie:', error);
      alert('Wystąpił błąd podczas usuwania.');
    }
    
    closeContextMenu();
  };

  const handleContextMenuAddNewForWezel = (wezelId: number) => {
    try {
      const w = wezels.find(x => x.id === wezelId);
      if (!w) return;
      handleAddNewRow(w.id, w.nazwa);
      closeContextMenu();
    } catch {}
  };

  const handleContextMenuEdit = () => {
    try {
      if (contextMenu.zamowienieId == null) return;
      const row = zamowienia.find(z => z.id === contextMenu.zamowienieId);
      if (!row) return;
      handleEdit(row);
      closeContextMenu();
    } catch {}
  };

  const handleContextMenuHistory = async () => {
    try {
      if (contextMenu.zamowienieId == null) return;
      const id = contextMenu.zamowienieId;
      const resp = await authenticatedFetch(`/api/zamowienia/${id}/history`, { credentials: 'include' });
      if (!resp.ok) {
        const t = await resp.text();
        alert(`Nie udało się pobrać historii: ${t}`);
        return;
      }
      const j = await resp.json();
      const w = window.open('', '_blank');
      if (w) {
        const rows = (j.history || []);
        const formatBool = (v: any) => v === true ? '✓' : (v === false ? '✗' : '—');
        // Pola historii to treść wpisana przez użytkowników (klient, budowa, uwagi). Okno
        // about:blank dziedziczy origin aplikacji, więc nieescapowany HTML w którymkolwiek polu
        // wykonałby się z dostępem do sesji każdego, kto otworzy historię tego zamówienia.
        const esc = (v: any) => String(v ?? '')
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        const htmlRows = rows.map((r: any) => `<tr>
          <td>${esc(r.changed_at)}</td>
          <td>${esc(r.action)}</td>
          <td>${esc(r.zamowienie_ilosc)}</td>
          <td>${esc(r.beton_klasa)}</td>
          <td>${esc(r.klient_nazwa)}</td>
          <td>${esc(r.klient_budowa)}</td>
          <td>${esc(r.pompa_nazwa)}</td>
          <td style="text-align:center">${formatBool(r.zamowienie_potwierdzone)}</td>
          <td style="text-align:center">${formatBool(r.zamowienie_realizacja)}</td>
          <td style="text-align:center">${formatBool(r.zamowienie_zakonczone)}</td>
          <td style="text-align:center">${formatBool(r.zamowienie_anulowane)}</td>
          <td>${esc(r.changed_by)}</td>
        </tr>`).join('');
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Historia zamówienia ${esc(id)}</title><style>body{font-family:system-ui,Segoe UI,Arial,sans-serif;padding:12px} table{border-collapse:collapse;width:100%} th,td{border:1px solid #e0e0e0;padding:6px 8px;text-align:left;font-size:12px} th{background:#f5f5f5} .status-col{text-align:center;min-width:40px}</style></head><body><h2>Historia zamówienia ${esc(id)}</h2><table><thead><tr><th>Czas</th><th>Akcja</th><th>Ilość</th><th>Recepta</th><th>Klient</th><th>Budowa</th><th>Pompa</th><th class="status-col">Potw.</th><th class="status-col">Real.</th><th class="status-col">Zak.</th><th class="status-col">Anul.</th><th>Użytkownik</th></tr></thead><tbody>${htmlRows}</tbody></table></body></html>`);
        w.document.close();
      }
      closeContextMenu();
    } catch (e) {
      alert('Błąd podczas otwierania historii');
    }
  };

  const handleContextMenuConfirmRecipe = async () => {
    try {
      if (contextMenu.zamowienieId == null) return;
      const id = contextMenu.zamowienieId;
      const resp = await authenticatedFetch(`/api/zamowienia/${id}/recepta/confirm`, {
        method: 'POST',
        credentials: 'include'
      });
      const txt = await resp.text();
      try {
        const j = JSON.parse(txt);
        if (!resp.ok || j.success === false) {
          alert(j.message || 'Operacja nie powiodła się');
        } else {
          alert(j.message || 'Zatwierdzono receptę');
        }
      } catch {
        if (!resp.ok) alert(txt || 'Operacja nie powiodła się');
        else alert('Zatwierdzono receptę');
      }
      closeContextMenu();
    } catch (e) {
      alert('Błąd podczas zatwierdzania recepty');
    }
  };

  const handleContextMenuClientToggle = async () => {
    try {
      if (!contextMenu.zamowienieId) return;
      const row = zamowienia.find(z => z.id === contextMenu.zamowienieId);
      if (!row) return;
      const nip = (row.klient_nip || '').trim();
      if (!nip) return;
      const isBlocked = Boolean((row as any).klient_blokada);
      const endpoint = isBlocked ? '/api/klient/consent' : '/api/klient/block';
      const resp = await authenticatedFetch(endpoint, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nip })
      });
      if (resp.ok) {
        // Socket will handle the update automatically via db:change event
        // No need to reload - socket will update all orders with matching NIP
        closeContextMenu();
      } else {
        const t = await resp.text();
        alert(`Nie udało się zaktualizować statusu klienta: ${t}`);
      }
    } catch (e) {
      alert('Błąd podczas aktualizacji statusu klienta');
    }
  };
  const handleContextMenuCopy = () => {
    if (!contextMenu.zamowienieId) {
      console.log('No zamowienieId in context menu');
      return;
    }
    
    const zamowienie = zamowienia.find(z => z.id === contextMenu.zamowienieId);
    if (!zamowienie) {
      console.log('Zamowienie not found for id:', contextMenu.zamowienieId);
      return;
    }

    console.log('Copying zamowienie:', zamowienie);

    // Copy zamowienie data without id and auto-generated fields
    const copyData: Partial<Zamowienie> = {
      zamowienie_gotowka: zamowienie.zamowienie_gotowka,
      zamowienie_uwagi1: zamowienie.zamowienie_uwagi1,
      // zamowienie_numer - removed, generated by database trigger
      zamowienie_godzinaod: zamowienie.zamowienie_godzinaod,
      zamowienie_godzinado: zamowienie.zamowienie_godzinado,
      zamowienie_dzien: zamowienie.zamowienie_dzien,
      klient_nazwa: zamowienie.klient_nazwa,
      klient_nip: zamowienie.klient_nip,
      klient_budowa: zamowienie.klient_budowa,
      klient_telefon: zamowienie.klient_telefon,
      zamowienie_okolo: zamowienie.zamowienie_okolo,
      zamowienie_ilosc: zamowienie.zamowienie_ilosc,
      beton_klasa: zamowienie.beton_klasa,
      beton_konsystencja: zamowienie.beton_konsystencja,
      pompa_nazwa: zamowienie.pompa_nazwa,
      pompa_numer: zamowienie.pompa_numer,
      pompa_kierowca: zamowienie.pompa_kierowca,
      zamowienie_transport: zamowienie.zamowienie_transport,
      zamowienie_uwagi2: zamowienie.zamowienie_uwagi2,
      zamowienie_wezel_wiodacy: zamowienie.zamowienie_wezel_wiodacy,
      region_id: zamowienie.region_id
    };

    setClipboardData(copyData);
    closeContextMenuWithDelay();
  };

  const handleContextMenuPaste = () => {
    if (!clipboardData) {
      alert('Brak danych do wklejenia. Skopiuj najpierw zamówienie.');
      closeContextMenu();
      return;
    }

    // Set default date if not present
    // If we are on a specific wezel tab, force leading wezel to this tab's wezel
    const currentTab = tabs.find(t => t.id === activeTab);
    const forcedWezelId = currentTab && (currentTab as any).wezelId ? Number((currentTab as any).wezelId) : null;
    const dataWithDefaultDate = {
      ...clipboardData,
      // Use app-selected date as default instead of system date
      zamowienie_dzien: clipboardData.zamowienie_dzien || selectedDate
    };

    if (forcedWezelId) {
      (dataWithDefaultDate as any).zamowienie_wezel_wiodacy = forcedWezelId;
    }

    setPasteModal({
      visible: true,
      data: dataWithDefaultDate
    });
    closeContextMenu();
  };

  const calculateWeekFromDate = (dateString: string): number => {
    const date = new Date(dateString);
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  };
  const handlePasteConfirm = async (pasteData: Partial<Zamowienie>) => {
    try {
      
      // Remove status fields from pasteData to avoid validation errors
      const { 
        zamowienie_potwierdzone, 
        zamowienie_realizacja, 
        zamowienie_zakonczone, 
        zamowienie_anulowane,
        ...cleanPasteData 
      } = pasteData;

      const currentTab = tabs.find(t => t.id === activeTab);
      const forcedWezelId = currentTab && (currentTab as any).wezelId ? Number((currentTab as any).wezelId) : null;

      const newZamowienie = {
        ...cleanPasteData,
        // Ensure pasted order defaults to the currently selected date in the app
        zamowienie_dzien: cleanPasteData.zamowienie_dzien || selectedDate,
        ...(forcedWezelId ? { zamowienie_wezel_wiodacy: forcedWezelId } : null),
        // zamowienie_tydzien - removed, calculated by database trigger
        // Status fields are not included in POST request - they will be set to false by default
      };


      const response = await authenticatedFetch('/api/zamowienia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify(newZamowienie)
      });

      if (response.ok) {
        const data = await response.json();
        
        // Add zamowienia_wezel entries for all wezels in the region
        if (data.zamowienie && data.zamowienie.id && selectedRegion) {
          try {
            // Get all wezels in the region
            const wezelsInRegion = wezels.filter(w => w.region_id === selectedRegion);
            
            // Create zamowienia_wezel entries
            for (const wezel of wezelsInRegion) {
              const wezelData = {
                zamowienie_id: data.zamowienie.id,
                wezel_id: wezel.id,
                zamowione: wezel.id === newZamowienie.zamowienie_wezel_wiodacy ? newZamowienie.zamowienie_ilosc : 0
              };
              
              
              const wezelResponse = await authenticatedFetch('/api/zamowienia-wezel', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  
                },
                body: JSON.stringify(wezelData)
              });
              
              if (!wezelResponse.ok) {
                console.error('Error creating zamowienia_wezel entry for wezel:', wezel.id);
              }
            }
          } catch (wezelError) {
            console.error('Error creating zamowienia_wezel entries:', wezelError);
          }
        }
        
        // OPTIMIZATION: Don't add to local state - let Socket.IO handle it
        // This prevents duplicate rows when Socket.IO notification arrives
        console.log('[Optimization] Skipped local state update - waiting for Socket.IO notification for order:', data.zamowienie.id);
        setPasteModal({ visible: false, data: null });
      } else {
        const errorText = await response.text();
        console.error('Error creating zamowienie:', errorText);
        alert('Nie udało się utworzyć zamówienia.');
      }
    } catch (error) {
      console.error('Error creating zamowienie:', error);
      alert('Wystąpił błąd podczas tworzenia zamówienia.');
    }
  };

  useAuthenticatedEffect(() => {
    if (selectedRegion) {
      loadWezels();
    }
  }, [selectedRegion]);

  // Cykliczne odświeżanie stanu węzłów. Bez tego wskaźnik zamarłby na chwili wejścia na
  // stronę: odliczanie dojechałoby do zera i stanęło, a kolor został z dawno minionego
  // przebiegu. Zapytanie jest tanie (kilka wierszy), więc 15 s jest bezpieczne.
  useEffect(() => {
    if (!selectedRegion) return;
    const t = setInterval(() => { loadWezels(); }, 15000);
    return () => clearInterval(t);
  }, [selectedRegion]);

  // JEDYNE miejsce, z ktorego startuje pobranie listy. Wczesniej byly trzy takie efekty i
  // wchodzily sobie w droge:
  //   - ten (region + dzien) startowal pobranie A,
  //   - zaraz po nim generateTabs() ustawialo `tabs`, co odpalalo efekt po `tabs` i pobranie B,
  //     ktore abortowalo A. AbortController przerywa jednak tylko strone przegladarki - Express
  //     i node-postgres nie anuluja rozpoczetego zapytania, wiec baza liczyla wszystko dwa razy,
  //   - trzeci efekt pobieral liste na kazda zmiane zakladki, mimo ze zapytanie jej nie uzywa.
  // Bariera isRegionChangingRef i straz tabsReadyRef byly tylko lataniem tego wyscigu; skoro
  // zrodlo pobrania jest jedno, obie sa niepotrzebne.
  useAuthenticatedEffect(() => {
    if (selectedRegion) {
      loadZamowienia();
    }
  }, [selectedRegion, selectedDate]);

  // Always exit edit mode and cleanup async work when date changes
  useEffect(() => {
    // Clear pending timeouts
    if (debounceTimeoutRef.current) { try { clearTimeout(debounceTimeoutRef.current); } catch {} }
    if (splitsDebounceRef.current) { try { clearTimeout(splitsDebounceRef.current); } catch {} }
    if (regionDebounceRef.current) { try { clearTimeout(regionDebounceRef.current); } catch {} }
    if (wezelDebounceRef.current) { try { clearTimeout(wezelDebounceRef.current); } catch {} }
    // Clear editing state and quantity UI state
    setEditingId(null);
    setEditData({});
    setIsAddingNew(false);
    resetQtyUiState();
    setSelectedRowId(null);
    // Dismiss any pending row-change confirmation
    setConfirmModal({ visible: false, targetRowId: null });
    setPendingRowChange(null);
    // Close any open context menu
    setContextMenu({ visible: false, x: 0, y: 0, zamowienieId: null });
  }, [selectedDate]);

  useEffect(() => {
    if (selectedRegion) {
      generateTabs();
    }
  }, [selectedRegion, wezels, regionName]);
  // Splits are now loaded in bulk during loadZamowienia()
  // This useEffect is no longer needed

  
  // Focus on klient_nazwa input when a new row is added
  useEffect(() => {
    if (isAddingNew && editingId !== null) {
      const timer = setTimeout(() => {
        const klientInput = document.querySelector(`input[data-field="klient_nazwa"][data-row-id="${editingId}"]`) as HTMLInputElement;
        if (klientInput) {
          klientInput.focus();
        }
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [isAddingNew, editingId]);

  // Cleanup effect to cancel pending requests and timeouts
  useEffect(() => {
    return () => {
      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Clear any pending timeouts
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (splitsDebounceRef.current) { clearTimeout(splitsDebounceRef.current); }
      if (regionDebounceRef.current) { try { clearTimeout(regionDebounceRef.current); } catch {} }
      if (wezelDebounceRef.current) { try { clearTimeout(wezelDebounceRef.current); } catch {} }
    };
  }, []);
  // Close export menu on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
      try {
        const footer = exportRef.current?.parentElement;
        if (footer && !footer.contains(e.target as Node)) {
          setFilterOpen(false);
        }
      } catch {}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  // Close context menu on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      // Don't close if clicking inside the context menu
      const target = e.target as Element;
      if (target.closest('[data-context-menu]')) {
        return;
      }
      
      if (contextMenu.visible) {
        closeContextMenu();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [contextMenu.visible]);

  // Global keyboard shortcuts: Ctrl+C (copy selected row), Ctrl+V (paste)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore when focused inside an input/textarea/select or contenteditable
      const target = e.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select' || (target?.isContentEditable ?? false);
      if (isEditable) return;

      const isMac = navigator.platform.includes('Mac');
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      if (!ctrl) return;

      if (e.key.toLowerCase() === 'c') {
        // Copy: requires a selected row
        if (selectedRowId != null) {
          e.preventDefault();
          const z = zamowienia.find(x => x.id === selectedRowId);
          if (z) {
            const copyData: Partial<Zamowienie> = {
              zamowienie_gotowka: z.zamowienie_gotowka,
              zamowienie_uwagi1: z.zamowienie_uwagi1,
              zamowienie_godzinaod: z.zamowienie_godzinaod,
              zamowienie_godzinado: z.zamowienie_godzinado,
              zamowienie_dzien: z.zamowienie_dzien,
              klient_nazwa: z.klient_nazwa,
              klient_nip: z.klient_nip,
              klient_budowa: z.klient_budowa,
              klient_telefon: z.klient_telefon,
              zamowienie_okolo: z.zamowienie_okolo,
              zamowienie_ilosc: z.zamowienie_ilosc,
              beton_klasa: z.beton_klasa,
              beton_konsystencja: z.beton_konsystencja,
              pompa_nazwa: z.pompa_nazwa,
              pompa_numer: z.pompa_numer,
              pompa_kierowca: z.pompa_kierowca,
              zamowienie_transport: z.zamowienie_transport,
              zamowienie_uwagi2: z.zamowienie_uwagi2,
              zamowienie_wezel_wiodacy: z.zamowienie_wezel_wiodacy,
              region_id: z.region_id
            };
            setClipboardData(copyData);
          }
        }
      }

      if (e.key.toLowerCase() === 'v') {
        // Paste: only if we have clipboardData in app state
        if (clipboardData) {
          e.preventDefault();
          handleContextMenuPaste();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [selectedRowId, zamowienia, clipboardData]);

  const buildExportData = () => {
    const fields: Array<{ key: keyof Zamowienie; label: string }> = [
      { key: 'zamowienie_gotowka', label: 'Gotówka' },
      { key: 'zamowienie_uwagi1', label: 'Uwagi 1' },
      { key: 'zamowienie_numer', label: 'Numer' },
      { key: 'zamowienie_godzinaod', label: 'Godz Od' },
      { key: 'zamowienie_godzinado', label: 'Do' },
      { key: 'klient_nazwa', label: 'Klient' },
      { key: 'klient_nip', label: 'NIP' },
      { key: 'klient_budowa', label: 'Budowa' },
      { key: 'klient_telefon', label: 'Telefon' },
      { key: 'zamowienie_okolo', label: 'Ok' },
      { key: 'zamowienie_ilosc', label: 'Ilość' },
      // zamowienie_zrealizowano intentionally excluded
      { key: 'beton_klasa', label: 'Recepta' },
      { key: 'beton_konsystencja', label: 'Konsyst.' },
      { key: 'pompa_nazwa', label: 'Pompa' },
      { key: 'pompa_numer', label: 'Nr p.' },
      { key: 'pompa_kierowca', label: 'Operator' },
      { key: 'zamowienie_transport', label: 'Transport' },
      { key: 'zamowienie_uwagi2', label: 'Uwagi 2' }
    ];
    const rows: string[][] = zamowienia.map(z => fields.map(f => {
      const v: any = (z as any)[f.key];
      if (f.key === 'zamowienie_gotowka') return z.zamowienie_gotowka ? 'TAK' : 'NIE';
      return v === undefined || v === null ? '' : String(v);
    }));
    return { fields, rows };
  };
  const makeFilename = (ext: string) => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const currentTab = tabs.find(tab => tab.id === activeTab);
    let wezelName = 'Region';
    if (currentTab && !currentTab.isRegion && currentTab.wezelId) {
      const w = wezels.find(x => x.id === currentTab.wezelId);
      if (w) wezelName = w.nazwa;
    }
    // Replace non-alphanumeric (ASCII) characters with underscore for filename safety
    const safeWezel = wezelName.replace(/[^a-zA-Z0-9_-]+/g, '_');
    return `${selectedDate}_${safeWezel}_${hh}${mm}.${ext}`;
  };

  const downloadFile = (content: string, mime: string, filename: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const handleExport = async (fmt: 'csv' | 'tsv' | 'json' | 'pdf') => {
    const { fields, rows } = buildExportData();
    if (fmt === 'json') {
      const objs = rows.map(r => {
        const obj: Record<string, string> = {};
        r.forEach((val, idx) => { obj[fields[idx].label] = val; });
        return obj;
      });
      downloadFile(JSON.stringify(objs, null, 2), 'application/json;charset=utf-8', makeFilename('json'));
    } else if (fmt === 'pdf') {
      try {
        const [{ default: pdfMake }] = await Promise.all([
          import('pdfmake/build/pdfmake'),
          import('pdfmake/build/vfs_fonts')
        ]).then((mods: any[]) => {
          const pdfmakeMod = (mods[0] as any).default || mods[0];
          const vfsExport = (mods[1] as any).default || mods[1];
          // In our build, vfs_fonts exports the vfs object directly (CommonJS: module.exports = vfs),
          // but in some builds it attaches to pdfMake. Support both shapes.
          (pdfmakeMod as any).vfs = (vfsExport as any).pdfMake?.vfs || vfsExport;
          return [{ default: pdfmakeMod }];
        });

        const tableBody: any[] = [fields.map(f => ({ text: f.label, bold: true }))];
        rows.forEach(r => tableBody.push(r.map(cell => ({ text: cell }))));

        const docDef: any = {
          pageSize: 'A4',
          pageOrientation: 'landscape',
          defaultStyle: { fontSize: 9 },
          content: [
            { text: `Zamówienia ${selectedDate}`, style: 'header', margin: [0, 0, 0, 8] },
            {
              table: {
                headerRows: 1,
                body: tableBody
              },
              layout: 'lightHorizontalLines'
            }
          ],
          styles: {
            header: { fontSize: 12, bold: true }
          }
        };
        const filename = makeFilename('pdf');
        (pdfMake as any).createPdf(docDef).download(filename);
      } catch (e) {
        console.error('PDF export failed', e);
        alert('Nie udało się wyeksportować do PDF.');
      }
    } else {
      const sep = fmt === 'csv' ? ',' : '\t';
      const esc = (s: string) => '"' + s.replace(/"/g, '""') + '"';
      const header = fields.map(f => esc(f.label)).join(sep);
      const lines = rows.map(r => r.map(v => esc(v)).join(sep));
      const content = [header, ...lines].join('\r\n');
      const mime = fmt === 'csv' ? 'text/csv;charset=utf-8' : 'text/tab-separated-values;charset=utf-8';
      downloadFile(content, mime, makeFilename(fmt));
    }
    setExportOpen(false);
  };
  // Realtime highlight on db changes using socketService
  useEffect(() => {
    // Initialize socket service and register load callback
    const initPromise = socketService.init();
    Promise.resolve(initPromise).catch(err => {
      console.error('[CardZamowieniaWidget] Failed to initialize socket:', err);
    });
    socketService.setLoadDataCallback(loadZamowienia);
    
    // Process a single notification (specific to CardZamowieniaWidget)
    const processNotification = async (evt: any) => {
      if (!evt) return;
      // Normalize table name: strip schema prefix, keep partition suffix
      const tableRaw = String(evt.table || '');
      const tableRawLc = tableRaw.toLowerCase().replace(/\"/g, '');
      const lastDot = tableRawLc.lastIndexOf('.');
      const lastColon = tableRawLc.lastIndexOf(':');
      const splitIdx = Math.max(lastDot, lastColon);
      const tableNorm = splitIdx >= 0 ? tableRawLc.slice(splitIdx + 1) : tableRawLc;
      // Heuristic classification for split events: table name OR row shape
      const rowHasSplitShape = !!(evt && evt.row && (
        (typeof evt.row.wezel_id !== 'undefined') &&
        (typeof (evt.row.zamowione) !== 'undefined' || typeof (evt.row.zrealizowane) !== 'undefined') &&
        (typeof (evt.row.zamowienie_id) !== 'undefined')
      ));
      const isSplitEvent = tableNorm.startsWith('zamowienie_wezel') || rowHasSplitShape;
      try {
        console.log('[Socket][Classify]', { table: tableRaw, norm: tableNorm, rowKeys: evt.row ? Object.keys(evt.row) : [], isSplitEvent, rowHasSplitShape });
      } catch {}
      // Suppress echoes from our own recent saves (only for zamowienie events; NEVER for splits)
      try {
        const now = Date.now();
        if (!isSplitEvent) {
          // If a global suppress window is active, skip processing (applies to zamowienie only)
          if (now < suppressAggregatesUntilRef.current) {
            console.log('[Socket][Filter] Skipping due to suppress window (zamowienie only). now:', now, 'until:', suppressAggregatesUntilRef.current, 'table:', tableRaw, 'norm:', tableNorm, 'op:', evt.operation);
            return;
          }
          const affectedOrderId = Number(
            (evt && (evt.id as any)) ??
            (evt && evt.row && ((evt.row as any).id ?? (evt.row as any).zamowienie_id)) ??
            (evt && evt.old_row && ((evt.old_row as any).id ?? (evt.old_row as any).zamowienie_id))
          );
          if (Number.isFinite(affectedOrderId)) {
            const lastLocal = lastLocalSaveByOrderRef.current[affectedOrderId] || 0;
            if (lastLocal && (now - lastLocal) < 1500) {
              console.log('[Socket][Filter] Suppressing local echo (zamowienie only):', { affectedOrderId, deltaMs: now - lastLocal, table: tableRaw, norm: tableNorm, op: evt.operation });
              return;
            }
          }
        } else {
          console.log('[Socket][Filter] Bypass suppress/echo gating for zamowienie_wezel event', { table: tableRaw, norm: tableNorm });
        }
      } catch {}
      // Instrumentation: confirm local processing of socket messages
      try {
        socketEventCountRef.current += 1;
        socketLastEventAtRef.current = Date.now();
        (window as any).__socket_last_seen__ = socketLastEventAtRef.current;
        (window as any).__socket_seen_count__ = socketEventCountRef.current;
        console.log('[Socket][Local] onDbChange seen', {
          count: socketEventCountRef.current,
          lastAt: new Date(socketLastEventAtRef.current).toISOString(),
          table: tableRaw,
          norm: tableNorm,
          operation: evt.operation,
          id: evt.id
        });
      } catch {}
      
      // OPTIMIZATION: Ignoruj partycjonowane tabele dla zamowienie*, ALE nie dla zamowienie_wezel*
      // - Dla splitów (zamowienie_wezel*_p...) musimy przetwarzać eventy, bo tylko one aktualizują Węzeł
      if (tableRaw && tableRaw.includes('_p')) {
        if (isSplitEvent) {
          console.log('[Socket][Filter] Allowing partitioned splits table:', tableRaw, 'norm:', tableNorm);
        } else {
          console.log('[Socket][Filter] Ignoring partitioned table:', tableRaw, 'norm:', tableNorm);
          return;
        }
      }
      
      // Dodaj szczegółowe logi dla debugowania
        console.log('[Socket] onDbChange received:', {
        table: tableRaw,
        norm: tableNorm,
        operation: evt.operation,
        id: evt.id,
        hasRow: !!evt.row,
        hasOldRow: !!evt.old_row,
        hasChanged: !!evt.changed,
        changedFields: evt.changed,
        fullEvent: evt
      });

      // Build idempotency signature (only for zamowienie and zamowienie_wezel tables)
      // Skip signature check for klient table - it should always process to update client financial data
      if (tableNorm === 'zamowienie' || isSplitEvent) {
        try {
          const baseId = Number(
            (evt && (evt.id as any)) ??
            (evt && evt.row && ((evt.row as any).id ?? (evt.row as any).zamowienie_id)) ??
            (evt && evt.old_row && ((evt.old_row as any).id ?? (evt.old_row as any).zamowienie_id))
          );
          if (Number.isFinite(baseId)) {
            const payload = {
              table: evt.table,
              op: evt.operation,
              id: baseId,
              row_qty: Number(evt.row?.zamowienie_ilosc ?? NaN),
              old_row_qty: Number(evt.old_row?.zamowienie_ilosc ?? NaN),
              row_wezel_id: Number(evt.row?.wezel_id ?? NaN),
              row_split: Number(evt.row?.zamowione ?? NaN),
              old_row_split: Number(evt.old_row?.zamowione ?? NaN),
              row_zreal: Number(evt.row?.zrealizowane ?? NaN),
              old_row_zreal: Number(evt.old_row?.zrealizowane ?? NaN),
              row_anul: Boolean(evt.row?.zamowienie_anulowane),
              old_row_anul: Boolean(evt.old_row?.zamowienie_anulowane)
            };
            const sig = JSON.stringify(payload);
            if (lastAppliedSignatureRef.current[baseId] === sig) {
              console.log('[Socket] Skip duplicate signature for', baseId);
              return;
            }
            lastAppliedSignatureRef.current[baseId] = sig;
            // If this exact signature equals our last local event for this id, skip as local echo
            if (lastLocalEventSigRef.current[baseId] === sig) {
              console.log('[Socket] Skip local echo by signature for', baseId);
              return;
            }
          }
        } catch {}
      }

      // Global relevance filter: apply only if BOTH date and region match current view
      const toYMD = (v: any) => { try { return String(v).slice(0, 10); } catch { return undefined; } };
      const selectedDateNow = selectedDateRef.current;
      const selectedRegionNow = selectedRegionRef.current;
      // Try to derive event date and region
      let eventDate: string | undefined = undefined;
      let eventRegion: number | undefined = undefined;
      if (evt && evt.row) {
        eventDate = toYMD((evt.row as any).zamowienie_dzien);
        const r = (evt.row as any).region_id;
        if (r !== undefined && r !== null) eventRegion = Number(r);
      }
      if (!eventDate && evt && evt.old_row) {
        eventDate = toYMD((evt.old_row as any).zamowienie_dzien);
      }
      if (eventRegion === undefined && evt && evt.old_row && (evt.old_row as any).region_id != null) {
        eventRegion = Number((evt.old_row as any).region_id);
      }
      // For zamowienie_wezel / split-only events, derive from local visible orders
      if ((!eventDate || eventRegion === undefined) && (isSplitEvent || tableNorm === 'zamowienie')) {
        const guessId = Number((evt.row?.zamowienie_id ?? evt.id ?? evt.old_row?.zamowienie_id) as any);
        if (Number.isFinite(guessId)) {
          const local = zamowienia.find(z => Number(z.id) === guessId);
          if (local) {
            eventDate = eventDate || toYMD((local as any).zamowienie_dzien);
            if (eventRegion === undefined && (local as any).region_id != null) eventRegion = Number((local as any).region_id);
          }
        }
      }
      const dateMatches = Boolean(eventDate && selectedDateNow && eventDate === selectedDateNow);
      const regionMatches = Boolean(eventRegion !== undefined && selectedRegionNow !== null && selectedRegionNow !== undefined && Number(eventRegion) === Number(selectedRegionNow));
      // Don't filter klient and chat_message tables by date/region
      // - klient affects all orders with matching NIP regardless of date/region
      // - chat_message is user-to-user communication, not related to orders/regions
      if ((!dateMatches || !regionMatches) && !isSplitEvent && tableNorm !== 'klient' && tableNorm !== 'chat_message') {
        console.log('[Socket][Filter] Ignored due to date/region mismatch', {
          eventDate, selectedDate: selectedDateNow, eventRegion, selectedRegion: selectedRegionNow, table: tableRaw, norm: tableNorm, op: evt.operation, id: evt.id
        });
        return;
      }
      
      // If geocode cache updated, refresh map automatically for current view
      if (evt.table === 'budowa_geocode' && (evt.operation === 'UPDATE' || evt.operation === 'INSERT')) {
        try {
          console.log('[Socket] budowa_geocode changed → refreshing map');
          window.dispatchEvent(new CustomEvent('map:refresh', {
            detail: {
              date: selectedDateRef.current,
              region_id: selectedRegionRef.current
            }
          }));
        } catch {}
        return;
      }

      // Handle zamowienie_wz changes - refresh WZ list if order is expanded
      if (tableNorm === 'zamowienie_wz' && (evt.operation === 'UPDATE' || evt.operation === 'INSERT' || evt.operation === 'DELETE')) {
        try {
          // Extract zamowienie_numer and zamowienie_tydzien from event to find affected orders
          const wzRow = evt.row || evt.old_row || {};
          const wzZamowienieNumer = Number(wzRow.zamowienie_numer);
          const wzZamowienieTydzien = Number(wzRow.zamowienie_tydzien);
          const wzZamowienieDzien = wzRow.zamowienie_dzien;
          
          if (wzZamowienieNumer && wzZamowienieTydzien && wzZamowienieDzien) {
            // Find all orders that match this zamowienie_numer and tydzien
            const affectedOrders = zamowienia.filter(z => 
              Number(z.zamowienie_numer) === wzZamowienieNumer &&
              Number(z.zamowienie_tydzien) === wzZamowienieTydzien &&
              String(z.zamowienie_dzien).slice(0, 10) === String(wzZamowienieDzien).slice(0, 10)
            );
            
            // Refresh WZ list for all affected orders that are currently expanded
            affectedOrders.forEach(order => {
              if (expanded[order.id]) {
                console.log('[Socket] zamowienie_wz changed → refreshing WZ for order', order.id);
                authenticatedFetch(`/api/zamowienia/${order.id}/wz`, { credentials: 'include' })
                  .then(res => res.ok ? res.json() : null)
                  .then(data => {
                    if (data && data.wz) {
                      setWzByOrder(prev => ({ ...prev, [order.id]: data.wz || [] }));
                    }
                  })
                  .catch(err => console.warn('[Socket] Failed to refresh WZ list:', err));
              }
            });
          }
        } catch (err) {
          console.warn('[Socket] Error handling zamowienie_wz notification:', err);
        }
        // Don't return - continue processing other events
      }
      
      // Handle komunikat changes - forward to Komunikaty component
      if (evt.table === 'komunikat') {
        try {
          // Add timestamp and sequence number for debugging
          const now = Date.now();
          const sequenceNumber = (window as any).__komunikat_sequence__ = ((window as any).__komunikat_sequence__ || 0) + 1;
          
          console.log(`[Socket] komunikat changed → forwarding to Komunikaty component (seq: ${sequenceNumber})`, {
            operation: evt.operation,
            id: evt.id,
            idType: typeof evt.id,
            hasRow: !!evt.row,
            rowKeys: evt.row ? Object.keys(evt.row) : 'NO_ROW_DATA',
            timestamp: new Date().toISOString(),
            sequenceNumber,
            socketId: (window as any).__socket_io_global__?.id || 'unknown'
          });
          
          // Check if this is a duplicate notification
          // Normalize ID to handle both string and number types
          const normalizedId = String(evt.id);
          const notificationKey = `komunikat_${evt.operation}_${normalizedId}`;
          const lastNotification = lastKomunikatNotificationRef.current;
          
          if (lastNotification === notificationKey) {
            console.warn(`[Socket] DUPLICATE komunikat notification detected (seq: ${sequenceNumber}):`, {
              key: notificationKey,
              lastNotification,
              currentId: evt.id,
              currentIdType: typeof evt.id,
              operation: evt.operation,
              timeSinceLast: now - (window as any).__last_komunikat_time__ || 0
            });
            return; // Skip duplicate
          }
          
          // Store this notification to prevent duplicates
          lastKomunikatNotificationRef.current = notificationKey;
          (window as any).__last_komunikat_time__ = now;
          
          console.log(`[Socket] Processing komunikat notification (seq: ${sequenceNumber}):`, {
            key: notificationKey,
            lastNotification,
            currentId: evt.id,
            currentIdType: typeof evt.id,
            operation: evt.operation
          });
          
          window.dispatchEvent(new CustomEvent('komunikat:change', {
            detail: evt
          }));
        } catch (error) {
          console.error('[Socket] Error handling komunikat change:', error);
        }
        return;
      }
      // Handle klient financial/status changes (blokada/zgoda/zadluzenie/limit/przeterminowane)
      if (evt.table === 'klient') {
        try {
          const row = evt.row || evt.old_row || {};
          const nipRaw = String(row.nip || '').trim();
          if (!nipRaw) {
            console.log('[Socket][Klient] Skipping - no NIP in event', { row, evt });
            return;
          }
          // Normalize NIP: remove dashes, spaces, and trim (matching backend btrim(replace(replace)))
          const normalizeNip = (s: string) => String(s).replace(/[-\s]/g, '').trim();
          const nipNorm = normalizeNip(nipRaw);
          
          const updatedFlags = {
            klient_zadluzenie: Number(row.zadluzenie ?? 0),
            klient_limit_kredytowy: Number(row.limit_kredytowy ?? 0),
            klient_przeterminowane: Number(row.przeterminowane ?? 0),
            klient_blokada: Boolean(row.blokada ?? false),
            klient_zgoda: Boolean(row.zgoda ?? false),
            klient_niezafakturowane: Number(row.niezafakturowane ?? 0)
          } as any;
          
          console.log('[Socket][Klient] Updating client financial data', {
            nip: nipRaw,
            normalized: nipNorm,
            flags: updatedFlags,
            operation: evt.operation
          });
          
          let updatedCount = 0;
          setZamowienia(prev => {
            const next = prev.map(z => {
              const znip = normalizeNip(String(z.klient_nip || ''));
              if (znip && znip === nipNorm) {
                updatedCount++;
                return { ...z, ...updatedFlags } as any;
              }
              return z;
            });
            if (updatedCount > 0) {
              console.log(`[Socket][Klient] Updated ${updatedCount} orders for NIP ${nipRaw}`);
            } else {
              console.warn(`[Socket][Klient] No orders found matching NIP ${nipRaw} (normalized: ${nipNorm})`);
              // Log all NIPs for debugging
              const allNips = prev.map(z => ({ id: z.id, nip: z.klient_nip, normalized: normalizeNip(String(z.klient_nip || '')) }));
              console.log('[Socket][Klient] All NIPs in current orders:', allNips.slice(0, 10));
            }
            return next;
          });
        } catch (e) {
          console.error('[Socket] klient update handling failed:', e);
        }
        return;
      }
      if (isSplitEvent) {
        // Handle zamowienie_wezel changes (splits and realizacja)
        console.log('[Socket] zamowienie_wezel changed:', evt.operation, { table: tableRaw, norm: tableNorm, id: evt.id, hasRow: !!evt.row, hasOldRow: !!evt.old_row });
        
        if (evt.operation === 'INSERT' || evt.operation === 'UPDATE') {
          // OPTIMIZATION: No need to refresh splits via API call
          // Socket.IO provides full data, we can update local state directly
          const zamowienieId = Number(evt.row?.zamowienie_id || evt.id);
          if (zamowienieId && evt.row) {
            console.log('[Optimization] Updating local splits state from Socket.IO data');
            
            // Update a single wezel row if available
            if (evt.row.wezel_id !== undefined) {
              // Compute delta for wezel aggregate based on old vs new split value
              const widNum = Number(evt.row.wezel_id);
              const oldQtyFromState = Number((splitsByOrder[zamowienieId] || {})[widNum] || 0);
              const oldQty = round2((evt.old_row && typeof evt.old_row.zamowione !== 'undefined') ? Number(evt.old_row.zamowione || 0) : oldQtyFromState);
              const newQty = round2(Number(evt.row.zamowione || 0));
              const localRow = zamowienia.find(z => Number(z.id) === zamowienieId) || null;
              // Build minimal row context for date/region gating when localRow is missing
              // Prefer localRow, then evt.row, then evt.old_row; skip if we don't have date/region info
              const rowCtx: any = localRow || (
                ((evt.row && ((evt.row as any).zamowienie_dzien || (evt.row as any).region_id)) ||
                 (evt.old_row && ((evt.old_row as any).zamowienie_dzien || (evt.old_row as any).region_id))) ? {
                  id: zamowienieId,
                  zamowienie_dzien: (evt.row && (evt.row as any).zamowienie_dzien) || (evt.old_row && (evt.old_row as any).zamowienie_dzien),
                  region_id: (evt.row && (evt.row as any).region_id) || (evt.old_row && (evt.old_row as any).region_id),
                  zamowienie_ilosc: 0
                } : null
              );
              // Skip aggregation if we don't have date/region info (can't determine if it matches current filters)
              if (!rowCtx || !rowCtx.zamowienie_dzien || !rowCtx.region_id) {
                console.log('[Socket][WezelDelta] skipped - missing date/region info', { zamowienieId, hasLocalRow: !!localRow, hasRow: !!evt.row, hasOldRow: !!evt.old_row });
                return;
              }
              setSplitsByOrder(prev => {
                const next = { ...(prev[zamowienieId] || {}) };
                next[Number(evt.row.wezel_id)] = newQty;
                return { ...prev, [zamowienieId]: next };
              });
              // Delta update for wezel aggregates (sum_ilosc) and region aggregates
              try {
                const sameDate = String(rowCtx.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
                const sameRegion = Number(rowCtx.region_id) === Number(selectedRegionRef.current);
                console.log('[Socket][WezelDelta] rowCtx:', { id: rowCtx.id, dzien: rowCtx.zamowienie_dzien, region: rowCtx.region_id, sameDate, sameRegion, wid: widNum, oldQty, newQty, delta: newQty - oldQty });
                if (sameDate && sameRegion) {
                  const deltaQty = round2(newQty - oldQty);
                  if (Math.abs(deltaQty) > 1e-9) {
                    // Skip if this exact delta was just applied locally (rare, but safe)
                    try {
                      const lastForOrder = lastLocalSplitApplyRef.current[zamowienieId]?.[widNum];
                      if (lastForOrder && Math.abs((lastForOrder.qty) - newQty) < 1e-9 && (Date.now() - lastForOrder.at) < 1200) {
                        console.log('[Socket][WezelDelta] skipping echo for wezel', widNum, 'order', zamowienieId, 'newQty', newQty);
                        return;
                      }
                    } catch {}
                    // Update wezel aggregates (sum_ilosc)
                    setWezelAgg(prev => {
                      const current = prev[widNum] || { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
                      const sum_ilosc = round2(Number(current.sum_ilosc || 0) + deltaQty);
                      const sum_zrealizowano = Number(current.sum_zrealizowano || 0);
                      const ilosc_pozostala = round2(sum_ilosc - sum_zrealizowano);
                      console.log('[Socket][WezelDelta] apply', { wid: widNum, from: current, to: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } });
                      return { ...prev, [widNum]: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } };
                    });
                    // Update region aggregates (sum_ilosc) - IMPORTANT: use order's region_id, not wezel's
                    const orderRegionId = Number(rowCtx.region_id);
                    if (orderRegionId && Number.isFinite(orderRegionId)) {
                      setRegionAgg(prev => {
                        const current = prev[orderRegionId] || { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
                        const sum_ilosc = round2(Number(current.sum_ilosc || 0) + deltaQty);
                        const sum_zrealizowano = Number(current.sum_zrealizowano || 0);
                        const ilosc_pozostala = round2(sum_ilosc - sum_zrealizowano);
                        console.log('[Socket][RegionDelta] apply', { regionId: orderRegionId, from: current, to: { sum_ilosc, sum_zrealizowano, ilosc_pozostala }, deltaQty });
                        return { ...prev, [orderRegionId]: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } };
                      });
                    }
                    try {
                      lastLocalSplitApplyRef.current[zamowienieId] = lastLocalSplitApplyRef.current[zamowienieId] || {};
                      lastLocalSplitApplyRef.current[zamowienieId][widNum] = { qty: newQty, at: Date.now() };
                    } catch {}
                  }
                } else {
                  console.log('[Socket][WezelDelta] skipped due to date/region mismatch', { sameDate, sameRegion, selectedDate: selectedDateRef.current, selectedRegion: selectedRegionRef.current });
                }
              } catch {}
              // Update realized per-wezel map for this order
              const newZrealizowane = Number(evt.row.zrealizowane || 0);
              const newZamowione = Number(evt.row.zamowione || 0);
              const hasNonZeroValue = newZrealizowane > 0 || newZamowione > 0;
              
              // Update realized per-wezel map first
              setRealizedByOrderWezel(prev => {
                const next = { ...(prev[zamowienieId] || {}) } as Record<number, number>;
                next[Number(evt.row.wezel_id)] = newZrealizowane;
                
                // If we have non-zero values, fetch fresh data from DB to ensure accuracy
                if (hasNonZeroValue) {
                  // Fetch splits from API to get accurate sum
                  authenticatedFetch(`/api/zamowienia/${zamowienieId}/splits`, { credentials: 'include' })
                    .then(res => res.ok ? res.json() : null)
                    .then(data => {
                      if (data && data.splits) {
                        const totalSum = data.splits.reduce((sum: number, s: any) => sum + Number(s.zrealizowane || 0), 0);
                        setRealizacjaByOrder(prevRealizacja => ({
                          ...prevRealizacja,
                          [zamowienieId]: totalSum
                        }));
                        // Update all realized values from API response
                        const realizedMap: Record<number, number> = {};
                        data.splits.forEach((s: any) => {
                          realizedMap[Number(s.wezel_id)] = Number(s.zrealizowane || 0);
                        });
                        setRealizedByOrderWezel(prevRealized => ({
                          ...prevRealized,
                          [zamowienieId]: realizedMap
                        }));
                      }
                    })
                    .catch(err => {
                      console.warn('[Socket] Failed to fetch splits for realizacja recalculation:', err);
                    });
                } else {
                  // If all values are zero, recalculate from local state (no DB call)
                  const totalSum = Object.values(next).reduce((sum, v) => sum + Number(v || 0), 0);
                  setRealizacjaByOrder(prevRealizacja => ({
                    ...prevRealizacja,
                    [zamowienieId]: totalSum
                  }));
                }
                
                return { ...prev, [zamowienieId]: next };
              });
              // Delta update for zrealizowane (sum_zrealizowano) in wezel and region aggregates
              try {
                const sameDate = String(rowCtx.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
                const sameRegion = Number(rowCtx.region_id) === Number(selectedRegionRef.current);
                if (sameDate && sameRegion) {
                  const oldZrealizowane = round2((evt.old_row && typeof evt.old_row.zrealizowane !== 'undefined') ? Number(evt.old_row.zrealizowane || 0) : 0);
                  const newZrealizowane = round2(Number(evt.row.zrealizowane || 0));
                  const deltaZrealizowane = round2(newZrealizowane - oldZrealizowane);
                  if (Math.abs(deltaZrealizowane) > 1e-9) {
                    // Update wezel aggregates (sum_zrealizowano)
                    setWezelAgg(prev => {
                      const current = prev[widNum] || { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
                      const sum_zrealizowano = round2(Number(current.sum_zrealizowano || 0) + deltaZrealizowane);
                      const sum_ilosc = Number(current.sum_ilosc || 0);
                      const ilosc_pozostala = round2(sum_ilosc - sum_zrealizowano);
                      console.log('[Socket][WezelDelta][Zrealizowane] apply', { wid: widNum, from: current, to: { sum_ilosc, sum_zrealizowano, ilosc_pozostala }, deltaZrealizowane });
                      return { ...prev, [widNum]: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } };
                    });
                    // Update region aggregates (sum_zrealizowano) - IMPORTANT: use order's region_id, not wezel's
                    const orderRegionId = Number(rowCtx.region_id);
                    if (orderRegionId && Number.isFinite(orderRegionId)) {
                      setRegionAgg(prev => {
                        const current = prev[orderRegionId] || { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
                        const sum_zrealizowano = round2(Number(current.sum_zrealizowano || 0) + deltaZrealizowane);
                        const sum_ilosc = Number(current.sum_ilosc || 0);
                        const ilosc_pozostala = round2(sum_ilosc - sum_zrealizowano);
                        console.log('[Socket][RegionDelta][Zrealizowane] apply', { regionId: orderRegionId, from: current, to: { sum_ilosc, sum_zrealizowano, ilosc_pozostala }, deltaZrealizowane });
                        return { ...prev, [orderRegionId]: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } };
                      });
                    }
                  }
                }
              } catch {}
            }
          }
        } else if (evt.operation === 'DELETE') {
          // Handle split deletion. Use old_row to get zamowienie_id and precise wezel_id/qty
          const old = evt.old_row || {};
          const zamowienieId = Number(old.zamowienie_id || evt.id);
          const wezelId = Number(old.wezel_id);
          const oldQty = Number(old.zamowione || 0);
          console.log('[Socket] Split deleted for order:', zamowienieId, 'wezel:', wezelId, 'qty:', oldQty);
          if (zamowienieId) {
            // Update local splits map precisely
              setSplitsByOrder(prev => {
                const map = { ...prev } as Record<number, Record<number, number>>;
                if (Number.isFinite(wezelId)) {
                  const next = { ...(map[zamowienieId] || {}) };
                  delete next[wezelId];
                  map[zamowienieId] = next;
                } else {
                  // Remove entire order entry if wezelId missing
                  const newMap = { ...map } as any;
                  delete newMap[zamowienieId];
                  return newMap;
                }
                return map;
              });
            // Apply Węzeł delta unless already subtracted by prior zamowienie DELETE handler
            try {
              const alreadyAdjusted = !!orderDeleteWezelAdjustedRef.current[zamowienieId];
              const localRow = zamowienia.find(z => Number(z.id) === zamowienieId) || null;
              // Build rowCtx: prefer localRow, then old_row, skip if we don't have date/region info
              const rowCtx: any = localRow || (
                (old && ((old as any).zamowienie_dzien || (old as any).region_id)) ? {
                  id: zamowienieId,
                  zamowienie_dzien: (old as any).zamowienie_dzien,
                  region_id: (old as any).region_id,
                  zamowienie_ilosc: 0
                } : null
              );
              // Skip aggregation if we don't have date/region info (can't determine if it matches current filters)
              if (!rowCtx || !rowCtx.zamowienie_dzien || !rowCtx.region_id) {
                console.log('[Socket][WezelDelta][DELETE] skipped - missing date/region info', { zamowienieId, wezelId, hasLocalRow: !!localRow, hasOldRow: !!old });
                return;
              }
              const regionMatches = Number(rowCtx.region_id) === Number(selectedRegionRef.current);
              const dateMatches = String(rowCtx.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
              if (dateMatches && regionMatches) {
                if (!alreadyAdjusted) {
                  if (Number.isFinite(wezelId) && oldQty > 0) {
                    setWezelAgg(prev => {
                      const current = prev[wezelId] || { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
                      const sum_ilosc = round2(Number(current.sum_ilosc || 0) - Number(oldQty || 0));
                      const sum_zrealizowano = Number(current.sum_zrealizowano || 0);
                      const ilosc_pozostala = round2(sum_ilosc - sum_zrealizowano);
                      console.log('[Socket][WezelDelta][DELETE] apply', { wid: wezelId, delta: -oldQty, from: current, to: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } });
                      return { ...prev, [wezelId]: { sum_ilosc, sum_zrealizowano, ilosc_pozostala } };
                    });
                  } else {
                    // DB-driven refresh for delete when details are missing
                    debouncedFetchWezel();
                  }
                } else {
                  console.log('[Socket][WezelDelta][DELETE] skipped - already adjusted by order DELETE', { zamowienieId, wezelId, oldQty });
                }
              } else {
                console.log('[Socket][WezelDelta][DELETE] skipped due to date/region mismatch', { zamowienieId, wezelId });
              }
            } catch {}
          }
        }
        
        // Note: DB-driven refresh is handled conditionally above (lines 3703-3723) based on date/region match
        // Aggregates are only fetched if the deleted split's order matches current filters
        
        return;
      }
      if (evt.table !== 'zamowienie') return;
      if (evt.operation !== 'UPDATE' && evt.operation !== 'INSERT' && evt.operation !== 'DELETE') return;
      console.log('[Socket] db:change', evt);
      const idNum = Number(
        (evt && (evt.id as any)) ??
        (evt && evt.row && ((evt.row as any).id ?? (evt.row as any).zamowienie_id)) ??
        (evt && evt.old_row && ((evt.old_row as any).id ?? (evt.old_row as any).zamowienie_id))
      );
      if (!idNum) {
        console.warn('[Socket] Missing id for zamowienie event', { evt });
        return;
      }
      if (evt.operation === 'DELETE') {
        // Filter out local delete echo
        const lastLocal = lastLocalSaveByOrderRef.current[idNum] || 0;
        if (lastLocal && (Date.now() - lastLocal) < 1500) {
          console.log('[Socket] Suppressing local delete echo for order', idNum);
          return;
        }
        // DB-driven region/wezel refresh on delete
        try {
          const oldRow = zamowienia.find(z => Number(z.id) === idNum) || null;
          if (oldRow) {
            const sameDate = String((oldRow as any).zamowienie_dzien).slice(0,10) === selectedDateRef.current;
            const sameRegion = Number((oldRow as any).region_id) === Number(selectedRegionRef.current);
            if (sameDate && sameRegion) {
              debouncedFetchRegion();
              debouncedFetchWezel();
              // Mark that Węzeł aggregates were adjusted by order DELETE
              orderDeleteWezelAdjustedRef.current[idNum] = true;
            }
          }
          setSplitsByOrder(prev => { const ns = { ...prev }; delete ns[idNum]; return ns; });
        } catch {}
        setZamowienia(prev => {
          const existed = prev.some(z => Number(z.id) === idNum);
          if (!existed) return prev; // already removed locally
          return prev.filter(z => Number(z.id) !== idNum);
        });
        // Note: Aggregates are fetched conditionally above (lines 3759-3761) only if date/region match
        return;
      }
      
      // Dodaj logi dla zamowienie changes
      console.log('[Socket] zamowienie change details:', {
        operation: evt.operation,
        id: idNum,
        hasRowData: !!evt.row,
        rowKeys: evt.row ? Object.keys(evt.row) : 'NO_ROW_DATA',
        changedFields: evt.changed || []
      });
      // On INSERT, try to add row immediately if it matches current filters; otherwise fetch
      if (evt.operation === 'INSERT') {
        const row = evt.row || null;
        const toYMD = (v: any) => {
          try { return String(v).slice(0,10); } catch { return null; }
        };
        const rowDate = row ? toYMD(row.zamowienie_dzien) : null;
        const matchesDate = !!rowDate && rowDate === selectedDateRef.current;
        const matchesRegion = row && selectedRegionRef.current ? Number(row.region_id) === Number(selectedRegionRef.current) : false;
        let matchesTab = true;
        try {
          const currentTab = tabs.find(t => t.id === activeTab);
          if (currentTab && !currentTab.isRegion && currentTab.wezelId) {
            matchesTab = Number(row?.zamowienie_wezel_wiodacy) === Number(currentTab.wezelId);
          }
        } catch {}
        if (row && matchesDate && (matchesTab || matchesRegion)) {
          setZamowienia(prev => {
            // ENHANCED DEDUPLICATION: Check for existing order by ID
            if (prev.some(z => Number(z.id) === idNum)) {
              console.log('[Socket] Order already exists, skipping duplicate:', idNum);
              return prev;
            }
            
            // Add new order and ensure uniqueness
            const next = [...prev, row as any];
            
            // Additional deduplication by ID (should not be needed but safety first)
            const m = new Map<number, any>();
            next.forEach((z: any) => m.set(Number(z.id), z));
            const unique = Array.from(m.values());
            
            console.log('[Socket] Added new order via Socket.IO:', {
              id: idNum,
              totalOrders: unique.length,
              wasDuplicate: false
            });
            
            // Stable sort by zamowienie_godzinaod, then zamowienie_godzinado
            const toTime = (s?: string) => {
              if (!s) return 0;
              const [hh, mm] = String(s).split(':').map(n => parseInt(n || '0', 10));
              return (hh || 0) * 60 + (mm || 0);
            };
            return unique.sort((a, b) => {
              const aStart = toTime(a.zamowienie_godzinaod as any);
              const bStart = toTime(b.zamowienie_godzinaod as any);
              if (aStart !== bStart) return aStart - bStart;
              const aEnd = toTime(a.zamowienie_godzinado as any);
              const bEnd = toTime(b.zamowienie_godzinado as any);
              if (aEnd !== bEnd) return aEnd - bEnd;
              // Use zamowienie_numer as final tiebreaker for consistent ordering
              return (a.zamowienie_numer || 0) - (b.zamowienie_numer || 0);
            });
          });
          // Update recept confirmation status if present
          if (row && (row as any).beton_klasa_is_confirmed !== null && (row as any).beton_klasa_is_confirmed !== undefined) {
            setBetonKlasaConfirmed(prev => {
              const newMap = new Map(prev);
              newMap.set(idNum, (row as any).beton_klasa_is_confirmed === true);
              return newMap;
            });
          }
          // Preserve financial data if missing from socket but exists in local state
          setZamowienia(prev => {
            const existing = prev.find(z => Number(z.id) === idNum);
            if (existing && row) {
              const existingAny = existing as any;
              const rowAny = row as any;
              // If socket data doesn't have financial info but local does, merge it
              if (!rowAny.klient_zadluzenie && existingAny.klient_zadluzenie) {
                return prev.map(z => {
                  if (Number(z.id) === idNum) {
                    return { ...z, ...row, ...existingAny } as any;
                  }
                  return z;
                });
              }
            }
            return prev;
          });
          // Apply aggregates delta for insert if matches current filters; ensure no duplication
          try {
            const eventDate = String(row.zamowienie_dzien).slice(0,10);
            const currentSelectedDate = selectedDateRef.current;
            const sameDate = eventDate === currentSelectedDate;
            const sameRegion = Number(row.region_id) === Number(selectedRegionRef.current);
            console.log('[Socket][INSERT][Aggregates] date check:', {
              orderId: idNum,
              eventDate,
              currentSelectedDate,
              sameDate,
              eventRegion: row.region_id,
              currentSelectedRegion: selectedRegionRef.current,
              sameRegion,
              willFetch: sameDate && sameRegion
            });
            if (sameDate && sameRegion) {
              // Prevent double-apply: if this order was just created locally, skip
              const lastLocal = lastLocalSaveByOrderRef.current[idNum] || 0;
              const isEcho = lastLocal && (Date.now() - lastLocal) < 1500;
              console.log('[Socket][INSERT][Aggregates] echo check:', {
                orderId: idNum,
                lastLocal,
                now: Date.now(),
                timeSinceLocal: lastLocal ? Date.now() - lastLocal : null,
                isEcho,
                willFetch: !isEcho
              });
              if (!isEcho) {
                // DB-driven refresh on insert
                console.log('[Socket][INSERT][Aggregates] calling debouncedFetchRegion/Wezel with date:', currentSelectedDate);
                debouncedFetchRegion();
                debouncedFetchWezel();
              } else {
                console.log('[Socket] Skipping delta for insert echo', idNum);
              }
            } else {
              console.log('[Socket][INSERT][Aggregates] SKIPPED - date/region mismatch', {
                orderId: idNum,
                eventDate,
                currentSelectedDate,
                sameDate,
                sameRegion
              });
            }
          } catch (err) {
            console.error('[Socket][INSERT][Aggregates] error:', err);
          }
          try {
            orderCacheRef.current[idNum] = {
              id: Number(row.id),
              region_id: Number(row.region_id),
              zamowienie_dzien: row.zamowienie_dzien,
              zamowienie_ilosc: Number(row.zamowienie_ilosc || 0),
              zamowienie_anulowane: Boolean(row.zamowienie_anulowane)
            } as any;
          } catch {}

          // Refresh pump schedule if new order has pump fields
          if (
            (row.pompa_nazwa && row.pompa_nazwa.trim() !== '') ||
            (row.pompa_numer && row.pompa_numer.trim() !== '')
          ) {
            try {
              console.log('[Socket] new order with pump → refreshing pump schedule', {
                date: selectedDate,
                region_id: row.region_id
              });
              window.dispatchEvent(new CustomEvent('pumpSchedule:refresh', {
                detail: {
                  date: selectedDate,
                  region_id: row.region_id
                }
              }));
            } catch {}
          }
        } else {
          // Force immediate reload to include new record
          loadZamowienia();
          // OPTIMIZATION: Aggregates will be updated by the subsequent INSERT event
          console.log('[Optimization] Full reload done; waiting for INSERT to update aggregates');
        }
        return;
      }
      // On UPDATE: handle status changes (anulowane) via delta
      if (evt.operation === 'UPDATE') {
        const row = evt.row || null;
        if (row && typeof row.zamowienie_anulowane !== 'undefined') {
          try {
            const sameDate = String(row.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
            const sameRegion = Number(row.region_id) === Number(selectedRegionRef.current);
            if (sameDate && sameRegion) {
              const before = orderCacheRef.current[idNum] || null;
              const oldAnul = Boolean(before?.zamowienie_anulowane);
              const newAnul = Boolean(row.zamowienie_anulowane);
              if (oldAnul !== newAnul) {
                const localSplits: Record<number, number> = splitsByOrder[idNum] || {};
                const needFetchSplits = !localSplits || Object.keys(localSplits).length === 0;
                const qtyUsed = Number((before?.zamowienie_ilosc ?? row.zamowienie_ilosc) || 0);
                console.log('[Status][Anul] change', { id: idNum, oldAnul, newAnul, qtyUsed, needFetchSplits });
                const applyWith = (splitsMap: Record<number, number>) => { debouncedFetchRegion(); debouncedFetchWezel(); };
                if (needFetchSplits) {
                  // Fetch splits for this order before applying Węzeł delta (remote client may not have them loaded)
                  (async () => {
                    try {
                      const res = await authenticatedFetch(`/api/zamowienia/${idNum}/splits`, { credentials: 'include' });
                      if (res.ok) {
                        const j = await res.json();
                        const map: Record<number, number> = {};
                        (j.splits || []).forEach((r: any) => { map[Number(r.wezel_id)] = Number(r.zamowione || 0); });
                        setSplitsByOrder(prev => ({ ...prev, [idNum]: map }));
                        console.log('[Status][Anul] fetched splits for order', idNum, map);
                        applyWith(map);
                      } else {
                        console.warn('[Status][Anul] failed to fetch splits for order', idNum, res.status);
                        applyWith({});
                      }
                    } catch (e) {
                      console.warn('[Status][Anul] error fetching splits for order', idNum, e);
                      applyWith({});
                    }
                  })();
                } else {
                  applyWith(localSplits);
                }
                // update cache flag
                try { orderCacheRef.current[idNum] = { ...(orderCacheRef.current[idNum] || {}), zamowienie_anulowane: newAnul }; } catch {}
              }
            }
          } catch {}
        }
        // On UPDATE: if zamowienie_ilosc changed, adjust Region by delta (splits handled only by split events)
        try {
          const r = evt.row || null;
          const o = evt.old_row || null;
          if (r && o && (typeof r.zamowienie_ilosc !== 'undefined') && (typeof o.zamowienie_ilosc !== 'undefined')) {
            const newQty = Number(r.zamowienie_ilosc || 0);
            const oldQty = Number(o.zamowienie_ilosc || 0);
            const delta = newQty - oldQty;
            if (Math.abs(delta) > 1e-9) {
              const sameDate = String(r.zamowienie_dzien).slice(0,10) === selectedDateRef.current;
              const sameRegion = Number(r.region_id) === Number(selectedRegionRef.current);
              if (sameDate && sameRegion) {
                // Signature-based echo dedupe (no time window)
                const sigPayload = {
                  table: 'zamowienie',
                  op: 'UPDATE',
                  id: idNum,
                  row_qty: newQty,
                  old_row_qty: oldQty,
                  row_wezel_id: NaN,
                  row_split: NaN,
                  old_row_split: NaN,
                  row_anul: Boolean(r.zamowienie_anulowane),
                  old_row_anul: Boolean(o.zamowienie_anulowane)
                };
                const sig = JSON.stringify(sigPayload);
                if (lastLocalEventSigRef.current[idNum] === sig) {
                  console.log('[Socket][RegionDelta][UPDATE] skip (signature echo)', { id: idNum });
                } else {
                  console.log('[Socket][RegionDelta][UPDATE] apply', { id: idNum, oldQty, newQty, delta });
                  debouncedFetchRegion();
                }
              } else {
                console.log('[Socket][RegionDelta][UPDATE] skipped due to date/region mismatch', { id: idNum });
              }
            }
          }
        } catch {}
      }
      // Blink changed cells for 5s instead of row outline
      if (Array.isArray(evt.changed) && evt.changed.length > 0) {
        setBlinkMap(prev => {
          const copy: Record<number, Set<string>> = { ...prev };
          copy[idNum] = new Set(evt.changed);
          return copy;
        });
        setTimeout(() => {
          setBlinkMap(prev => {
            const copy: Record<number, Set<string>> = { ...prev };
            delete copy[idNum];
            return copy;
          });
        }, 60000);
      }
      // Apply partial row update if payload contains row and matches current filters
      if (evt.row) {
        console.log('[Socket] Applying partial row update for zamowienie:', idNum, evt.row);
        setZamowienia(prev => {
          const exists = prev.some(z => z.id === idNum);
          const base = exists ? prev.map(z => (z.id === idNum ? { ...z, ...evt.row } : z)) : prev;
          const updated = base;
          // Stable sort by zamowienie_godzinaod, then zamowienie_godzinado
          const toTime = (s?: string) => {
            if (!s) return 0;
            const [hh, mm] = String(s).split(':').map(n => parseInt(n || '0', 10));
            return (hh || 0) * 60 + (mm || 0);
          };
          return [...updated].sort((a, b) => {
            const aStart = toTime(a.zamowienie_godzinaod as any);
            const bStart = toTime(b.zamowienie_godzinaod as any);
            if (aStart !== bStart) return aStart - bStart;
            const aEnd = toTime(a.zamowienie_godzinado as any);
            const bEnd = toTime(b.zamowienie_godzinado as any);
            if (aEnd !== bEnd) return aEnd - bEnd;
            // Use zamowienie_numer as final tiebreaker for consistent ordering
            return (a.zamowienie_numer || 0) - (b.zamowienie_numer || 0);
          });
        });
        // Update recept confirmation status if present
        if ((evt.row as any).beton_klasa_is_confirmed !== null && (evt.row as any).beton_klasa_is_confirmed !== undefined) {
          setBetonKlasaConfirmed(prev => {
            const newMap = new Map(prev);
            newMap.set(idNum, (evt.row as any).beton_klasa_is_confirmed === true);
            return newMap;
          });
        }

        // Check if pump fields changed and refresh pump schedule
        if (Array.isArray(evt.changed) && evt.changed.length > 0) {
          const pumpFieldsChanged = evt.changed.some((field: string) => 
            field === 'pompa_nazwa' || field === 'pompa_numer'
          );    
          if (pumpFieldsChanged && evt.row && evt.row.region_id) {
            try {
              console.log('[Socket] pump fields changed → refreshing pump schedule', {
                date: selectedDate,
                region_id: evt.row.region_id
              });
              window.dispatchEvent(new CustomEvent('pumpSchedule:refresh', {
                detail: {
                  date: selectedDate,
                  region_id: evt.row.region_id
                }
              }));
            } catch {}
          }
        }
      } else if (evt.truncated) {
        // Payload przekroczyl limit NOTIFY i baza przyslala sama liste zmienionych kolumn,
        // bez zawartosci wiersza. Dzieje sie tak przy dlugich uwagach - rzadko, ale realnie.
        // Bez tej galezi zmiana nie dotarlaby do innych stanowisk NIGDY: wpis w
        // notification_log jest rowniez obciety, wiec resync po rozlaczeniu tez by nie pomogl,
        // a lastSeq i tak zostaje podbity. Nie mamy czym zaktualizowac wiersza punktowo,
        // wiec pobieramy liste od nowa - to jedyna droga do prawdziwego stanu.
        console.warn('[Socket] Payload obciety (za duzy) - przeladowuje liste', {
          operation: evt.operation, id: idNum, zmienione: evt.changed_keys
        });
        debouncedLoadZamowienia();
      } else {
        console.warn('[Socket] NO ROW DATA for zamowienie change:', {
          operation: evt.operation,
          id: idNum,
          event: evt
        });
      }
      // Aggregates are updated via local deltas only (no effect-based recompute)

      // If no row payload, skip aggregates update to avoid inconsistencies
    };
    
    // Subscribe to notifications from global socketService
    const unsubscribe = socketService.subscribe(processNotification);
    
    return () => {
      unsubscribe();
      // Note: Don't close socket on unmount - it's shared across components
    };
  }, [selectedRegion, selectedDate, loadZamowienia]);

  const fetchRegionAggregates = async (date: string, source?: string) => {
    try {
      console.log('[API] fetchRegionAggregates called for date:', date, source ? `[source: ${source}]` : '');
      
      const resp = await authenticatedFetch(`/api/aggregates/regions?date=${encodeURIComponent(date)}`, {
        credentials: 'include'
      });
      if (!resp.ok) return;
      const j = await resp.json();
      const map: Record<number, { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }> = {};
      (j.data || []).forEach((row: any) => {
        map[Number(row.region_id)] = {
          sum_ilosc: parseFloat(String(row.sum_ilosc || 0)) || 0,
          sum_zrealizowano: parseFloat(String(row.sum_zrealizowano || 0)) || 0,
          ilosc_pozostala: parseFloat(String(row.ilosc_pozostala || 0)) || 0,
        };
      });
      setRegionAgg(map);
    } catch {}
  };

  const fetchWezelAggregates = async (date: string, regionId: number, source?: string) => {
    try {
      console.log('[API] fetchWezelAggregates called for date:', date, 'region:', regionId, source ? `[source: ${source}]` : '');
      
      const params = new URLSearchParams({ date, region_id: String(regionId) });
      const resp = await authenticatedFetch(`/api/aggregates/wezels?${params.toString()}`, {
        credentials: 'include'
      });
      if (!resp.ok) return;
      const j = await resp.json();
      const map: Record<number, { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }> = {};
      (j.data || []).forEach((row: any) => {
        map[Number(row.wezel_id)] = {
          sum_ilosc: parseFloat(String(row.sum_ilosc || 0)) || 0,
          sum_zrealizowano: parseFloat(String(row.sum_zrealizowano || 0)) || 0,
          ilosc_pozostala: parseFloat(String(row.ilosc_pozostala || 0)) || 0,
        };
      });
      setWezelAgg(map);
    } catch {}
  };
  // Debounced fetchers (500ms) with in-flight guards
  const debouncedFetchRegion = useCallback(() => {
    // Use ref to always get current date, not stale closure value
    const currentDate = selectedDateRef.current;
    const callStack = new Error().stack;
    console.log('[debouncedFetchRegion] called', {
      currentDate,
      callStack: callStack?.split('\n').slice(1, 4).join(' | ')
    });
    if (!currentDate) {
      console.log('[debouncedFetchRegion] SKIPPED - no currentDate');
      return;
    }
    if (regionDebounceRef.current) clearTimeout(regionDebounceRef.current);
    regionDebounceRef.current = setTimeout(async () => {
      if (isRegionFetchInFlightRef.current) {
        console.log('[debouncedFetchRegion] SKIPPED - fetch already in flight');
        return;
      }
      try {
        isRegionFetchInFlightRef.current = true;
        // Check date again in timeout to ensure it hasn't changed
        const dateAtTimeout = selectedDateRef.current;
        console.log('[debouncedFetchRegion] timeout callback', {
          originalDate: currentDate,
          dateAtTimeout,
          datesMatch: dateAtTimeout === currentDate,
          willFetch: dateAtTimeout && dateAtTimeout === currentDate
        });
        if (dateAtTimeout && dateAtTimeout === currentDate) {
          await fetchRegionAggregates(dateAtTimeout, 'debouncedFetchRegion');
        } else {
          console.log('[debouncedFetchRegion] SKIPPED - date changed during timeout', {
            originalDate: currentDate,
            dateAtTimeout
          });
        }
      } finally {
        isRegionFetchInFlightRef.current = false;
      }
    }, 500);
  }, [fetchRegionAggregates]);

  const debouncedFetchWezel = useCallback(() => {
    // Use refs to always get current values, not stale closure values
    const currentDate = selectedDateRef.current;
    const currentRegion = selectedRegionRef.current;
    const callStack = new Error().stack;
    console.log('[debouncedFetchWezel] called', {
      currentDate,
      currentRegion,
      callStack: callStack?.split('\n').slice(1, 4).join(' | ')
    });
    if (!currentDate || !currentRegion) {
      console.log('[debouncedFetchWezel] SKIPPED - missing date/region', { currentDate, currentRegion });
      return;
    }
    if (wezelDebounceRef.current) clearTimeout(wezelDebounceRef.current);
    wezelDebounceRef.current = setTimeout(async () => {
      if (isWezelFetchInFlightRef.current) {
        console.log('[debouncedFetchWezel] SKIPPED - fetch already in flight');
        return;
      }
      try {
        isWezelFetchInFlightRef.current = true;
        // Check date and region again in timeout to ensure they haven't changed
        const dateAtTimeout = selectedDateRef.current;
        const regionAtTimeout = selectedRegionRef.current;
        console.log('[debouncedFetchWezel] timeout callback', {
          originalDate: currentDate,
          originalRegion: currentRegion,
          dateAtTimeout,
          regionAtTimeout,
          datesMatch: dateAtTimeout === currentDate,
          regionsMatch: regionAtTimeout === currentRegion,
          willFetch: dateAtTimeout && regionAtTimeout && dateAtTimeout === currentDate && regionAtTimeout === currentRegion
        });
        if (dateAtTimeout && regionAtTimeout && dateAtTimeout === currentDate && regionAtTimeout === currentRegion) {
          await fetchWezelAggregates(dateAtTimeout, regionAtTimeout, 'debouncedFetchWezel');
        } else {
          console.log('[debouncedFetchWezel] SKIPPED - date/region changed during timeout', {
            originalDate: currentDate,
            originalRegion: currentRegion,
            dateAtTimeout,
            regionAtTimeout
          });
        }
      } finally {
        isWezelFetchInFlightRef.current = false;
      }
    }, 500);
  }, [fetchWezelAggregates]);
  // Calculate aggregates locally from zamowienia data
  const calculateLocalAggregates = useCallback(() => {
    if (!selectedDate) {
      console.log('[Local] No selectedDate, returning empty aggregates');
      return { regions: {}, wezels: {} };
    }
    
    const toYMD = (v: any) => {
      try { return String(v).slice(0,10); } catch { return String(v || ''); }
    };
    
    console.log('[Local] Calculating aggregates for date:', selectedDate, {
      zamowieniaCount: zamowienia.length,
      selectedRegion,
      zamowieniaDates: zamowienia.map(z => toYMD(z.zamowienie_dzien)),
      zamowieniaRegions: zamowienia.map(z => z.region_id)
    });
    
    const regions: Record<number, { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }> = {};
    const wezels: Record<number, { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }> = {};
    
    let processedCount = 0;
    let skippedCount = 0;
    
    zamowienia.forEach((zamowienie, index) => {
      // Normalize date for comparison
      const rowDate = toYMD(zamowienie.zamowienie_dzien);
      if (rowDate !== selectedDate) {
        skippedCount++;
        return;
      }
      // Skip canceled orders from aggregates
      if (zamowienie.zamowienie_anulowane) {
        skippedCount++;
        return;
      }
      
      processedCount++;
      const regionId = zamowienie.region_id;
      const wezelIdLeading = zamowienie.zamowienie_wezel_wiodacy;
      const ilosc = Number(zamowienie.zamowienie_ilosc || 0);
      
      // Realized quantity: prefer per-order map from live updates, else sum per-wezel realized, else 0
      let zrealizowano = Number(realizacjaByOrder[zamowienie.id] ?? 0);
      if (!zrealizowano && realizedByOrderWezel[zamowienie.id]) {
        zrealizowano = Object.values(realizedByOrderWezel[zamowienie.id]).reduce((sum, v) => sum + Number(v || 0), 0);
      }
      
      // Update region aggregates
      if (regionId && !regions[regionId]) {
        regions[regionId] = { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
      }
      if (regionId) {
        regions[regionId].sum_ilosc += ilosc;
        regions[regionId].sum_zrealizowano += zrealizowano;
        regions[regionId].ilosc_pozostala += (ilosc - zrealizowano);
      }
      
      // Update wezel aggregates: per-wezel based on splits (zamowienie_wezel.zamowione), independent of leading
      const splits = splitsByOrder[zamowienie.id] || {};
      for (const [widStr, qty] of Object.entries(splits)) {
        const wid = Number(widStr);
        const splitQty = Number(qty || 0);
        if (!wezels[wid]) {
          wezels[wid] = { sum_ilosc: 0, sum_zrealizowano: 0, ilosc_pozostala: 0 };
        }
        const realizedForWezel = Number(realizedByOrderWezel[zamowienie.id]?.[wid] ?? 0);
        wezels[wid].sum_ilosc += splitQty;
        wezels[wid].sum_zrealizowano += realizedForWezel;
        wezels[wid].ilosc_pozostala += (splitQty - realizedForWezel);
      }
    });
    
    console.log('[Local] Calculated aggregates:', { 
      regions, 
      wezels,
      processedCount,
      skippedCount,
      totalZamowienia: zamowienia.length
    });
    return { regions, wezels };
  }, [zamowienia, selectedDate, selectedRegion, realizacjaByOrder, realizedByOrderWezel]);
  // Removed local delta adjustments: aggregates now fetched from backend

  // Removed local aggregates recompute effects. Aggregates update only on saves and socket events.

  // Calculate aggregates locally from zamowienia data (no API calls)
  useEffect(() => {
    const agg = calculateLocalAggregates();
    setRegionAgg(agg.regions);
    setWezelAgg(agg.wezels);
    
    console.log('[Local] Aggregates updated from local calculation', {
      regionsCount: Object.keys(agg.regions).length,
      wezelsCount: Object.keys(agg.wezels).length
    });
  }, [zamowienia, splitsByOrder, realizedByOrderWezel, selectedDate, selectedRegion, calculateLocalAggregates]);

  const renderTriple = (a?: { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }) => {
    const sumI = a?.sum_ilosc ?? 0;
    const sumZ = a?.sum_zrealizowano ?? 0;
    const left = a?.ilosc_pozostala ?? (sumI - sumZ);
    const format = (n: number) => {
      if (!Number.isFinite(n)) return '0,00';
      return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
    };
    return (
      <span style={{ marginLeft: 6, fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ color: 'var(--text)', fontWeight: 'inherit' }}>Zam. {format(sumI)}</span>
        <span style={{ color: 'var(--muted)', fontWeight: 'inherit' }}>/</span>
        <span style={{ color: 'var(--ok)', fontWeight: 'inherit' }}>Wyk. {format(sumZ)}</span>
        <span style={{ color: 'var(--muted)', fontWeight: 'inherit' }}>/</span>
        <span style={{ color: 'var(--error)', fontWeight: 'inherit' }}>Poz. {format(left)}</span>
      </span>
    );
  };

  const renderTripleCompact = (a?: { sum_ilosc: number; sum_zrealizowano: number; ilosc_pozostala: number }): string => {
    const sumI = a?.sum_ilosc ?? 0;
    const sumZ = a?.sum_zrealizowano ?? 0;
    const left = a?.ilosc_pozostala ?? (sumI - sumZ);
    const fmt = (n: number) => {
      if (!Number.isFinite(n)) return '0,00';
      return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
    };
    return `${fmt(sumI)} / ${fmt(sumZ)} / ${fmt(left)}`;
  };

  // Filters: statuses + godziny (HH:mm)
  const [filterStatus, setFilterStatus] = React.useState<{ potwierdzone?: boolean; realizacja?: boolean; zakonczone?: boolean; anulowane?: boolean }>({});
  const [filterFrom, setFilterFrom] = React.useState<string>('');
  const [filterTo, setFilterTo] = React.useState<string>('');

  const parseMinutes = (val?: string): number | null => {
    if (!val) return null;
    const s = String(val).trim();
    if (s === '') return null;
    const parts = s.split(':');
    let h = NaN; let m = NaN;
    if (parts.length === 1) {
      // Only hour provided, treat as HH:00
      h = Number(parts[0]);
      m = 0;
    } else if (parts.length >= 2) {
      h = Number(parts[0]);
      // If minutes not provided or empty, treat as 0
      m = parts[1] === '' || parts[1] == null ? 0 : Number(parts[1]);
    }
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
  };

  // Time filter semantics:
  // - If filterFrom set (e.g. 15:00) show rows with godzinaod >= filterFrom (inclusive)
  // - If filterTo set (e.g. 17:00) show rows with godzinado <= filterTo (inclusive)
  // Rows missing the required time are excluded when that bound is active
  const isRowInTimeWindow = (row: any): boolean => {
    const fromMin = parseMinutes(filterFrom);
    const toMin = parseMinutes(filterTo);

    if (fromMin != null) {
      const start = parseMinutes(row?.zamowienie_godzinaod);
      if (start == null || !(start >= fromMin)) return false;
    }
    if (toMin != null) {
      const end = parseMinutes(row?.zamowienie_godzinado);
      if (end == null || !(end <= toMin)) return false;
    }
    return true;
  };

  // Normalize free-form time input (accepts '23', '7:5', '07', '23:') to 'HH:MM'
  const normalizeTimeInput = (val: string): string => {
    if (!val) return '';
    const s = String(val).trim();
    if (s === '') return '';
    const parts = s.split(':');
    let hStr = parts[0] ?? '';
    let mStr = parts[1] ?? '';
    if (hStr === '' && mStr === '') return '';
    let h = Number(hStr);
    if (!Number.isFinite(h) || h < 0 || h > 23) return '';
    let m = mStr === '' ? 0 : Number(mStr);
    if (!Number.isFinite(m) || m < 0 || m > 59) return '';
    const hh = h.toString().padStart(2, '0');
    const mm = m.toString().padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // Status filter semantics:
  // - Domyślnie wszystkie stany włączone
  // - Jeżeli jakiś stan jest wyłączony (false), ukryj rekordy, które mają ten stan = true
  const matchesStatus = (row: any): boolean => {
    if (filterStatus.potwierdzone === false && !!row?.zamowienie_potwierdzone) return false;
    if (filterStatus.realizacja === false && !!row?.zamowienie_realizacja) return false;
    if (filterStatus.zakonczone === false && !!row?.zamowienie_zakonczone) return false;
    if (filterStatus.anulowane === false && !!row?.zamowienie_anulowane) return false;
    return true;
  };

  // Compute rows per active tab, then apply filters
  const rowsForTab = (() => {
    try {
      const currentTab = tabs.find(t => t.id === activeTab);
      if (currentTab && !currentTab.isRegion && currentTab.wezelId) {
        const wid = Number(currentTab.wezelId);
        return zamowienia
          .filter(z => (
            Number(z.zamowienie_wezel_wiodacy) === wid || Number((splitsByOrder[z.id] || {})[wid] || 0) > 0
          ))
          .filter(z => isTempId(Number(z.id)) || (matchesStatus(z) && isRowInTimeWindow(z)));
      }
    } catch {}
    return zamowienia.filter(z => isTempId(Number(z.id)) || (matchesStatus(z) && isRowInTimeWindow(z)));
  })();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid var(--border)', 
          borderTop: '4px solid var(--accent)', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
      </div>
    );
  }
  // Determine if filters are active (for icon highlight)
  const defaultStatuses = (filterStatus.potwierdzone !== false) && (filterStatus.realizacja !== false) && (filterStatus.zakonczone !== false) && (filterStatus.anulowane !== false);
  const defaultTimes = !normalizeTimeInput(filterFrom) && !normalizeTimeInput(filterTo);
  const isFilterActive = !(defaultStatuses && defaultTimes);
  return (
    <div
      className="card-zamowienia-widget-root"
      style={widgetRootStyle}
    >
      {/* Sentinel for IntersectionObserver (positioned just before tabs) */}
      {tabs.length > 0 && <div ref={tabsSentinelRef} style={{ height: 1 }} />}
      
      {/* Tabs - pill style with CSS sticky */}
      {tabs.length > 0 && (
          <div
            ref={tabsContainerRef}
            role="tablist"
            aria-label="Filtry węzłów"
            style={{
              position: 'sticky',
              top: '38px', // TopBar height (37.6px rounded up)
              zIndex: 25, // Lower than TopBar (z-50)
              background: 'var(--card)',
              borderBottom: '1px solid var(--border)',
              padding: '6px 0',
              overflowX: 'auto',
              display: 'flex',
              gap: 8,
              flexShrink: 0,
              boxShadow: tabsSticky ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
              transition: 'box-shadow 150ms ease-in-out'
            }}
          >
          {tabs.map((tab) => {
            // Read colors once from context hook defined at top-level
            const tabColors = statusColorsRef.current?.Tabs || null;
            const isActive = activeTab === tab.id;
            const aggObj = (tab.isRegion && selectedRegion)
              ? (regionAgg[selectedRegion] || null)
              : (!tab.isRegion && tab.wezelId ? (wezelAgg[tab.wezelId] || null) : null);
            const fmt = (n: number) => {
              const val = Number.isFinite(n) ? n : 0;
              return val.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            };
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                title={tab.name}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                  height: 32,
                  padding: '0 8px',
                  borderRadius: 8,
                  border: `1px solid ${tabColors ? (isActive ? tabColors.activeBorder : tabColors.inactiveBorder) : (isActive ? 'var(--accent)' : 'var(--border)')}`,
                  background: tabColors ? (isActive ? tabColors.activeBg : tabColors.inactiveBg) : (isActive ? 'var(--accent)' : 'var(--bg)'),
                  color: tabColors ? (isActive ? tabColors.activeText : tabColors.inactiveText) : (isActive ? 'var(--accent)' : 'var(--text)'),
                  fontSize: 10,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  width: 230,
                  minWidth: 230,
                  maxWidth: 230,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'background-color 150ms, border-color 150ms, color 150ms'
                }}
              >
                {/* Left side: status dot + name + Zam */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', flex: 1 }}>
                  {!tab.isRegion && (
                    <WskaznikWezla
                      wezelId={Number(tab.wezelId)}
                      tytul={tab.name}
                      rozmiar={22}
                      onPokazOpis={(opis, prostokat) => setTooltip({
                        visible: true, text: opis,
                        x: prostokat.left + prostokat.width / 2, y: prostokat.top - 8,
                      })}
                      onUkryjOpis={() => setTooltip(prev => ({ ...prev, visible: false }))}
                    />
                  )}
                  <span style={{ flex: 1, fontWeight: 700, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1, textAlign: 'center' }}>{tab.name}</span>
                  {aggObj && (
                    <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'monospace', color: ((statusColorsRef.current as any)?.Aggregates?.zam || 'var(--info)'), flexShrink: 0, minWidth: 45, textAlign: 'right' }}>
                      {fmt(aggObj.sum_ilosc || 0)}
                    </span>
                  )}
                </div>
                
                {/* Right side: Wyk and Poz in 2 rows */}
                {aggObj && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0, alignItems: 'flex-end' }}>
                    <span style={{ fontSize: 10, fontWeight: 500, fontFamily: 'monospace', lineHeight: 1, color: ((statusColorsRef.current as any)?.Aggregates?.wykonano || 'var(--ok)') }}>
                      {fmt(aggObj.sum_zrealizowano || 0)}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 500, fontFamily: 'monospace', lineHeight: 1, color: ((statusColorsRef.current as any)?.Aggregates?.pozostalo || 'var(--error)') }}>
                      {fmt((aggObj.ilosc_pozostala != null ? aggObj.ilosc_pozostala : (Number(aggObj.sum_ilosc || 0) - Number(aggObj.sum_zrealizowano || 0))) || 0)}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
          {/* Filter icon moved to footer (removed from tabs bar) */}
        </div>
      )}
      
      {/* Control Panel Row - Above Table (statuses quick toggle) */}
      {selectedRegion && (
          <div
            style={{ 
              background: 'var(--card)',
              borderBottom: '1px solid var(--border)',
              padding: '4px 0',
              minHeight: 32,
              flexShrink: 0
            }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              {/* Wezel buttons container (left-aligned) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', whiteSpace: 'nowrap', flex: 1, minWidth: 0, maxWidth: 'calc(100% - 640px)' }}>
                {(() => {
                  const currentTab = tabs.find(tab => tab.id === activeTab);
                  if (currentTab && currentTab.isRegion) {
                    // Show all wezel buttons for region tab, ordered by wezel.id ASC
                    return [...wezels].sort((a, b) => Number(a.id) - Number(b.id)).map((wezel) => (
                      <button
                        key={wezel.id}
                        onClick={() => {
                          // Click adds a new inline row for this wezel
                          handleAddNewRow(wezel.id, wezel.nazwa);
                        }}
                        onDoubleClick={() => {
                          // Double-click switches to this wezel tab (filter)
                          setActiveTab(`wezel-${wezel.id}`);
                          debouncedLoadZamowienia();
                          // Remember intent to focus ilość when editing starts from this context
                          if (editingId != null) {
                            lastIntentFocusRef.current = { field: 'zamowienie_ilosc', rowId: editingId };
                          }
                        }}
                        disabled={isAddingNew || editingId !== null}
                        style={{
                          background: (isAddingNew || editingId !== null)
                            ? (((statusColorsRef.current as any)?.WezelButton?.disabledBg) || '#f1f4f8')
                            : (((statusColorsRef.current as any)?.WezelButton?.bg) || '#E3B6AA'),
                          color: (isAddingNew || editingId !== null)
                            ? (((statusColorsRef.current as any)?.WezelButton?.disabledText) || '#adb5bd')
                            : (((statusColorsRef.current as any)?.WezelButton?.text) || '#4a5568'),
                          border: `2px solid ${(((statusColorsRef.current as any)?.WezelButton?.border) || '#4a5568')}`,
                          padding: '0 12px',
                          borderRadius: 9999,
                          cursor: isAddingNew || editingId !== null ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          transition: 'all 0.15s ease',
                          fontSize: '12px',
                          height: 24,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 210,
                          minWidth: 210,
                          maxWidth: 210
                        }}
                        onMouseEnter={(e) => {
                          if (isAddingNew || editingId !== null) return;
                          try {
                            const c = (statusColorsRef.current as any)?.WezelButton;
                            e.currentTarget.style.background = c?.hoverBg || '#eef7f3';
                          } catch { e.currentTarget.style.background = '#eef7f3'; }
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.06)';
                        }}
                        onMouseLeave={(e) => {
                          // Always reset styles to avoid sticky hover after state change during hover
                          try { const c = (statusColorsRef.current as any)?.WezelButton; e.currentTarget.style.background = c?.bg || '#E3B6AA'; } catch { e.currentTarget.style.background = 'var(--card)'; }
                            e.currentTarget.style.transform = 'translateY(0px)';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                        }}
                      >
                        {wezel.nazwa}
                      </button>
                    ));
                  } else if (currentTab && !currentTab.isRegion && currentTab.wezelId) {
                    // Show single wezel button for wezel tab
                    const wezel = [...wezels].sort((a, b) => Number(a.id) - Number(b.id)).find(w => w.id === currentTab.wezelId);
                    if (wezel) {
                      return (
                        <button
                          onClick={() => {
                            // Click adds a new inline row for this wezel
                            handleAddNewRow(wezel.id, wezel.nazwa);
                          }}
                          onDoubleClick={() => {
                            // Double-click ensures active tab is the current wezel (filter)
                            setActiveTab(`wezel-${wezel.id}`);
                            debouncedLoadZamowienia();
                            if (editingId != null) {
                              lastIntentFocusRef.current = { field: 'zamowienie_ilosc', rowId: editingId };
                            }
                          }}
                          disabled={isAddingNew || editingId !== null}
                          style={{
                            background: (isAddingNew || editingId !== null)
                              ? (((statusColorsRef.current as any)?.WezelButton?.disabledBg) || '#f1f4f8')
                              : (((statusColorsRef.current as any)?.WezelButton?.bg) || '#E3B6AA'),
                            color: (isAddingNew || editingId !== null)
                              ? (((statusColorsRef.current as any)?.WezelButton?.disabledText) || '#adb5bd')
                              : (((statusColorsRef.current as any)?.WezelButton?.text) || '#495057'),
                            border: `2px solid ${(((statusColorsRef.current as any)?.WezelButton?.border) || '#4a5568')}`,
                            padding: '0 12px',
                            borderRadius: 9999,
                            cursor: isAddingNew || editingId !== null ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                            transition: 'all 0.15s ease',
                            fontSize: '12px',
                            height: 24,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 210,
                            minWidth: 210,
                            maxWidth: 210
                          }}
                          onMouseEnter={(e) => {
                            if (isAddingNew || editingId !== null) return;
                            try { const c = (statusColorsRef.current as any)?.WezelButton; e.currentTarget.style.background = c?.hoverBg || '#eef7f3'; e.currentTarget.style.color = c?.text || '#495057'; } catch { e.currentTarget.style.background = '#eef7f3'; }
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(78, 205, 196, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            // Always reset styles to avoid sticky hover after state change during hover
                            try { const c = (statusColorsRef.current as any)?.WezelButton; e.currentTarget.style.background = c?.bg || '#E3B6AA'; e.currentTarget.style.color = c?.text || '#495057'; } catch { e.currentTarget.style.background = 'var(--card)'; }
                              e.currentTarget.style.transform = 'translateY(0px)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                          }}
                        >
                          {wezel.nazwa}
                        </button>
                      );
                    }
                  }
                  return null;
                })()}
              </div>
              {/* Status Buttons (right-aligned) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexShrink: 0 }}>
                  {[
                    { label: 'Potwierdź', value: 'Potwierdzone', bg: '#90caf9', hoverBg: '#64b5f6', text: '#1565c0', border: '#42a5f5', priority: 1 },
                    { label: 'Realizacja', value: 'Realizacja', bg: '#fff59d', hoverBg: '#fff176', text: '#f57f17', border: '#ffeb3b', priority: 2 },
                    { label: 'Zakończ', value: 'Zakonczone', bg: '#a5d6a7', hoverBg: '#81c784', text: '#2e7d32', border: '#66bb6a', priority: 3 },
                    { label: 'Anuluj', value: 'Anulowane', bg: '#bdbdbd', hoverBg: '#9e9e9e', text: '#424242', border: '#757575', priority: 4 }
                  ].map((action) => (
                    <button
                      key={action.value}
                      onClick={() => {
                        if (selectedRowId === null) {
                          alert('Brak zaznaczonego wiersza');
                          return;
                        }
                        
                        // Znajdź aktualne zamówienie
                        const currentZamowienie = zamowienia.find(z => z.id === selectedRowId);
                        if (!currentZamowienie) {
                          alert('Nie znaleziono zamówienia');
                          return;
                        }
                        
                        // Toggle status based on current state
                        let statusUpdate: any = {};
                        
                        if (action.value === 'Potwierdzone') {
                          statusUpdate = {
                            zamowienie_potwierdzone: !currentZamowienie.zamowienie_potwierdzone,
                            zamowienie_realizacja: currentZamowienie.zamowienie_realizacja,
                            zamowienie_zakonczone: currentZamowienie.zamowienie_zakonczone,
                            zamowienie_anulowane: currentZamowienie.zamowienie_anulowane
                          };
                        } else if (action.value === 'Realizacja') {
                          statusUpdate = {
                            zamowienie_potwierdzone: currentZamowienie.zamowienie_potwierdzone,
                            zamowienie_realizacja: !currentZamowienie.zamowienie_realizacja,
                            zamowienie_zakonczone: currentZamowienie.zamowienie_zakonczone,
                            zamowienie_anulowane: currentZamowienie.zamowienie_anulowane
                          };
                        } else if (action.value === 'Zakonczone') {
                          statusUpdate = {
                            zamowienie_potwierdzone: currentZamowienie.zamowienie_potwierdzone,
                            zamowienie_realizacja: currentZamowienie.zamowienie_realizacja,
                            zamowienie_zakonczone: !currentZamowienie.zamowienie_zakonczone,
                            zamowienie_anulowane: currentZamowienie.zamowienie_anulowane
                          };
                        } else if (action.value === 'Anulowane') {
                          statusUpdate = {
                            zamowienie_potwierdzone: currentZamowienie.zamowienie_potwierdzone,
                            zamowienie_realizacja: currentZamowienie.zamowienie_realizacja,
                            zamowienie_zakonczone: currentZamowienie.zamowienie_zakonczone,
                            zamowienie_anulowane: !currentZamowienie.zamowienie_anulowane
                          };
                        }
                        
                        if (Object.keys(statusUpdate).length > 0) {
                          handleStatusChange(selectedRowId, action.value, statusUpdate);
                        }
                      }}
                      style={{
                        background: (statusColorsRef.current as any)[action.value].bg,
                        color: (statusColorsRef.current as any)[action.value].text,
                        border: `1px solid ${(statusColorsRef.current as any)[action.value].border}`,
                        padding: '0 12px',
                        borderRadius: 9999,
                        cursor: 'pointer',
                        fontWeight: 600,
                        transition: 'all 0.2s ease',
                        fontSize: '12px',
                        height: 24,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 65,
                        minWidth: 65,
                        maxWidth: 65
                      }}
                      onMouseEnter={(e) => {
                        const c = (statusColorsRef.current as any)[action.value];
                        e.currentTarget.style.background = c.hoverBg;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        const c = (statusColorsRef.current as any)[action.value];
                        e.currentTarget.style.background = c.bg;
                        e.currentTarget.style.transform = 'translateY(0px)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
              </div>

              {/* Prawa strona: menu filtrowania + akcje */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ position: 'relative' }}>
                  {(() => {
                    const isDefaultStatuses = (filterStatus.potwierdzone !== false) && (filterStatus.realizacja !== false) && (filterStatus.zakonczone !== false) && (filterStatus.anulowane !== false);
                    const isDefaultTimes = !normalizeTimeInput(filterFrom) && !normalizeTimeInput(filterTo);
                    const isActive = !(isDefaultStatuses && isDefaultTimes);
                    return (
                      <details>
                        <summary style={{ display: 'none' }} />
                        <div style={{ position: 'fixed', right: 12, top: 60, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', zIndex: 1000, minWidth: 320, maxHeight: '70vh', overflow: 'auto' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridAutoRows: 'minmax(30px, auto)', gap: 6 }}>
                            {[ 
                              { key: 'potwierdzone', label: 'Potwierdzone', color: '#0d47a1' },
                              { key: 'realizacja', label: 'Realizacja', color: '#f57f17' },
                              { key: 'zakonczone', label: 'Zakonczone', color: '#2e7d32' },
                              { key: 'anulowane', label: 'Anulowane', color: '#424242' },
                            ].map(s => {
                              const enabled = (filterStatus as any)[s.key] !== false; // domyślnie true
                              return (
                                <button 
                                  key={s.key}
                                  onClick={() => setFilterStatus(prev => ({ ...prev, [s.key]: enabled ? false : true }))}
                                  style={{
                                    width: '100%',
                                    height: 28,
                                    padding: '4px 8px',
                                    borderRadius: 9999,
                                    fontSize: 8,
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    border: enabled ? `1px solid ${s.color}` : '1px solid var(--border)',
                                    background: enabled ? 'var(--accent)' : 'var(--card)',
                                    color: enabled ? s.color : 'var(--muted)',
                                    cursor: 'pointer'
                                  }}
                                >{s.label}</button>
                              );
                            })}
                            {/* Time filters disabled per request */}
                          </div>
                        </div>
                      </details>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={handleDeleteCurrent}
                    disabled={editingId === null && selectedRowId === null}
                    title="Usuń zaznaczenie"
                    aria-label="Usuń zamówienie"
                    style={{
                      background: '#ffffff',
                      border: '1px solid var(--border)',
                      borderRadius: 9999,
                      width: 26,
                      height: 26,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: (editingId === null && selectedRowId === null) ? '#bdbdbd' : '#c0392b',
                      cursor: (editingId === null && selectedRowId === null) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <TrashIcon size={14} strokeWidth={2} />
                  </button>
                  <div ref={exportRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <button
                      onClick={() => setFilterOpen(o => !o)}
                      title="Filtruj"
                      style={{
                        background: '#ffffff',
                        border: '1px solid var(--border)',
                        borderRadius: '9999px',
                        width: 26,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: isFilterActive ? '#d32f2f' : '#374151'
                      }}
                    >
                      <FilterIcon size={14} strokeWidth={2} />
                    </button>
                    {filterOpen && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #e6ebf2',
                        borderRadius: '8px',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                        padding: '10px',
                        zIndex: 1000,
                        minWidth: 340,
                        maxHeight: '70vh',
                        overflow: 'auto'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridAutoRows: 'minmax(36px, auto)', gap: 8 }}>
                          {[
                            { key: 'potwierdzone', label: 'Potwierdzone', color: '#0d47a1' },
                            { key: 'realizacja', label: 'Realizacja', color: '#f57f17' },
                            { key: 'zakonczone', label: 'Zakończone', color: '#2e7d32' },
                            { key: 'anulowane', label: 'Anulowane', color: '#424242' },
                          ].map(s => {
                            const enabled = (filterStatus as any)[s.key] !== false;
                            return (
                              <button 
                                key={s.key}
                                onClick={() => setFilterStatus(prev => ({ ...prev, [s.key]: enabled ? false : true }))}
                                style={{
                                  width: '100%', height: 36, padding: '6px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600, textAlign: 'center',
                                  border: enabled ? `1px solid ${s.color}` : '1px solid #e0e0e0',
                                  background: enabled ? '#eef5ff' : '#ffffff', color: enabled ? s.color : '#757575', cursor: 'pointer'
                                }}
                              >{s.label}</button>
                            );
                          })}
                          <div style={{ width: '100%' }}>
                            <label style={{ display: 'block', fontSize: 11, color: '#616161', marginBottom: 4 }}>Godz. od</label>
                            <input type="time" value={normalizeTimeInput(filterFrom)} onChange={(e) => setFilterFrom(e.target.value)} style={{ width: '100%', height: 36, padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 9999, fontSize: 12 }} />
                          </div>
                          <div style={{ width: '100%' }}>
                            <label style={{ display: 'block', fontSize: 11, color: '#616161', marginBottom: 4 }}>Godz. do</label>
                            <input type="time" value={normalizeTimeInput(filterTo)} onChange={(e) => setFilterTo(e.target.value)} style={{ width: '100%', height: 36, padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: 9999, fontSize: 12 }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => setExportOpen(o => !o)}
                      title="Eksportuj"
                      style={{
                        background: '#ffffff',
                        border: '1px solid var(--border)',
                        borderRadius: '9999px',
                        width: 26,
                        height: 26,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#374151'
                      }}
                    >
                      <DownloadIcon size={14} strokeWidth={2} />
                    </button>
                    {exportOpen && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #e6ebf2',
                        borderRadius: '4px',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
                        padding: '6px',
                        zIndex: 10
                      }}>
                        <button onClick={() => handleExport('csv')} style={{ display: 'block', fontSize: '12px', padding: '6px 10px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: '#374151' }}>CSV</button>
                        <button onClick={() => handleExport('tsv')} style={{ display: 'block', fontSize: '12px', padding: '6px 10px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: '#374151' }}>TSV</button>
                        <button onClick={() => handleExport('json')} style={{ display: 'block', fontSize: '12px', padding: '6px 10px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: '#374151' }}>JSON</button>
                        <button onClick={() => handleExport('pdf')} style={{ display: 'block', fontSize: '12px', padding: '6px 10px', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', color: '#374151' }}>PDF</button>
                      </div>
                    )}
                  </div>
                  {qtyRowId != null && qtyState.error && (
                    <div style={{ fontSize: '11px', color: '#d32f2f', fontWeight: 600, marginLeft: 6 }}>
                      {qtyState.error}
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>
      )}
        
      {/* Table - scrolls in main HomePage container */}
      <table ref={tableRef} style={{ 
        width: '100%', 
        borderCollapse: 'separate',
        borderSpacing: 0,
        lineHeight: '1',
        margin: '0',
        padding: '0',
        tableLayout: 'fixed',
        paddingBottom: '120px'
      }}>
          <thead>
            <tr
              key="header-row"
              ref={headerRowRef}
              style={{
                background: 'var(--card)'
              }}
              onContextMenu={(e) => { e.preventDefault(); setHeaderMenu({ open: true, x: e.clientX, y: e.clientY }); }}
            >
              <th style={{
                background: 'var(--card)',
                padding: '0px 2px',
                textAlign: 'center',
                fontWeight: '500',
                color: 'var(--muted)',
                fontSize: '11px',
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '25px',
                maxWidth: '25px'
              }}>
                #
              </th>
              <th style={{
                background: 'var(--card)',
                padding: '0px 2px',
                textAlign: 'center',
                fontWeight: '500',
                color: 'var(--muted)',
                fontSize: '11px',
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '25px',
                maxWidth: '25px'
              }}>
                
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '20px',
                maxWidth: '20px'
              }}>
                KP
              </th>
              
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '120px',
                maxWidth: '120px'
              }}>
                Uwagi 1
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '40px',   
                maxWidth: '40px'
              }}>
                Num.
              </th>
                               <th style={{
                  position: 'sticky',
                  top: stickyHeaderTop,
                  zIndex: 20,
                  background: 'var(--card)',
                   padding: '0px 2px',
                   textAlign: 'center',
                   fontWeight: '500',
                   color: 'var(--muted)',
                   fontSize: '11px',
                   borderBottom: '1px solid var(--border)',
                   borderRight: '1px solid #e0e0e0',
                   width: '38px',
                   maxWidth: '38px'
                 }}>
                   Godz Od
                 </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '38px',
                maxWidth: '38px'
              }}>
                Do
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '150px',
                maxWidth: '150px'
              }}>
                Klient
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '75px',
                maxWidth: '75px'
              }}>
                NIP
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                 width: '125px',
                 maxWidth: '125px'
              }}>
                Budowa
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '80px',
                maxWidth: '80px'
              }}>
              Recepta
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '50px',
                maxWidth: '50px'
              }}>
                Kons.
              </th>
                              <th style={{ 
                 position: 'sticky',
                 top: stickyHeaderTop,
                 zIndex: 20,
                 background: 'var(--card)', 
                  padding: '0px 2px', 
                  textAlign: 'center', 
                  fontWeight: '500', 
                  color: 'var(--muted)', 
                  fontSize: '11px', 
                  borderBottom: '1px solid var(--border)',
                  borderRight: '1px solid var(--border)',
                  width: '82px',
                  maxWidth: '82px'
                }}>
                  Telefon
                </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '25px',
                maxWidth: '25px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Ok
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '52px',
                maxWidth: '52px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Ilość
              </th>

                              <th style={{
                 position: 'sticky',
                 top: stickyHeaderTop,
                 zIndex: 20,
                 background: 'var(--card)',
                  padding: '0px 2px',
                  textAlign: 'center',
                  fontWeight: '500',
                  color: 'var(--muted)',
                  fontSize: '11px',
                  borderBottom: '1px solid var(--border)',
                  borderRight: '1px solid var(--border)',
                  width: '52px',
                  maxWidth: '52px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  Wysł.
                </th>

              {/* Dynamiczne kolumny węzłów */}
              {wezels.map((wezel) => (
                <th key={`wezel-${wezel.id}`} style={{ 
                  position: 'sticky',
                  top: stickyHeaderTop,
                  zIndex: 20,
                  background: 'var(--card)', 
                  padding: '0px 2px', 
                  textAlign: 'center', 
                  fontWeight: '500', 
                  color: 'var(--muted)', 
                  fontSize: '11px', 
                  borderBottom: '1px solid var(--border)',
                  borderRight: '1px solid var(--border)',
                  width: '52px',
                  maxWidth: '52px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {wezel.nazwa}
                </th>
              ))}
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '60px',
                maxWidth: '60px'
              }}>
                Pompa
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '25px',
                maxWidth: '25px'
              }}>
                Nr p.
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '60px',
                maxWidth: '60px'
              }}>
                Operator
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '50px',
                maxWidth: '50px'
              }}>
                Transport
              </th>
              <th style={{ 
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 20,
                background: 'var(--card)', 
                padding: '0px 2px', 
                textAlign: 'center', 
                fontWeight: '500', 
                color: 'var(--muted)', 
                fontSize: '11px', 
                borderBottom: '1px solid var(--border)',
                borderRight: '1px solid var(--border)',
                width: '125px',
                maxWidth: '125px'
              }}>
                Uwagi 2
              </th>
            </tr>
            {/* Filters row (client-side). Debounced (400ms) onChange */}
            <tr>
              {/* index */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }} />
              {/* expand */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }} />
              {/* KP */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <select value={columnFilters['zamowienie_gotowka'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, zamowienie_gotowka: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, zamowienie_gotowka: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['zamowienie_gotowka'] || '') ? 'var(--warn)' : 'var(--border)', borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }}>
                  <option value="">Wszystko</option>
                  <option value="true">Tak</option>
                  <option value="false">Nie</option>
                </select>
              </th>
              {/* Uwagi 1 */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['zamowienie_uwagi1'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, zamowienie_uwagi1: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, zamowienie_uwagi1: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: DATA_FONT, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['zamowienie_uwagi1'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['zamowienie_uwagi1'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Numer */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['zamowienie_numer'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, zamowienie_numer: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, zamowienie_numer: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: DATA_FONT, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['zamowienie_numer'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['zamowienie_numer'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Godz od */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }} />
              {/* Do */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }} />
              {/* Klient */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['klient_nazwa'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, klient_nazwa: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, klient_nazwa: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['klient_nazwa'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['klient_nazwa'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* NIP */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['klient_nip'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, klient_nip: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, klient_nip: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['klient_nip'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['klient_nip'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Budowa */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['klient_budowa'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, klient_budowa: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, klient_budowa: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['klient_budowa'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['klient_budowa'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Recepta */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['beton_klasa'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, beton_klasa: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, beton_klasa: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['beton_klasa'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['beton_klasa'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Kons. */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['beton_konsystencja'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, beton_konsystencja: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, beton_konsystencja: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['beton_konsystencja'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['beton_konsystencja'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Telefon */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['klient_telefon'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, klient_telefon: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, klient_telefon: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['klient_telefon'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['klient_telefon'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Ok */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['zamowienie_okolo'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, zamowienie_okolo: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, zamowienie_okolo: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters['zamowienie_okolo'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['zamowienie_okolo'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Ilość */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['zamowienie_ilosc'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, zamowienie_ilosc: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, zamowienie_ilosc: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, textAlign: 'right', border: '1px solid', borderColor: (columnFilters['zamowienie_ilosc'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['zamowienie_ilosc'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Wysłano */}
              <th style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                <input value={columnFilters['zamowienie_zrealizowano'] || ''} onChange={(e) => {
                  const v = e.target.value; setColumnFilters(prev => ({ ...prev, zamowienie_zrealizowano: v }));
                  if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                  filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, zamowienie_zrealizowano: v })), 400);
                }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, textAlign: 'right', border: '1px solid', borderColor: (columnFilters['zamowienie_zrealizowano'] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters['zamowienie_zrealizowano'] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
              </th>
              {/* Dynamiczne węzły: pusta komórka – filtrowanie per węzeł można dodać analogicznie */}
              {wezels.map(() => (
                <th key={Math.random()} style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }} />
              ))}
              {/* Pompa/Nr p./Operator/Transport/Uwagi2 – uproszczone tekstowe */}
              {['pompa_nazwa','pompa_numer','pompa_kierowca','zamowienie_transport','zamowienie_uwagi2'].map((k) => (
                <th key={k} style={{ background: 'var(--card)', padding: '2px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
                  <input value={columnFilters[k] || ''} onChange={(e) => {
                    const v = e.target.value; setColumnFilters(prev => ({ ...prev, [k]: v }));
                    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
                    filterDebounceRef.current = setTimeout(() => setAppliedFilters(prev => ({ ...prev, [k]: v })), 400);
                  }} style={{ width: '100%', height: 18, fontSize: 10, fontWeight: 400, border: '1px solid', borderColor: (columnFilters[k] || '') ? 'var(--warn)' : 'var(--border)', borderLeft: (columnFilters[k] || '') ? '3px solid var(--warn)' : undefined, borderRadius: 6, background: 'var(--card)', color: 'var(--text)' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody style={{ lineHeight: '1', margin: '0', padding: '0' }} onContextMenu={handleContextMenuEmpty}>
            {rowsForTab.filter((z) => {
              // Apply client-side filters
              const f = appliedFilters;
              const like = (v: any, q?: string) => {
                if (!q) return true; const s = String(v || '').toLowerCase(); return s.includes(String(q).toLowerCase());
              };
              const eqBool = (v: any, q?: string) => {
                if (q === undefined || q === null || q === '') return true; const b = String(q) === 'true'; return Boolean(v) === b;
              };
              const timeOk = (v: any, from?: string, to?: string) => {
                if (!from && !to) return true; const s = String(v || ''); if (from && s < from) return false; if (to && s > to) return false; return true;
              };
              const numLike = (v: any, q?: string) => {
                if (!q) return true; return String(v ?? '').includes(q);
              };
              // Always include draft (temporary) rows regardless of active filters
              if (isTempId(Number(z.id))) return true;
              if (!eqBool(z.zamowienie_gotowka, f['zamowienie_gotowka'])) return false;
              if (!like(z.zamowienie_uwagi1, f['zamowienie_uwagi1'])) return false;
              if (!numLike(z.zamowienie_numer, f['zamowienie_numer'])) return false;
              if (!timeOk(z.zamowienie_godzinaod, f['zamowienie_godzinaod'] || undefined, undefined)) return false;
              if (!timeOk(z.zamowienie_godzinado, undefined, f['zamowienie_godzinado'] || undefined)) return false;
              if (!like(z.klient_nazwa, f['klient_nazwa'])) return false;
              if (!like(z.klient_nip, f['klient_nip'])) return false;
              if (!like(z.klient_budowa, f['klient_budowa'])) return false;
              if (!like(z.klient_telefon, f['klient_telefon'])) return false;
              if (!like(z.zamowienie_okolo, f['zamowienie_okolo'])) return false;
              if (!numLike(z.zamowienie_ilosc, f['zamowienie_ilosc'])) return false;
              if (!numLike((z as any).zamowienie_zrealizowano, f['zamowienie_zrealizowano'])) return false;
              if (!like(z.beton_klasa, f['beton_klasa'])) return false;
              if (!like(z.beton_konsystencja, f['beton_konsystencja'])) return false;
              if (!like(z.pompa_nazwa, f['pompa_nazwa'])) return false;
              if (!like(z.pompa_numer, f['pompa_numer'])) return false;
              if (!like(z.pompa_kierowca, f['pompa_kierowca'])) return false;
              if (!like((z as any).zamowienie_transport, f['zamowienie_transport'])) return false;
              if (!like(z.zamowienie_uwagi2, f['zamowienie_uwagi2'])) return false;
              return true;
            }).length === 0 ? (
              <tr key="no-data-row" onContextMenu={handleContextMenuEmpty}>
                <td colSpan={23} style={{ 
                  textAlign: 'center', 
                  padding: '20px', 
                  color: 'var(--muted)',
                  borderBottom: '1px solid var(--border)'
                }}>
                  Brak zamówień dla wybranej daty
                </td>
              </tr>
            ) : (
              rowsForTab.filter((z) => {
                const f = appliedFilters;
                const like = (v: any, q?: string) => { if (!q) return true; const s = String(v || '').toLowerCase(); return s.includes(String(q).toLowerCase()); };
                const eqBool = (v: any, q?: string) => { if (!q && q !== 'false') return true; const b = String(q) === 'true'; return Boolean(v) === b; };
                const timeOk = (v: any, from?: string, to?: string) => { if (!from && !to) return true; const s = String(v || ''); if (from && s < from) return false; if (to && s > to) return false; return true; };
                const numLike = (v: any, q?: string) => { if (!q) return true; return String(v ?? '').includes(q); };
                // Always include draft (temporary) rows regardless of active filters
                if (isTempId(Number(z.id))) return true;
                if (!eqBool(z.zamowienie_gotowka, f['zamowienie_gotowka'])) return false;
                if (!like(z.zamowienie_uwagi1, f['zamowienie_uwagi1'])) return false;
                if (!numLike(z.zamowienie_numer, f['zamowienie_numer'])) return false;
                if (!timeOk(z.zamowienie_godzinaod, f['zamowienie_godzinaod'] || undefined, undefined)) return false;
                if (!timeOk(z.zamowienie_godzinado, undefined, f['zamowienie_godzinado'] || undefined)) return false;
                if (!like(z.klient_nazwa, f['klient_nazwa'])) return false;
                if (!like(z.klient_nip, f['klient_nip'])) return false;
                if (!like(z.klient_budowa, f['klient_budowa'])) return false;
                if (!like(z.klient_telefon, f['klient_telefon'])) return false;
                if (!like(z.zamowienie_okolo, f['zamowienie_okolo'])) return false;
                if (!numLike(z.zamowienie_ilosc, f['zamowienie_ilosc'])) return false;
                if (!numLike((z as any).zamowienie_zrealizowano, f['zamowienie_zrealizowano'])) return false;
                if (!like(z.beton_klasa, f['beton_klasa'])) return false;
                if (!like(z.beton_konsystencja, f['beton_konsystencja'])) return false;
                if (!like(z.pompa_nazwa, f['pompa_nazwa'])) return false;
                if (!like(z.pompa_numer, f['pompa_numer'])) return false;
                if (!like(z.pompa_kierowca, f['pompa_kierowca'])) return false;
                if (!like((z as any).zamowienie_transport, f['zamowienie_transport'])) return false;
                if (!like(z.zamowienie_uwagi2, f['zamowienie_uwagi2'])) return false;
                return true;
              }).map((zamowienie, rowIndex) => {
                const isEditing = editingId === zamowienie.id;
                const cellBlink = blinkMap[zamowienie.id] || new Set<string>();
                return (
                  <React.Fragment key={`frag-${zamowienie.id}`}>
                  <tr 
                    key={zamowienie.id}
                    data-zamowienie-id={zamowienie.id}
                    style={{ 
                      height: DATA_ROW_HEIGHT, 
                      lineHeight: '1', 
                      margin: '0', 
                      padding: '0', 
                      cursor: 'pointer',
                      outline: selectedRowId === zamowienie.id ? '2px solid var(--accent)' : 'none',
                      outlineOffset: selectedRowId === zamowienie.id ? '-2px' : '0',
                      fontStyle: selectedRowId === zamowienie.id ? 'italic' : 'normal',
                      background: (() => {
                        const colors = statusColorsRef.current as any;
                        if (zamowienie.zamowienie_anulowane) return (colors?.Anulowane?.rowBg) || 'var(--muted)';
                        if (zamowienie.zamowienie_zakonczone) return (colors?.Zakonczone?.rowBg) || 'var(--ok)';
                        if (zamowienie.zamowienie_realizacja) return (colors?.Realizacja?.rowBg) || 'var(--warn)';
                        if (zamowienie.zamowienie_potwierdzone) return (colors?.Potwierdzone?.rowBg) || 'var(--info)';
                        return '#ffffff';
                      })(),
                      color: hasClientFinancialProblem(zamowienie) ? 'var(--error)' : 'inherit'
                    }}
                    onClick={() => {
                      // If we're editing a different row, cancel the edit first
                      if (editingId !== null && editingId !== zamowienie.id) {
                        // If we're canceling a new row (isAddingNew is true), remove it from the table
                        if (isAddingNew) {
                          setZamowienia(prev => prev.filter(z => z.id !== editingId));
                        }
                        setEditingId(null);
                        setEditData({});
                        setIsAddingNew(false);
                        // Reset quantity-related UI/reducer state
                        resetQtyUiState();
                      }
                      setSelectedRowId(zamowienie.id);
                    }}
                    onDoubleClick={() => {
                      // Block double-click when adding new row or editing another row
                      if (isAddingNew || (editingId !== null && editingId !== zamowienie.id)) {
                        return;
                      }
                      handleEdit(zamowienie);
                    }}
                    onContextMenu={(e) => handleContextMenu(e, zamowienie.id)}
                  >
                    {/* Row index */}
                    <td className="text-cell" style={{ padding: '0px 4px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--muted)', fontSize: '9px' }}>
                      {rowIndex + 1}
                    </td>

                    {/* Expand toggle */}
                    <td className="text-cell" style={{ padding: '0px 4px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                      <button onClick={(e) => { e.stopPropagation(); toggleExpand(zamowienie); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} title="Pokaż WZ">
                        {expanded[zamowienie.id] ? '▾' : '▸'}
                      </button>
                    </td>

                    {/* Gotówka */}
                    <td className="text-cell" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'center', width: '20px', maxWidth: '20px', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_gotowka') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_gotowka') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="checkbox"
                          checked={editData.zamowienie_gotowka || false}
                          onChange={(e) => handleInputChange('zamowienie_gotowka', e.target.checked.toString())}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          style={{
                            width: '16px',
                            height: '16px',
                            display: 'block',
                            margin: '0 auto'
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: DATA_FONT }}>
                          {zamowienie.zamowienie_gotowka ? '✓' : ''}
                        </span>
                      )}
                    </td>
                    
                    
                    
                    {/* Uwagi 1 */}
                    <td className="text-cell" data-field="zamowienie_uwagi1" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_uwagi1') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_uwagi1') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.zamowienie_uwagi1 || ''}
                          onChange={(e) => handleInputChange('zamowienie_uwagi1', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          style={{
                            width: '100%',
                            padding: '2px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: '3px',
                            fontSize: DATA_FONT,
                            fontFamily: 'inherit',
                            height: DATA_INPUT_HEIGHT,
                            boxSizing: 'border-box',
                            background: 'var(--card)'
                          }}
                        />
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.zamowienie_uwagi1 || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT
                          }}
                        >
                          {zamowienie.zamowienie_uwagi1}
                        </div>
                      )}
                    </td>
                    
                    {/* Numer */}
                    <td className="text-cell" data-field="zamowienie_numer" style={{ padding: '0px 12px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_numer') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_numer') ? 'var(--ok)' : undefined }}>
                      <span style={{ fontSize: DATA_FONT }}>
                        {zamowienie.zamowienie_numer ? zamowienie.zamowienie_numer.toString() : ''}
                      </span>
                    </td>
                    
                    {/* Godzina od */}
                    <td className="text-cell" data-field="zamowienie_godzinaod" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', width: '58px', textAlign: 'center', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_godzinaod') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_godzinaod') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="time"
                          value={editData.zamowienie_godzinaod || ''}
                          onChange={(e) => handleInputChange('zamowienie_godzinaod', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          step="60"
                          style={{
                            width: '100%',
                            padding: '2px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: '3px',
                            fontSize: DATA_FONT,
                            fontFamily: 'inherit',
                            height: DATA_INPUT_HEIGHT,
                            boxSizing: 'border-box',
                            textAlign: 'center',
                            background: 'var(--card)'
                          }}
                          className="no-calendar-icon"
                        />
                      ) : (
                        <span style={{ fontSize: DATA_FONT }}>
                          {formatTimeDisplay(zamowienie.zamowienie_godzinaod)}
                        </span>
                      )}
                    </td>
                    
                    {/* Godzina do */}
                    <td className="text-cell" data-field="zamowienie_godzinado" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', width: '58px', textAlign: 'center', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_godzinado') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_godzinado') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="time"
                          value={editData.zamowienie_godzinado || ''}
                          onChange={(e) => handleInputChange('zamowienie_godzinado', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          step="60"
                          style={{
                            width: '100%',
                            padding: '2px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: '3px',
                            fontSize: DATA_FONT,
                            fontFamily: 'inherit',
                            height: DATA_INPUT_HEIGHT,
                            boxSizing: 'border-box',
                            textAlign: 'center',
                            background: 'var(--card)'
                          }}
                          className="no-calendar-icon"
                        />
                      ) : (
                        <span style={{ fontSize: DATA_FONT }}>
                          {formatTimeDisplay(zamowienie.zamowienie_godzinado)}
                        </span>
                      )}
                    </td>
                    
                    {/* Klient */}
                    <td className="text-cell" data-field="klient_nazwa" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('klient_nazwa') ? 'var(--warn)' : 'transparent', color: cellBlink.has('klient_nazwa') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <div style={{ width: '100%', padding: 0, margin: 0 }}>
                          <AutocompleteInput
                            value={editData.klient_nazwa || ''}
                            onChange={(value) => handleInputChange('klient_nazwa', value)}
                            onClientSelect={handleClientSelect}
                            onSelectionComplete={handleClientSelectionComplete}
                            onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                            onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                            fieldType="nazwa"
                            dataField="klient_nazwa"
                            dataRowId={zamowienie.id}
                            className="cell-input"
                            style={{ width: '100%', height: DATA_INPUT_HEIGHT, boxSizing: 'border-box' }}
                          />
                        </div>
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.klient_nazwa || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            color: getClientFinancialColor(zamowienie)
                          }}
                        >
                          {zamowienie.klient_nazwa}
                        </div>
                      )}
                    </td>
                    
                    {/* NIP */}
                    <td className="text-cell" data-field="klient_nip" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('klient_nip') ? 'var(--warn)' : 'transparent', color: cellBlink.has('klient_nip') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <div style={{ width: '100%', padding: 0, margin: 0 }}>
                          <AutocompleteInput
                            value={editData.klient_nip || ''}
                            onChange={(value) => handleInputChange('klient_nip', value)}
                            onClientSelect={handleClientSelect}
                            onSelectionComplete={handleClientSelectionComplete}
                            onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                            onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                            fieldType="nip"
                            dataField="klient_nip"
                            dataRowId={zamowienie.id}
                            className="cell-input"
                            style={{ width: '100%', height: DATA_INPUT_HEIGHT, boxSizing: 'border-box' }}
                          />
                        </div>
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            const hasNip = String(zamowienie.klient_nip || '').trim() !== '';
                            const canSeeFinancial = hasRole('Administrator') || hasRole('Ksiegowosc');
                            if (hasNip && canSeeFinancial) {
                              const content = getClientFinancialTooltipContent(zamowienie);
                              if (content) {
                                setTooltip({ visible: true, text: '', content, x: r.right + 8, y: r.top });
                              }
                            } else if (zamowienie.klient_nip) {
                              setTooltip({ visible: true, text: String(zamowienie.klient_nip), x: r.right + 8, y: r.top });
                            }
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            color: getClientFinancialColor(zamowienie)
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {zamowienie.klient_nip}
                          </span>
                          {/* Show ZGODA icon if zgoda=true (takes precedence) */}
                          {zamowienie.klient_zgoda === true && (
                            <span style={{ color: 'var(--ok)', fontWeight: 600, fontSize: 11, flexShrink: 0 }} title="Zgoda">✅</span>
                          )}
                          {/* Show BLOKADA icon only if blokada=true AND zgoda=false */}
                          {(zamowienie as any).klient_blokada === true && zamowienie.klient_zgoda !== true && (
                            <span style={{ color: 'var(--error)', fontWeight: 600, fontSize: 11, flexShrink: 0 }} title="Blokada">⛔</span>
                          )}
                        </div>
                      )}
                    </td>
                    
                    {/* Budowa */}
                    <td className="text-cell" data-field="klient_budowa" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('klient_budowa') ? 'var(--warn)' : 'transparent', color: cellBlink.has('klient_budowa') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <div style={{ flex: 1 }}>
                            <AutocompleteInput
                              value={editData.klient_budowa || ''}
                              onChange={(value) => handleInputChange('klient_budowa', value)}
                              onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                              onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                              onSelectionComplete={() => {
                              }}
                              onBlurInput={async (val) => {
                                try {
                                  // Skip geocode if budowa is empty
                                  if (!val || !val.trim()) return;
                                  
                                  const regionId = selectedRegion ?? (zamowienia.find(z=>z.id===zamowienie.id)?.region_id);
                                  const leadingWezel = editData.zamowienie_wezel_wiodacy ?? zamowienie.zamowienie_wezel_wiodacy;
                                  const q = new URLSearchParams({
                                    region_id: String(regionId||''),
                                    klient_nazwa: String(editData.klient_nazwa||zamowienie.klient_nazwa||''),
                                    klient_budowa: String(val||''),
                                    wezel_id: String(leadingWezel||'')
                                  });
                                  const resp = await authenticatedFetch(`/api/geocode/resolve?${q.toString()}`, { credentials: 'include' });
                                  if (resp.ok) {
                                    const j = await resp.json();
                                    console.log('[Geocode][resolve] FE result', j);
                                    if (j && j.success && j.lat && j.lon) {
                                      // Optional: store for save payload if needed in future
                                      (window as any).__last_geocode__ = { lat: j.lat, lon: j.lon, formatted_address: j.formatted_address, source: j.source };
                                      // Trigger map refresh to show red/green pin as soon as cache fills
                                      window.dispatchEvent(new CustomEvent('map:refresh', { detail: { date: selectedDate, region_id: regionId } }));
                                    } else {
                                      // no result → show soft warning in cell
                                      try {
                                        setBlinkMap(prev => {
                                          const next = { ...prev } as typeof prev;
                                          const current = prev[zamowienie.id] ? new Set(prev[zamowienie.id]) : new Set<string>();
                                          current.add('klient_budowa');
                                          next[zamowienie.id] = current;
                                          return next;
                                        });
                                        setTimeout(() => {
                                          setBlinkMap(prev => {
                                            const next = { ...prev } as typeof prev;
                                            next[zamowienie.id] = new Set<string>();
                                            return next;
                                          });
                                        }, 1200);
                                      } catch {}
                                    }
                                  }
                                } catch (e) { console.warn('[Geocode][resolve] FE error', e); }
                              }}
                              fieldType="budowa"
                              dataField="klient_budowa"
                              dataRowId={zamowienie.id}
                              clientName={editData.klient_nazwa}
                              clientNip={editData.klient_nip}
                              style={{}}
                              className="cell-input"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openMapModal(zamowienie.id); }}
                            title="Otwórz mapę"
                            style={{
                              border: '1px solid var(--border)',
                              background: 'var(--card)',
                              borderRadius: '3px',
                              width: '22px',
                              height: DATA_INPUT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            🗺
                          </button>
                        </div>
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.klient_budowa || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            padding: '2px 8px'
                          }}
                        >
                          {zamowienie.klient_budowa}
                        </div>
                      )}
                    </td>
                    
                    {/* Klasa */}
                    <td data-field="beton_klasa" style={{ 
                      padding: '0px', 
                      whiteSpace: 'nowrap', 
                      borderRight: '1px solid var(--border)', 
                      borderBottom: '1px solid var(--border)', 
                      transition: 'background-color 300ms, color 300ms', 
                      backgroundColor: cellBlink.has('beton_klasa') 
                        ? 'var(--warn)' 
                        : betonKlasaConfirmed.has(zamowienie.id)
                          ? (betonKlasaConfirmed.get(zamowienie.id) === true 
                             ? 'rgba(22, 163, 74, 0.25)' // dark green for confirmed
                             : 'rgba(245, 158, 11, 0.25)') // dark orange for unconfirmed
                          : 'transparent',
                      color: cellBlink.has('beton_klasa') ? 'var(--ok)' : undefined 
                    }}>
                      {isEditing ? (
                        <div style={{ width: '100%' }}>
                          <AutocompleteInput
                            value={editData.beton_klasa || ''}
                            onChange={(value) => handleInputChange('beton_klasa', value)}
                            onClientSelect={(suggestion) => {
                              // Store is_confirmed status when user selects a recept
                              if (suggestion.is_confirmed !== null && suggestion.is_confirmed !== undefined) {
                                setBetonKlasaConfirmed(prev => {
                                  const newMap = new Map(prev);
                                  newMap.set(zamowienie.id, suggestion.is_confirmed === true);
                                  return newMap;
                                });
                              }
                            }}
                            onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                            onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                            fieldType="klasa"
                            dataField="beton_klasa"
                            dataRowId={zamowienie.id}
                            regionId={selectedRegion || editData.region_id || zamowienie.region_id}
                            wezelId={editData.zamowienie_wezel_wiodacy ?? zamowienie.zamowienie_wezel_wiodacy}
                            clientName={editData.klient_nazwa || zamowienie.klient_nazwa}
                            clientNip={editData.klient_nip || zamowienie.klient_nip}
                            className="cell-input"
                            style={{ width: '100%', height: DATA_INPUT_HEIGHT, boxSizing: 'border-box', fontSize: DATA_FONT }}
                          />
                        </div>
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.beton_klasa || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            padding: '0px 4px'
                          }}
                        >
                          {zamowienie.beton_klasa}
                        </div>
                      )}
                    </td>
                    
                    {/* Konsystencja */}
                    <td data-field="beton_konsystencja" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('beton_konsystencja') ? 'var(--warn)' : 'transparent', color: cellBlink.has('beton_konsystencja') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.beton_konsystencja || ''}
                          onChange={(e) => handleInputChange('beton_konsystencja', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          className="cell-input"
                          style={{ width: '100%', height: DATA_INPUT_HEIGHT, boxSizing: 'border-box', fontSize: DATA_FONT }}
                        />
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.beton_konsystencja || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            padding: '0px 4px'
                          }}
                        >
                          {zamowienie.beton_konsystencja}
                        </div>
                      )}
                    </td>
                    
                    {/* Telefon */}
                    <td className="text-cell" data-field="klient_telefon" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('klient_telefon') ? 'var(--warn)' : 'transparent', color: cellBlink.has('klient_telefon') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <AutocompleteInput
                          value={editData.klient_telefon || ''}
                          onChange={(value) => handleInputChange('klient_telefon', value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          fieldType="telefon"
                          dataField="klient_telefon"
                          dataRowId={zamowienie.id}
                          clientName={editData.klient_nazwa}
                          clientNip={editData.klient_nip}
                          style={{}}
                          className="cell-input"
                        />
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.klient_telefon || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT
                          }}
                        >
                          {zamowienie.klient_telefon}
                        </div>
                      )}
                    </td>
                    
                    {/* Ok */}
                    <td className="text-cell" data-field="zamowienie_okolo" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', textAlign: 'center', width: '30px', maxWidth: '30px', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_okolo') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_okolo') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.zamowienie_okolo || ''}
                          onChange={(e) => handleInputChange('zamowienie_okolo', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          className="cell-input"
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <span style={{ fontSize: DATA_FONT, textAlign: 'center' }}>
                          {zamowienie.zamowienie_okolo}
                        </span>
                      )}
                    </td>
                    
                    {/* Ilość (bez paska) */}
                    <td className="number-cell" data-field="zamowienie_ilosc" style={{ padding: '0 4px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', width: '52px', maxWidth: '52px' }}>
                      {!isEditing && (
                        <span style={{ fontSize: DATA_FONT }}>
                          {formatDot(Number(zamowienie.zamowienie_ilosc || 0))}
                        </span>
                      )}
                      {isEditing && (() => {
                        const row = zamowienie;
                        if (!isUsingQtyReducerForRow(row.id)) initQtyFromRow(row);
                        const value = isUsingQtyReducerForRow(row.id) ? globalDisplayValue() : String(editData.zamowienie_ilosc || '');
                        return (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => {
                              const s = e.target.value;
                              setGlobalDraft(s);
                              const val = parseFloat(s.replace(',', '.'));
                              applyQtyAction({ type: 'SET_GLOBAL', value: Number.isFinite(val) ? val : 0 });
                            }}
                            onFocus={(e) => { 
                              try { 
                                const el = e.currentTarget as HTMLInputElement;
                                requestAnimationFrame(() => el.select());
                              } catch {} 
                            }}
                            onBlur={() => {
                              setGlobalDraft(null);
                            }}
                            inputMode="decimal"
                            onPaste={(e) => {
                              try {
                                const text = e.clipboardData.getData('text');
                                const sanitized = (text || '').replace(/[^0-9.,]/g, '');
                                if (sanitized !== text) {
                                  e.preventDefault();
                                  const el = e.currentTarget as HTMLInputElement;
                                  const start = el.selectionStart ?? el.value.length;
                                  const end = el.selectionEnd ?? el.value.length;
                                  const newVal = el.value.slice(0, start) + sanitized + el.value.slice(end);
                                  // Reflect sanitized paste via state updates
                                  setGlobalDraft(newVal);
                                  const v = parseFloat(newVal.replace(',', '.'));
                                  applyQtyAction({ type: 'SET_GLOBAL', value: Number.isFinite(v) ? v : 0 });
                                  requestAnimationFrame(() => {
                                    const pos = start + sanitized.length;
                                    try { el.setSelectionRange(pos, pos); } catch {}
                                  });
                                }
                              } catch {}
                            }}
                            onKeyDown={(e) => {
                              // Allow only digits, comma, dot, control/navigation, and standard shortcuts
                              const key = e.key;
                              const isAllowedChar = /^[0-9.,]$/.test(key) || key === 'Decimal';
                              const isControl = key === 'Backspace' || key === 'Delete' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End' || key === 'Tab';
                              const isShortcut = (e.ctrlKey || e.metaKey) && ['a','c','v','x'].includes(key.toLowerCase());
                              const isEnterOrEscape = key === 'Enter' || key === 'Escape';
                              if (!(isAllowedChar || isControl || isShortcut || isEnterOrEscape)) {
                                e.preventDefault();
                                return;
                              }
                              if (key === 'Enter') {
                                e.preventDefault();
                                handleKeyPress(e, row.id);
                                return;
                              }
                              handleKeyPress(e, row.id);
                            }}
                            style={{
                              width: '100%',
                              margin: 0,
                              padding: '2px 4px',
                              border: '1px solid var(--border)',
                              borderRadius: '3px',
                              fontSize: DATA_FONT,
                              fontFamily: 'inherit',
                              height: DATA_INPUT_HEIGHT,
                              boxSizing: 'border-box',
                              textAlign: 'right'
                            }}
                          />
                        );
                      })()}
                    </td>
                    {/* Wysłano (Zrealizowano) - z paskiem postępu realizacji */}
                    <td className="number-cell" data-field="zamowienie_zrealizowano" style={{ padding: 0, borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('zamowienie_zrealizowano') ? 'var(--warn)' : 'transparent', color: cellBlink.has('zamowienie_zrealizowano') ? 'var(--ok)' : undefined, width: '52px', maxWidth: '52px' }}>
                      {(() => {
                        const total = Number(zamowienie.zamowienie_ilosc || 0);
                        const done = Number(realizacjaByOrder[zamowienie.id] ?? 0);
                        const pct = total > 0 ? Math.max(0, Math.min(100, Math.round((done / total) * 100))) : 0;
                        const tooltipText = `Zrealizowano ${formatDot(done)} m³ z ${formatDot(total)} m³`;
                        return (
                          <div 
                            onMouseEnter={(e) => {
                              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setTooltip({ visible: true, text: tooltipText, x: r.left + r.width / 2, y: r.top - 8 });
                            }}
                            onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                            style={{ position: 'relative', width: '100%', height: DATA_INPUT_HEIGHT, lineHeight: DATA_INPUT_HEIGHT, textAlign: 'right', padding: '0 4px', fontSize: DATA_FONT, background: `linear-gradient(90deg, rgba(76,175,80,0.35) ${pct}%, rgba(0,0,0,0) ${pct}%)` }}>
                            {formatDot(done)}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Dynamiczne kolumny węzłów: inputy inline w trybie edycji, wartości w trybie podglądu */}
                    {wezels.map((wezel) => {
                      const leadingIdForRow = (editingId === zamowienie.id)
                        ? (editData.zamowienie_wezel_wiodacy ?? zamowienie.zamowienie_wezel_wiodacy)
                        : zamowienie.zamowienie_wezel_wiodacy;
                      const isLeading = Number(leadingIdForRow) === Number(wezel.id);
                      if (isEditing) {
                        if (!isUsingQtyReducerForRow(zamowienie.id)) initQtyFromRow(zamowienie);
                        const bufVal = nodeDisplayValue(wezel.id);
                        return (
                          <td key={`wezel-${wezel.id}`} className={`${isLeading ? 'leader-cell ' : ''}number-cell`} style={{ 
                            padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                            width: '52px', maxWidth: '52px', textAlign: 'right'
                          }}>
                            <input
                              type="text"
                              data-field={`wezel_${wezel.id}`}
                              data-row-id={zamowienie.id}
                              value={bufVal}
                              onChange={(e) => {
                                const s = e.target.value;
                                setNodeDraft(prev => ({ ...prev, [wezel.id]: s }));
                                const val = parseFloat(s.replace(',', '.'));
                                applyQtyAction({ type: 'SET_NODE', id: wezel.id, value: Number.isFinite(val) ? val : 0 });
                              }}
                              onFocus={(e) => { 
                                try { 
                                  const el = e.currentTarget as HTMLInputElement;
                                  requestAnimationFrame(() => el.select());
                                } catch {} 
                              }}
                              onBlur={() => {
                                setNodeDraft(prev => { const { [wezel.id]: _, ...rest } = prev; return rest; });
                              }}
                              onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                              inputMode="decimal"
                              onPaste={(e) => {
                                 try {
                                   const text = e.clipboardData.getData('text');
                                   const sanitized = (text || '').replace(/[^0-9.,]/g, '');
                                   if (sanitized !== text) {
                                     e.preventDefault();
                                     const el = e.currentTarget as HTMLInputElement;
                                     const start = el.selectionStart ?? el.value.length;
                                     const end = el.selectionEnd ?? el.value.length;
                                     const newVal = el.value.slice(0, start) + sanitized + el.value.slice(end);
                                     setNodeDraft(prev => ({ ...prev, [wezel.id]: newVal }));
                                     const v = parseFloat(newVal.replace(',', '.'));
                                     applyQtyAction({ type: 'SET_NODE', id: wezel.id, value: Number.isFinite(v) ? v : 0 });
                                     requestAnimationFrame(() => {
                                       const pos = start + sanitized.length;
                                       try { el.setSelectionRange(pos, pos); } catch {}
                                     });
                                   }
                                 } catch {}
                               }}
                               onKeyDown={(e) => {
                                 const key = e.key;
                                 const isAllowedChar = /^[0-9.,]$/.test(key) || key === 'Decimal';
                                 const isControl = key === 'Backspace' || key === 'Delete' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End' || key === 'Tab';
                                 const isShortcut = (e.ctrlKey || e.metaKey) && ['a','c','v','x'].includes(key.toLowerCase());
                                const isEnterOrEscape = key === 'Enter' || key === 'Escape';
                                if (!(isAllowedChar || isControl || isShortcut || isEnterOrEscape)) {
                                  e.preventDefault();
                                  return;
                                }
                                if (key === 'Enter') {
                                  e.preventDefault();
                                  handleKeyPress(e, zamowienie.id);
                                  return;
                                }
                                handleKeyPress(e, zamowienie.id);
                              }}
                              style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--border)', borderRadius: 3, fontSize: DATA_FONT, height: DATA_INPUT_HEIGHT, boxSizing: 'border-box', background: 'var(--card)' }}
                            />
                          </td>
                        );
                      }
                      // View mode
                      const ordered = Number(splitsByOrder[zamowienie.id]?.[wezel.id] ?? 0);
                      const realized = Number(realizedByOrderWezel[zamowienie.id]?.[wezel.id] ?? 0);
                      const tooltipText = `Zrealizowano ${formatDot(realized)} m³ z ${formatDot(ordered)} m³`;
                      
                      return (
                        <td key={`wezel-${wezel.id}`} className={`${isLeading ? 'leader-cell ' : ''}number-cell`} style={{ 
                          padding: '0 4px', whiteSpace: 'nowrap', borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0',
                          width: '52px', maxWidth: '52px', textAlign: 'right'
                        }}>
                          <span 
                            onMouseEnter={(e) => {
                              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setTooltip({ visible: true, text: tooltipText, x: r.left + r.width / 2, y: r.top - 8 });
                            }}
                            onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                            style={{ fontSize: DATA_FONT }}>
                            {formatDot(ordered)}
                          </span>
                        </td>
                      );
                    })}
                    
                    {/* Pompa */}
                    <td data-field="pompa_nazwa" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('pompa_nazwa') ? 'var(--warn)' : 'transparent', color: cellBlink.has('pompa_nazwa') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <div style={{ width: '100%' }}>
                          <AutocompleteInput
                            value={editData.pompa_nazwa || ''}
                            onChange={(value) => handleInputChange('pompa_nazwa', value)}
                            onClientSelect={handlePumpSelect}
                            onSelectionComplete={handlePumpSelectionComplete}
                            onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                            onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                            fieldType="pompa_nazwa"
                            regionId={selectedRegion || undefined}
                            dataField="pompa_nazwa"
                            dataRowId={zamowienie.id}
                            className="cell-input"
                            style={{ width: '100%', height: DATA_INPUT_HEIGHT, boxSizing: 'border-box', fontSize: DATA_FONT }}
                          />
                        </div>
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setTooltip({ visible: true, text: String(zamowienie.pompa_nazwa || ''), x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            color: 'var(--text)',
                            padding: '0px 4px'
                          }}
                        >
                          {zamowienie.pompa_nazwa}
                        </div>
                      )}
                    </td>
                    
                    {/* Nr p. */}
                    <td data-field="pompa_numer" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('pompa_numer') ? 'var(--warn)' : 'transparent', color: cellBlink.has('pompa_numer') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <div style={{ width: '100%' }}>
                          <AutocompleteInput
                            value={editData.pompa_numer || ''}
                            onChange={(value) => handleInputChange('pompa_numer', value)}
                            onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                            onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                            fieldType="pompa_numer"
                            regionId={selectedRegion || undefined}
                            dataField="pompa_numer"
                            dataRowId={zamowienie.id}
                            className="cell-input"
                            style={{ width: '100%', height: DATA_INPUT_HEIGHT, boxSizing: 'border-box', fontSize: DATA_FONT }}
                          />
                        </div>
                      ) : (
                        <span style={{ fontSize: DATA_FONT }}>
                          {zamowienie.pompa_numer}
                        </span>
                      )}
                    </td>
                    
                    {/* Operator (Kierowca) */}
                    <td data-field="pompa_kierowca" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms, color 300ms', backgroundColor: cellBlink.has('pompa_kierowca') ? 'var(--warn)' : 'transparent', color: cellBlink.has('pompa_kierowca') ? 'var(--ok)' : undefined }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.pompa_kierowca || ''}
                          onChange={(e) => handleInputChange('pompa_kierowca', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          style={{
                            width: '100%',
                            padding: '2px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: '3px',
                            fontSize: DATA_FONT,
                            fontFamily: 'inherit',
                            height: DATA_INPUT_HEIGHT,
                            boxSizing: 'border-box'
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: DATA_FONT }}>
                          {zamowienie.pompa_kierowca}
                        </span>
                      )}
                    </td>
                    
                    {/* Transport */}
                    <td data-field="zamowienie_transport" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms', backgroundColor: cellBlink.has('zamowienie_transport') ? 'var(--warn)' : 'transparent' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.zamowienie_transport || ''}
                          onChange={(e) => handleInputChange('zamowienie_transport', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          style={{
                            width: '100%',
                            padding: '2px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: '3px',
                            fontSize: DATA_FONT,
                            fontFamily: 'inherit',
                            height: DATA_INPUT_HEIGHT,
                            boxSizing: 'border-box',
                            background: 'transparent'
                          }}
                        />
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            const debugInfo = `Transport: "${zamowienie.zamowienie_transport || ''}"`;
                            setTooltip({ visible: true, text: debugInfo, x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            padding: '0px 4px'
                          }}
                        >
                          {zamowienie.zamowienie_transport || ''}
                        </div>
                      )}
                    </td>
                    
                    {/* Uwagi 2 */}
                    <td className="text-cell" data-field="zamowienie_uwagi2" style={{ padding: '0px', whiteSpace: 'nowrap', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', transition: 'background-color 300ms', backgroundColor: cellBlink.has('zamowienie_uwagi2') ? 'var(--warn)' : 'transparent' }}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.zamowienie_uwagi2 || ''}
                          onChange={(e) => handleInputChange('zamowienie_uwagi2', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, zamowienie.id)}
                          onKeyDown={(e) => handleKeyPress(e, zamowienie.id)}
                          style={{
                            width: '100%',
                            padding: '2px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: '3px',
                            fontSize: DATA_FONT,
                            fontFamily: 'inherit',
                            height: DATA_INPUT_HEIGHT,
                            boxSizing: 'border-box',
                            background: 'transparent'
                          }}
                        />
                      ) : (
                        <div
                          onMouseEnter={(e) => {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            const debugInfo = `Uwagi2: "${zamowienie.zamowienie_uwagi2 || ''}"`;
                            setTooltip({ visible: true, text: debugInfo, x: r.left + r.width / 2, y: r.top - 8 });
                          }}
                          onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: DATA_FONT,
                            padding: '0px 4px'
                          }}
                        >
                          {zamowienie.zamowienie_uwagi2 || ''}
                        </div>
                      )}
                    </td>
                  </tr>
                  {expanded[zamowienie.id] ? (
                    <tr key={`wz-${zamowienie.id}`} style={{ background: 'var(--bg)' }}>
                      <td colSpan={22 + wezels.length + 1} style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)' }}>
                        {(wzByOrder[zamowienie.id] || []).length === 0 ? (
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Brak dokumentów WZ</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '120px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Węzeł</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '80px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Nr zam.</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '80px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>WZ</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '120px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Recepta</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '80px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Ilość</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '100px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Dzień</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '90px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Czas start</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '90px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Czas stop</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '120px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Kierowca</th>
                                <th style={{ textAlign: 'left', padding: '2px 6px', width: '110px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Nr rej.</th>
                                {(hasRole('Administrator') || hasRole('Dyspozytor')) && (
                                  <th style={{ textAlign: 'left', padding: '2px 6px', width: '80px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>Akcja</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {(wzByOrder[zamowienie.id] || []).map((wz: any) => (
                                <tr key={wz.id}>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.wezel_nazwa}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.zamowienie_numer}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.numer_wz}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.recepta}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{Number(wz.ilosc || 0)}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{String(wz.zamowienie_dzien || '').split('T')[0]}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.czastart || ''}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.czasstop || ''}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.kierowca || ''}</td>
                                  <td style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)', color: 'var(--text)' }}>{wz.nrrej || ''}</td>
                                  {(hasRole('Administrator') || hasRole('Dyspozytor')) && (
                                    <td style={{ textAlign: 'center', padding: '2px 6px', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
                                      <button
                                        onClick={() => {
                                          setWzTransferModal({ visible: true, wz, zamowienieId: zamowienie.id });
                                          setWzTransferData({ numer: '', tydzien: String(zamowienie.zamowienie_tydzien || '') });
                                        }}
                                        style={{
                                          padding: '2px 6px',
                                          fontSize: '11px',
                                          border: '1px solid var(--border)',
                                          borderRadius: '3px',
                                          background: 'var(--card)',
                                          color: 'var(--text)',
                                          cursor: 'pointer'
                                        }}
                                        title="Przenieś WZ do innego zamówienia"
                                      >
                                        Przenieś
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          {/* tfoot delete removed; delete action relocated to footer bar */}
      </table>
      {/* Header columns context menu */}
      {headerMenu.open && (
        <div
          id="header-columns-menu"
          style={{
            position: 'fixed',
            top: headerMenu.y,
            left: headerMenu.x,
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
            padding: 8,
            zIndex: 2147483000,
            minWidth: 200,
            maxWidth: 220,
            maxHeight: '70vh',
            overflowY: 'auto',
            color: 'var(--text)'
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text)', borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
            Widoczność kolumn
          </div>
          
          {/* Search input */}
          <input
            type="text"
            placeholder="Szukaj..."
            value={columnMenuSearch}
            onChange={(e) => setColumnMenuSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '4px 6px',
              marginBottom: 8,
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: 11,
              background: 'var(--bg)',
              color: 'var(--text)'
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* View presets */}
          <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {Object.entries(VIEW_PRESETS).map(([presetKey, preset]) => (
                <button
                  key={presetKey}
                  onClick={() => applyPreset(presetKey)}
                  style={{
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    borderRadius: 3,
                    padding: '3px 6px',
                    fontSize: 10,
                    cursor: 'pointer',
                    color: 'var(--text)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--accent)';
                    e.currentTarget.style.color = 'var(--card)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg)';
                    e.currentTarget.style.color = 'var(--text)';
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Kolumny */}
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {getAllColumnKeys()
              .filter(key => {
                const label = Object.entries(LABEL_TO_KEY).find(([l, k]) => k === key)?.[0] || '';
                return !columnMenuSearch || label.toLowerCase().includes(columnMenuSearch.toLowerCase()) || key.toLowerCase().includes(columnMenuSearch.toLowerCase());
              })
              .map(key => {
                const label = Object.entries(LABEL_TO_KEY).find(([l, k]) => k === key)?.[0] || key;
                const mandatory = MANDATORY_COLUMN_KEYS.current.has(key);
                const checked = mandatory || visibleColumns.size === 0 || visibleColumns.has(key);
                return (
                  <label
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      margin: '2px 0',
                      padding: '2px 4px',
                      borderRadius: 4,
                      cursor: mandatory ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => !mandatory && (e.currentTarget.style.background = 'var(--bg)')}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={mandatory}
                      onChange={() => toggleColumn(key)}
                      style={{ cursor: mandatory ? 'not-allowed' : 'pointer' }}
                    />
                    <span style={{ 
                      opacity: mandatory ? 0.6 : 1,
                      flex: 1,
                      userSelect: 'none'
                    }}>
                      {label}
                    </span>
                    {mandatory && (
                      <span style={{ fontSize: 9, color: 'var(--muted)', fontStyle: 'italic' }}>obowiązkowe</span>
                    )}
                  </label>
                );
              })}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 4, marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => { 
                setVisibleColumns(new Set()); 
                try { localStorage.removeItem('ui.zamowienia.tableColumns.visible'); } catch {} 
                setHeaderMenu({ open: false, x: 0, y: 0 }); 
                setColumnMenuSearch(''); 
              }}
              style={{
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 10,
                cursor: 'pointer',
                color: 'var(--text)',
                flex: 1,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'var(--card)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg)';
                e.currentTarget.style.color = 'var(--text)';
              }}
            >
              Wszystkie
            </button>
            <button
              onClick={() => { setHeaderMenu({ open: false, x: 0, y: 0 }); setColumnMenuSearch(''); }}
              style={{
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 10,
                cursor: 'pointer',
                color: 'var(--text)',
                flex: 1,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--muted)';
                e.currentTarget.style.color = 'var(--card)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg)';
                e.currentTarget.style.color = 'var(--text)';
              }}
            >
              Zamknij
            </button>
          </div>
        </div>
      )}
      {/* Validation Error Modal */}
      {validationError && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setValidationError(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setValidationError(null);
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              try { (document.getElementById('validation-ok') as HTMLButtonElement)?.focus(); } catch {}
            }
          }}
          tabIndex={0}
        >
          <div 
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: '2px solid #f44336'
            }}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
            ref={(el) => {
              if (!el) return;
              try {
                const ok = document.getElementById('validation-ok') as HTMLButtonElement | null;
                ok?.focus();
              } catch {}
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#f44336',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }}>
                <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>!</span>
              </div>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#d32f2f'
              }}>
                Błędy walidacji
              </h3>
            </div>
            
            <div style={{
              backgroundColor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <pre style={{
                margin: 0,
                fontSize: '14px',
                lineHeight: '1.5',
                color: '#b71c1c',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap'
              }}>
                {validationError}
              </pre>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                id="validation-ok"
                onClick={() => setValidationError(null)}
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1976D2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2196F3';
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Map Modal for precise Budowa localization */}
      {mapModal.visible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeMapModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '16px',
              width: '90vw',
              height: '80vh',
              maxWidth: '1200px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              border: '1px solid #e0e0e0'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={mapModal.value}
                onChange={(e) => setMapModal(prev => ({ ...prev, value: e.target.value }))}
                placeholder="Wpisz adres / lokalizację"
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: DATA_FONT
                }}
              />
              <button
                onClick={(e) => { e.stopPropagation(); geocodeAddressInModal(); }}
                style={{ padding: '8px 12px', border: '1px solid #1976D2', background: '#1976D2', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}
              >
                Wyszukaj
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); applyMapModal(); }}
                disabled={!pendingGeo}
                style={{ padding: '8px 12px', border: '1px solid #2e7d32', background: pendingGeo ? '#2e7d32' : '#a5d6a7', color: '#fff', borderRadius: '4px', cursor: pendingGeo ? 'pointer' : 'not-allowed' }}
              >
                Użyj
              </button>
              <button
                onClick={closeMapModal}
                style={{ padding: '8px 12px', border: '1px solid #e0e0e0', background: '#fff', color: '#333', borderRadius: '4px', cursor: 'pointer' }}
              >
                Anuluj
              </button>
            </div>
            <div style={{ flex: 1, position: 'relative', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              {!gKey ? (
                <div style={{ padding: 12, fontSize: 12, color: '#d32f2f' }}>Błąd: Brak GMAPS_API_KEY – nie można załadować Map Google</div>
              ) : !modalMapId || !modalMapId.trim() ? (
                <div style={{ padding: 12, fontSize: 12, color: '#d32f2f' }}>Błąd: Brak Map ID – wymagane dla AdvancedMarkerElement</div>
              ) : mapLibError ? (
                <div style={{ padding: 12, fontSize: 12, color: '#d32f2f' }}>Błąd ładowania Google Maps</div>
              ) : !mapLibLoaded ? (
                <div style={{ padding: 12, fontSize: 12, color: '#757575' }}>Ładowanie Google Maps...</div>
              ) : (
                <GoogleMap
                  onLoad={(map: any) => {
                    modalMapRef.current = map;
                    try { console.log('[MapModal][onLoad] setCenter', mapCenter); } catch {}
                    try { map.setCenter(mapCenter); map.setZoom(14); } catch {}
                    // initial mount of advanced markers if state available
                    try {
                      // cleanup previous if any
                      if (wezelAdvRef.current) { wezelAdvRef.current.map = null; wezelAdvRef.current = null; }
                      if (budowaAdvRef.current) { budowaAdvRef.current.map = null; budowaAdvRef.current = null; }
                      if (pickAdvRef.current) { pickAdvRef.current.map = null; pickAdvRef.current = null; }
                      if (wezelMarker) {
                        wezelAdvRef.current = mountAdvancedMarker({ position: wezelMarker, color: '#d32f2f', border: '#8b1d1d', zIndex: 20 });
                      }
                      if (budowaMarker) {
                        budowaAdvRef.current = mountAdvancedMarker({ position: budowaMarker, color: '#1976D2', border: '#0D47A1', zIndex: 10 });
                      }
                      // pickMarker visualization not needed; we use blue budowa marker for selection
                    } catch {}
                  }}
                  onUnmount={() => { modalMapRef.current = null; }}
                  center={mapCenter}
                  zoom={14}
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  options={{ streetViewControl: false, mapTypeControl: false, mapId: modalMapId.trim() }}
                  onClick={(e: any) => {
                    try {
                      const lat = e.latLng?.lat();
                      const lng = e.latLng?.lng();
                      if (typeof lat === 'number' && typeof lng === 'number') {
                        console.log('[MapModal][click map] picked', { lat, lon: lng });
                        setPickMarker({ lat, lng });
                        // Always remember picked coordinates for manual upsert on "Użyj"
                        try { setPendingGeo({ lat, lon: lng }); } catch {}
                        // Immediately try to translate to address and reflect in input
                        reverseGeocodeAndFill(lat, lng).catch(() => {
                          try { setMapModal(prev => ({ ...prev, value: `${lat.toFixed(6)}, ${lng.toFixed(6)}` })); } catch {}
                        });
                        // place/update blue budowa marker at clicked location
                        try {
                          setBudowaMarker({ lat, lng });
                          if (budowaAdvRef.current) {
                            budowaAdvRef.current.position = { lat, lng };
                          } else {
                            budowaAdvRef.current = mountAdvancedMarker({ position: { lat, lng }, color: '#1976D2', border: '#0D47A1', zIndex: 10 });
                          }
                        } catch {}
                      }
                    } catch {}
                  }}
                >
                  {/* Advanced markers are handled imperatively via AdvancedMarkerElement */}
                </GoogleMap>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Save/Discard Modal when switching rows */}
      {confirmModal.visible && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setConfirmModal({ visible: false, targetRowId: null });
          }}
          tabIndex={0}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: 20, width: '90%', maxWidth: 480, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12, color: '#333' }}>Niezapisane zmiany</h3>
            <p style={{ marginTop: 0, marginBottom: 20, color: '#555', lineHeight: 1.5 }}>
              Masz niezapisane zmiany w edytowanym wierszu. Zapisać zmiany przed przejściem?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={confirmSwitchDiscard}
                style={{ background: '#f5f5f5', color: '#333', border: '1px solid #e0e0e0', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }}
              >Nie</button>
              <button
                onClick={confirmSwitchSave}
                style={{ background: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 16px', cursor: 'pointer' }}
              >Tak</button>
            </div>
          </div>
        </div>
      )}
      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          data-context-menu="true"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 9999,
            minWidth: '150px',
            padding: '4px 0',
            textAlign: 'left'
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {/* Add submenu */}
          <div style={{ position: 'relative' }}>
            <div
              style={{
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: '11px',
                color: '#333',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
                const submenu = e.currentTarget.nextElementSibling as HTMLElement;
                if (submenu) submenu.style.display = 'block';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                const submenu = e.currentTarget.nextElementSibling as HTMLElement;
                if (submenu) submenu.style.display = 'none';
              }}
            >
              Dodaj
              <span style={{ fontSize: '10px' }}>▶</span>
            </div>
            <div
              data-context-menu="true"
              style={{
                position: 'absolute',
                right: '100%',
                top: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '120px',
                padding: '4px 0',
                display: 'none',
                zIndex: 1001
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.display = 'block'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
            >
              {[...wezels].sort((a, b) => Number(a.id) - Number(b.id)).map(w => (
                <div key={`add-${w.id}`} onClick={() => handleContextMenuAddNewForWezel(w.id)}
                  style={{ padding: '4px 12px', cursor: 'pointer', fontSize: '11px', color: '#333', textAlign: 'left' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; }}
                >{w.nazwa}</div>
              ))}
            </div>
          </div>

          {/* Edit */}
          <div onClick={handleContextMenuEdit}
            style={{ padding: '4px 12px', cursor: contextMenu.zamowienieId ? 'pointer' : 'not-allowed', fontSize: '11px', color: contextMenu.zamowienieId ? '#333' : '#999', borderBottom: '1px solid #f0f0f0', textAlign: 'left' }}
            onMouseEnter={(e) => { if (contextMenu.zamowienieId) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { if (contextMenu.zamowienieId) e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            Edytuj
          </div>

          {/* Status submenu */}
          <div style={{ position: 'relative' }}>
            <div
              style={{
                padding: '4px 12px',
                cursor: 'pointer',
                fontSize: '11px',
                color: '#333',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
                const submenu = e.currentTarget.nextElementSibling as HTMLElement;
                if (submenu) {
                  submenu.style.display = 'block';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                const submenu = e.currentTarget.nextElementSibling as HTMLElement;
                if (submenu) {
                  submenu.style.display = 'none';
                }
              }}
            >
              Ustaw stan
              <span style={{ fontSize: '10px' }}>▶</span>
            </div>
            <div
              data-context-menu="true"
              style={{
                position: 'absolute',
                right: '100%',
                top: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '120px',
                padding: '4px 0',
                display: 'none',
                zIndex: 1001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.display = 'block';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            >
              <div
                onClick={() => handleContextMenuStatusChange('Potwierdzone')}
                style={{
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1976D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Potwierdź
              </div>
              <div
                onClick={() => handleContextMenuStatusChange('Realizacja')}
                style={{
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#F9A825" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                Realizacja
              </div>
              <div
                onClick={() => handleContextMenuStatusChange('Zakonczone')}
                style={{
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M7 12l3 3 7-7" />
                </svg>
                Zakończ
              </div>
              <div
                onClick={() => handleContextMenuStatusChange('Anulowane')}
                style={{
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#616161" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                Anuluj
              </div>
            </div>
          </div>

          {/* Delete */}
          <div
            onClick={handleContextMenuDelete}
            style={{
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: '11px',
              color: '#d32f2f',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#ffebee';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
            Usuń
          </div>

          {/* Copy */}
          <div
            onClick={handleContextMenuCopy}
            style={{
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: '11px',
              color: '#333',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#424242" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            Kopiuj
          </div>

          {/* Paste */}
          <div
            onClick={() => { if (clipboardData) handleContextMenuPaste(); }}
            style={{
              padding: '4px 12px',
              cursor: clipboardData ? 'pointer' : 'not-allowed',
              fontSize: '11px',
              color: clipboardData ? '#333' : '#999',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              if (clipboardData) {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }
            }}
            onMouseLeave={(e) => {
              if (clipboardData) {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={clipboardData ? '#424242' : '#9e9e9e'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3" />
              <path d="M16 5h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
              <rect x="8" y="3" width="8" height="4" rx="1" ry="1" />
            </svg>
            Wklej
          </div>
          {/* Historia */}
          <div
            onClick={handleContextMenuHistory}
            style={{ padding: '4px 12px', cursor: contextMenu.zamowienieId ? 'pointer' : 'not-allowed', fontSize: '11px', color: contextMenu.zamowienieId ? '#333' : '#999', borderTop: '1px solid #f0f0f0', textAlign: 'left' }}
            onMouseEnter={(e) => { if (contextMenu.zamowienieId) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { if (contextMenu.zamowienieId) e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            Historia
          </div>

          {/* Zatwierdź receptę */}
          <div
            onClick={handleContextMenuConfirmRecipe}
            style={{ padding: '4px 12px', cursor: contextMenu.zamowienieId ? 'pointer' : 'not-allowed', fontSize: '11px', color: contextMenu.zamowienieId ? '#333' : '#999', textAlign: 'left' }}
            onMouseEnter={(e) => { if (contextMenu.zamowienieId) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { if (contextMenu.zamowienieId) e.currentTarget.style.backgroundColor = '#ffffff'; }}
          >
            Zatwierdź receptę
          </div>

          {(() => {
            if (!contextMenu.zamowienieId) return null;
            const row = zamowienia.find(z => z.id === contextMenu.zamowienieId);
            if (!row) return null;
            const nip = (row.klient_nip || '').trim();
            if (!nip) return null;
            const isBlocked = Boolean((row as any).klient_blokada);
            const label = isBlocked ? 'Zgoda' : 'Blokada';
            const color = isBlocked ? '#2e7d32' : '#d32f2f';
            return (
              <div
                onClick={handleContextMenuClientToggle}
                style={{
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isBlocked ? (
                    <polyline points="20 6 9 17 4 12" />
                  ) : (
                    <>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </>
                  )}
                </svg>
                {label}
              </div>
            );
          })()}
        </div>
      )}
      {/* Paste Confirmation Modal */}
      {pasteModal.visible && pasteModal.data && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setPasteModal({ visible: false, data: null })}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#333'
            }}>
              Potwierdź dane wklejania
            </h3>

            {/* Client information */}
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#333' }}>Klient:</strong> {pasteModal.data.klient_nazwa || 'Brak'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: '#333' }}>Budowa:</strong> {pasteModal.data.klient_budowa || 'Brak'}
              </div>
              <div>
                <strong style={{ color: '#333' }}>Telefon:</strong> {pasteModal.data.klient_telefon || 'Brak'}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Dzień:
              </label>
              <input
                type="date"
                value={pasteModal.data.zamowienie_dzien || ''}
                onChange={(e) => setPasteModal({
                  visible: true,
                  data: { ...pasteModal.data, zamowienie_dzien: e.target.value }
                })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Godzina od:
              </label>
              <input
                type="time"
                value={pasteModal.data.zamowienie_godzinaod || ''}
                onChange={(e) => setPasteModal({
                  visible: true,
                  data: { ...pasteModal.data, zamowienie_godzinaod: e.target.value }
                })}
                step="60"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                className="no-calendar-icon"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Godzina do:
              </label>
              <input
                type="time"
                value={pasteModal.data.zamowienie_godzinado || ''}
                onChange={(e) => setPasteModal({
                  visible: true,
                  data: { ...pasteModal.data, zamowienie_godzinado: e.target.value }
                })}
                step="60"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                className="no-calendar-icon"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                Ilość:
              </label>
              <input
                type="number"
                value={pasteModal.data.zamowienie_ilosc || ''}
                onChange={(e) => setPasteModal({
                  visible: true,
                  data: { ...pasteModal.data, zamowienie_ilosc: Number(e.target.value) }
                })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setPasteModal({ visible: false, data: null })}
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={() => pasteModal.data && handlePasteConfirm(pasteModal.data)}
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Utwórz zamówienie
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal przenoszenia WZ */}
      {wzTransferModal.visible && wzTransferModal.wz && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setWzTransferModal({ visible: false, wz: null, zamowienieId: null })}
        >
          <div
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '20px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 10px 24px rgba(0,0,0,0.18)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>
              Przenieś WZ do innego zamówienia
            </h3>
            
            <div style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--muted)' }}>
              <div>WZ: <strong>{wzTransferModal.wz.numer_wz}</strong></div>
              <div>Obecne zamówienie: <strong>{wzTransferModal.wz.zamowienie_numer}</strong> (tydzień {wzTransferModal.wz.zamowienie_tydzien})</div>
              <div>Ilość: <strong>{Number(wzTransferModal.wz.ilosc || 0)} m³</strong></div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>
                Nowy numer zamówienia:
              </label>
              <input
                type="number"
                value={wzTransferData.numer}
                onChange={(e) => setWzTransferData(prev => ({ ...prev, numer: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  background: 'var(--bg)',
                  color: 'var(--text)'
                }}
                placeholder="Numer zamówienia"
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>
                Tydzień:
              </label>
              <input
                type="number"
                value={wzTransferData.tydzien}
                onChange={(e) => setWzTransferData(prev => ({ ...prev, tydzien: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  fontSize: '14px',
                  background: 'var(--bg)',
                  color: 'var(--text)'
                }}
                placeholder="Numer tygodnia"
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setWzTransferModal({ visible: false, wz: null, zamowienieId: null });
                  setWzTransferData({ numer: '', tydzien: '' });
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--bg)',
                  color: 'var(--text)',
                  cursor: 'pointer'
                }}
              >
                Anuluj
              </button>
              <button
                onClick={handleWzTransfer}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '4px',
                  background: 'var(--accent)',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Przenieś
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightweight custom tooltip (replaces native black title) */}
      {tooltip.visible && (tooltip.text || tooltip.content) && (
        <div
          style={{
            position: 'fixed',
            top: Math.max(8, tooltip.y),
            left: Math.min(window.innerWidth - 280, tooltip.x),
            background: '#ffffff',
            color: '#333',
            border: '1px solid #e0e0e0',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
            lineHeight: 1.4,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 99999,
            maxWidth: 320,
            whiteSpace: tooltip.content ? 'normal' : 'pre-wrap',
            pointerEvents: 'none'
          }}
        >
          {tooltip.content || tooltip.text}
        </div>
      )}
    </div>
  );
};

const arePropsEqual = (
  prev: CardZamowieniaWidgetProps,
  next: CardZamowieniaWidgetProps
): boolean => {
  return (
    prev.selectedRegion === next.selectedRegion &&
    prev.selectedWezel === next.selectedWezel &&
    prev.selectedDate === next.selectedDate
  );
};

export default memo(CardZamowieniaWidget, arePropsEqual);